  import { useEffect, useMemo, useRef, useState } from 'react';
  import { useNavigate, useParams } from 'react-router-dom';
  import {
    AlertTriangle,
    ArrowUpDown,
    ArrowRight,
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Filter,
    FolderKanban,
    LayoutDashboard,
    List,
    Search,
    Settings as SettingsIcon,
    Grid2X2,
    Thermometer,
    TrendingUp,
    Cpu,
    Waves,
    X,
    Activity,
    CheckCircle2,
    Droplets,
    Eye,
    Building2,
    MapPin,
    Radio,
    Zap
  } from 'lucide-react';
  import { toast } from 'sonner';
  import { supabase } from '@/utils/supabase/client';
  import { UniversalSmartLogin } from './universal-smart-login';
  import { UniversalAppHeader } from './universal-app-header';
  import '@/styles/vision-responsive.css';
  import { VisionDashboard, type MvpDashboardSummary } from './vision-dashboard';
  import { VisionAnalytics } from './vision-analytics';
  import { VisionDemo } from './vision-demo';
  import { VisionGrowthMonitor } from './vision-growth-monitor';
  import { VisionSensorsDashboard } from './vision-sensors-dashboard';
  import { PrivacyPolicy } from './privacy-policy';
  import { ClinicalCaseCard } from './clinical-case-card';
  import { ClassifyAlertDialog, type BulkValidationTarget } from './classify-alert-dialog';
  import { GlobalScopeBar } from './global-scope-bar';
  import { AqlAssistantPanel } from './aql-assistant/AqlAssistantPanel';
  import { AqlOperationalContextProvider } from './aql-assistant/AqlOperationalContextProvider';
  import { ConfigurationModule } from './configuration/configuration-module';
  import { AqlSeahorseLogo, AqlVisionMark } from './branding/aql-vision-brand';
  import { AqlVisionLoader } from './branding/aql-vision-loader';
  import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
  import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
  import { useGlobal } from '../providers/global-provider';
  import {
    CookieConsentBanner,
    CookiePreferencesDialog,
    defaultCookiePreferences,
    readCookiePreferences,
    saveCookiePreferences,
    type CookiePreferencesState
  } from './cookie-preferences';
  import {
    applyGlobalScope,
    buildClientCatalog,
    collectScopeOptions,
    normalizeAlertsToCases,
    getAlertSensorType,
    hasVisualEvidence,
    type ClinicalStatus,
    type Phase,
    type RawVisionAlert
  } from '@/app/utils/vision-biology';
  import { t, useLanguage } from '@/app/utils/i18n';
  import { getOperationalContextProfile, normalizeOperationalContextType, type OperationalContextType } from '@/app/utils/operational-context';
  import {
    DEMO_AUTH_TOKEN,
    DEMO_USER_EMAIL,
    getDemoDashboardSummary,
    getDemoVisionAlerts
  } from '@/app/utils/vision-demo-data';
  import { showNotification } from '@/app/utils/notification-fallback';
  import type { AqlAssistantPage, AqlAssistantTimeWindow } from './aql-assistant/assistantApi';
  import type { AqlAssistantLocalSummary } from './aql-assistant/useAqlAssistant';

  interface VisionAlertsProps {
    apiUrl: string;
    publicAnonKey: string;
    onBack: () => void;
    demoMode?: boolean;
    demoInitialView?: VisionView;
    demoSelectedAlertId?: string | null;
  }

  type MainView = 'operations' | 'clinical' | 'sensors' | 'analytics' | 'growth' | 'live';
  type VisionView = MainView | 'settings';
  type SectionViewItem = {
    id: MainView;
    label: string;
    icon: typeof LayoutDashboard;
    title?: string;
    mobileLabel?: string;
  };
  type SectionItem = SectionViewItem;
  type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low';
  type AlertTypeFilter = 'all' | 'visual' | 'sensor' | 'temperature' | 'ph' | 'dissolved_oxygen' | 'redox';
  type QuickFilter = 'all' | 'pending' | 'high_priority' | 'low_confidence' | 'confirmed';
  type AlertViewMode = 'grid' | 'list';
  type AlertSortMode = 'newest' | 'oldest' | 'severity' | 'confidence' | 'tank';
  type DateFilterPreset = 'all' | 'today' | 'week' | 'month' | 'custom';
  type ActiveOperationalAlertToast = {
    signature: string;
    valueLabel: string | null;
    title: string;
    body: string;
  };
  const MENU_ORDER_STORAGE_KEY = 'aql_vision_sidebar_menu_order';
  const MENU_HIDDEN_STORAGE_KEY = 'aql_vision_sidebar_menu_hidden';
  const MENU_ORDER_EVENT = 'aql-vision-menu-order-changed';
  const DEFAULT_MENU_ORDER: MainView[] = ['operations', 'clinical', 'sensors', 'growth', 'live', 'analytics'];

  const normalizeMenuOrder = (value: unknown): MainView[] => {
    const parsed = Array.isArray(value) ? value : [];
    const valid = parsed.filter((item): item is MainView =>
      DEFAULT_MENU_ORDER.includes(item as MainView)
    );
    return [...valid, ...DEFAULT_MENU_ORDER.filter((item) => !valid.includes(item))];
  };

  const readMenuOrderPreference = (): MainView[] => {
    if (typeof window === 'undefined') return [...DEFAULT_MENU_ORDER];
    try {
      return normalizeMenuOrder(JSON.parse(window.localStorage.getItem(MENU_ORDER_STORAGE_KEY) ?? '[]'));
    } catch {
      return [...DEFAULT_MENU_ORDER];
    }
  };

  const normalizeHiddenMenuItems = (value: unknown): MainView[] => {
    const parsed = Array.isArray(value) ? value : [];
    const valid = Array.from(new Set(parsed.filter((item): item is MainView =>
      DEFAULT_MENU_ORDER.includes(item as MainView)
    )));
    return valid.length >= DEFAULT_MENU_ORDER.length ? [] : valid;
  };

  const readHiddenMenuPreference = (): MainView[] => {
    if (typeof window === 'undefined') return [];
    try {
      return normalizeHiddenMenuItems(JSON.parse(window.localStorage.getItem(MENU_HIDDEN_STORAGE_KEY) ?? '[]'));
    } catch {
      return [];
    }
  };

  const orderSectionItems = (items: SectionItem[], order: MainView[], hiddenItems: MainView[]) => {
    const itemMap = new Map(items.map((item) => [item.id, item]));
    return [
      ...order.map((id) => itemMap.get(id)).filter(Boolean),
      ...items.filter((item) => !order.includes(item.id))
    ].filter((item) => item && !hiddenItems.includes(item.id)) as SectionItem[];
  };
  type ClientProjectChoice = {
    id: string;
    name: string;
    slug: string;
    description?: string | null;
    contextType: OperationalContextType;
    speciesFocus?: string | null;
    defaultPhase?: string | null;
    status?: 'operational' | 'attention' | 'critical' | 'offline';
    primaryInstallation?: string | null;
    locationLabel?: string | null;
    installationsCount?: number;
    operationalAreasCount?: number;
    monitoringUnitsCount?: number;
    activeAlertsCount?: number;
    pendingValidationsCount?: number;
    offlineUnitsCount?: number;
    lastUpdatedAt?: string | null;
    hasVision?: boolean;
    hasSensors?: boolean;
    capacityLabel?: string | null;
    coverImageUrl?: string | null;
    installations?: Array<{
      id: string;
      name: string;
      latitude?: number | null;
      longitude?: number | null;
      status?: 'operational' | 'attention' | 'critical' | 'offline';
    }>;
    operationalAreas?: Array<{
      id: string;
      name: string;
      code?: string | null;
      locationName?: string | null;
      species?: string | null;
      phase?: string | null;
      unitsCount?: number;
      activeAlertsCount?: number;
      pendingValidationsCount?: number;
      status?: 'operational' | 'attention' | 'critical' | 'offline';
    }>;
    monitoringUnits?: Array<{
      id: string;
      name: string;
      zoneName?: string | null;
      type?: string | null;
      status?: 'operational' | 'attention' | 'critical' | 'offline';
      heartbeat?: string | null;
    }>;
  };
  type DatabaseScopeOptions = {
    species: string[];
    phases: Phase[];
    sites: string[];
    tanks: string[];
    tanksBySite: Record<string, string[]>;
    speciesIdsByLabel: Record<string, string>;
    phaseIdsByLabel: Record<string, string>;
    siteIdsByLabel: Record<string, string>;
    tankIdsByLabel: Record<string, string>;
    kitIdsByTankLabel: Record<string, string>;
  };

  const EMPTY_DATABASE_SCOPE_OPTIONS: DatabaseScopeOptions = {
    species: [],
    phases: [],
    sites: [],
    tanks: [],
    tanksBySite: {},
    speciesIdsByLabel: {},
    phaseIdsByLabel: {},
    siteIdsByLabel: {},
    tankIdsByLabel: {},
    kitIdsByTankLabel: {}
  };

  const sensorTypeFromAlertCase = (item: { rawAlert?: RawVisionAlert | null }) =>
    getAlertSensorType(item.rawAlert);

  const isSensorAlertCase = (item: { rawAlert?: RawVisionAlert | null }) => Boolean(sensorTypeFromAlertCase(item));

  const matchesAlertTypeFilter = (item: { rawAlert?: RawVisionAlert | null; image?: string }, filter: AlertTypeFilter) => {
    if (filter === 'all') return true;
    const sensorType = sensorTypeFromAlertCase(item);
    if (filter === 'sensor') return Boolean(sensorType);
    if (filter === 'visual') return hasVisualEvidence(item.rawAlert) || Boolean(item.image);
    return sensorType === filter;
  };
  const slugify = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const slugSegments = (value: unknown) =>
    String(value ?? '')
      .split(/[/?#]+/)
      .map((segment) => slugify(segment))
      .filter(Boolean);

  const projectMatchesRoute = (row: any, routeProjectSlug: string, routeClientSlug: string) => {
    const rawSlug = String(row.slug ?? '');
    const slugParts = slugSegments(rawSlug);
    const directSlug = slugify(rawSlug);
    const nameSlug = slugify(String(row.name ?? ''));

    return (
      directSlug === routeProjectSlug ||
      directSlug === `${routeClientSlug}-${routeProjectSlug}` ||
      slugParts[slugParts.length - 1] === routeProjectSlug ||
      (slugParts[0] === routeClientSlug && slugParts[slugParts.length - 1] === routeProjectSlug) ||
      nameSlug === routeProjectSlug
    );
  };

  const PHASE_ORDER: Phase[] = ['larval', 'juvenile', 'pre-grow', 'adult'];
  const PHASE_LABEL_KEYS: Record<Phase, string> = {
    larval: 'larval',
    juvenile: 'juvenile',
    'pre-grow': 'preGrow',
    adult: 'adult'
  };
  const SEVERITY_RANK: Record<SeverityFilter, number> = {
    all: 0,
    critical: 4,
    high: 3,
    medium: 2,
    low: 1
  };
  const SEVERITY_LABELS: Record<string, string> = {
    critical: 'Crítica',
    high: 'Alta',
    medium: 'Média',
    low: 'Baixa'
  };
  const VALIDATION_STATUS_LABELS: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    rejected: 'Rejeitado',
    inconclusive: 'Inconclusivo'
  };

  const uniqueSorted = (values: Array<string | null | undefined>) =>
    Array.from(new Set(values.map((value) => String(value ?? '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

  const scopeLabelKey = (value: unknown) => slugify(String(value ?? ''));

  const isTechnicalTemperatureAlias = (value: unknown) => {
    const normalized = String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return /^(temp|temperatura|temperature)(_(temp|temperatura|temperature))*$/.test(normalized);
  };

  const isInternalIdentifier = (value: unknown) => {
    const text = String(value ?? '').trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)
      || /^[0-9a-f]{12,}$/i.test(text);
  };

  const readableAlertLabel = (...values: unknown[]) =>
    values
      .map((value) => String(value ?? '').trim())
      .find((value) => value && !isInternalIdentifier(value)) ?? null;

  const formatAlertNumber = (value: unknown) => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2).replace(/\.?0+$/, '');
    }
    return readableAlertLabel(value);
  };

  const formatAlertUnit = (value: unknown) => {
    const unit = String(value ?? '').trim();
    if (!unit) return '';
    if (/^(c|celsius|ºc|°c)$/i.test(unit)) return ' °C';
    if (/^(mg_l|mg\/l)$/i.test(unit)) return ' mg/L';
    return ` ${unit}`;
  };

  const projectChoiceSlug = (project: ClientProjectChoice) =>
    slugify(project.slug || project.name || project.id);
  const projectHasSingleOperationalPath = (project: ClientProjectChoice) =>
    (project.installationsCount ?? project.installations?.length ?? 0) === 1 &&
    (project.operationalAreasCount ?? project.operationalAreas?.length ?? 0) === 1;

  function ClientProjectGate({
    clientName,
    clientSlug,
    userName,
    userEmail,
    projects,
    loading,
    onChooseProject,
    onOpenAdmin,
    onLogout,
    onBack
  }: {
    clientName: string;
    clientSlug: string;
    userName?: string | null;
    userEmail: string;
    projects: ClientProjectChoice[];
    loading: boolean;
    onChooseProject: (project: ClientProjectChoice, area?: NonNullable<ClientProjectChoice['operationalAreas']>[number]) => void;
    onOpenAdmin: () => void;
    onLogout: () => void;
    onBack: () => void;
  }) {
    const displayName = String(userName || userEmail.split('@')[0] || '').trim();
    const statusConfig = {
      operational: {
        label: 'Operacional',
        badge: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-300/20 dark:bg-emerald-300/10 dark:text-emerald-100',
      },
      attention: {
        label: 'Atenção',
        badge: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-300/20 dark:bg-amber-300/10 dark:text-amber-100',
      },
      critical: {
        label: 'Crítico',
        badge: 'border-red-200 bg-red-50 text-red-700 dark:border-red-300/20 dark:bg-red-300/10 dark:text-red-100',
      },
      offline: {
        label: 'Offline',
        badge: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-white/12 dark:bg-white/[0.06] dark:text-slate-200',
      }
    } as const;
    const formatCount = (value: number | undefined) => String(value ?? 0);
    const formatUpdatedAt = (value: string | null | undefined) => {
      if (!value) return 'Sem atualização';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return 'Sem atualização';
      return new Intl.DateTimeFormat('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(parsed);
    };
    const [drilldownProject, setDrilldownProject] = useState<ClientProjectChoice | null>(null);
    void setDrilldownProject; void drilldownProject;

    return (
      <div className="aql-vision-projects-shell relative flex min-h-screen flex-col overflow-hidden text-foreground">
        <UniversalAppHeader
          appName="AQL Vision"
          appIcon={<AqlVisionMark className="h-full w-full object-contain" />}
          onBack={onBack}
          onSignOut={onLogout}
          showSignOut
          showSystemMenuButton
          visionLabUrl="https://aqlapps.com/vision-lab"
          showClientBadge={false}
          showBrandBadge={false}
          compactHeader
          contentContainerClassName="w-full px-4 sm:px-8 lg:px-10"
          rightActions={
            <button
              type="button"
              onClick={onOpenAdmin}
              title="Criar e gerir projetos"
              className="flex items-center gap-3 text-sm font-semibold text-slate-700 transition-colors hover:text-sky-700 dark:text-slate-200 dark:hover:text-cyan-100"
            >
              <SettingsIcon className="h-4 w-4" />
              <span>Gerir projetos</span>
            </button>
          }
        />

        <section className="relative z-10 mx-auto flex w-full max-w-[94rem] flex-1 flex-col gap-5 px-4 py-6 sm:px-8 lg:px-10">
          <div className="w-full">
            <div>
              <nav aria-label="Navegação estrutural" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
                <span className="text-cyan-700 dark:text-cyan-300">Projetos</span>
                {projects.length === 1 ? (
                  <>
                    <span className="text-slate-400 dark:text-slate-600">›</span>
                    <span className="max-w-[min(70vw,40rem)] truncate text-slate-600 dark:text-slate-300">{projects[0].name}</span>
                  </>
                ) : null}
              </nav>
              <h1 className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">Projetos</h1>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{clientName}</p>
            </div>
          </div>
          {loading ? (
            <div className="flex min-h-[420px] w-full items-center justify-center rounded-xl border border-slate-200 bg-white/78 shadow-sm dark:border-white/10 dark:bg-white/[0.035]">
              <AqlVisionLoader compact label="A carregar visão operacional" detail="A preparar projetos, instalações e unidades de monitorização." className="text-left" />
            </div>
          ) : projects.length === 0 ? (
            <div className="mx-auto mt-12 w-full max-w-[440px] rounded-xl border border-dashed border-slate-300 bg-white/78 px-5 py-10 text-center shadow-sm dark:border-white/14 dark:bg-white/[0.035]">
              <FolderKanban className="mx-auto h-8 w-8 text-slate-500 dark:text-slate-400" />
              <h3 className="mt-3 text-base font-semibold text-slate-950 dark:text-white">Sem projetos ativos</h3>
              <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-slate-600 dark:text-slate-400">
                Acede à administração do cliente para criar ou ativar projetos.
              </p>
            </div>
          ) : (
              <div className="grid items-start gap-5">
                {projects.map((project) => {
                  const contextProfile = getOperationalContextProfile(project.contextType);
                  const status = project.status ?? 'operational';
                  const config = statusConfig[status];
                  const locationLabel = project.locationLabel ?? project.primaryInstallation;
                  const areas = project.operationalAreas ?? [];
                  const singleArea = areas.length === 1 ? areas[0] : null;
                  const installationGroups = Array.from(areas.reduce((groups, area) => {
                    const installationName = area.locationName ?? locationLabel ?? 'Instalação por definir';
                    const current = groups.get(installationName) ?? [];
                    current.push(area);
                    groups.set(installationName, current);
                    return groups;
                  }, new Map<string, typeof areas>()));
                  const cardVisual = project.coverImageUrl ?? null;
                  const subtitleParts = [
                    `${areas.length} ${areas.length === 1 ? contextProfile.areaCountSingularLabel : contextProfile.areaPluralLabel}`,
                    `${installationGroups.length} instalaç${installationGroups.length !== 1 ? 'ões' : 'ão'}`,
                    project.speciesFocus,
                    project.defaultPhase,
                    contextProfile.label
                  ].filter(Boolean).join(' · ');
                  const statusDotColor = { operational: 'bg-emerald-500', attention: 'bg-amber-400', critical: 'bg-rose-500', offline: 'bg-slate-500' }[status] ?? 'bg-slate-500';
                  return (
                    <article
                      key={project.id}
                      className="relative self-start overflow-hidden rounded-2xl border border-slate-200/80 bg-white text-left shadow-[0_18px_42px_-32px_rgba(15,23,42,0.34)] hover:border-sky-300 dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_18px_44px_-30px_rgba(0,0,0,0.75)] dark:hover:border-cyan-200/28"
                    >
                      {/* Status indicator */}
                      <div className="absolute right-4 top-4 z-20 flex items-center gap-1.5">
                        <div className="relative flex h-2.5 w-2.5 items-center justify-center">
                          {status === 'critical' ? (
                            <span
                              aria-hidden="true"
                              className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-55 motion-reduce:animate-none"
                            />
                          ) : null}
                          <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${statusDotColor}`} />
                        </div>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${config.badge}`}>{config.label}</span>
                      </div>

                      {/* Header */}
                      <div
                        className={`relative flex min-h-28 flex-col justify-end overflow-hidden px-5 pb-5 pt-5 ${cardVisual ? 'border-b border-slate-200/70 dark:border-white/10' : ''}`}
                        style={{ paddingRight: 120 }}
                      >
                        {cardVisual ? (
                          <>
                            <img
                              src={cardVisual}
                              alt=""
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-75 saturate-[0.82] dark:opacity-70"
                            />
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(248,250,252,0.98)_0%,rgba(248,250,252,0.92)_35%,rgba(248,250,252,0.32)_68%,rgba(248,250,252,0.12)_100%)] dark:bg-[linear-gradient(90deg,rgba(15,23,42,0.99)_0%,rgba(15,23,42,0.94)_38%,rgba(15,23,42,0.42)_72%,rgba(15,23,42,0.18)_100%)]"
                            />
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/75 to-transparent dark:from-slate-900 dark:via-slate-900/72"
                            />
                          </>
                        ) : null}
                        <h2 className="relative z-10 text-[18px] font-bold leading-tight tracking-tight text-slate-950 dark:text-white">{project.name}</h2>
                        {subtitleParts && (
                          <p className="relative z-10 mt-1 truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">{subtitleParts}</p>
                        )}
                      </div>

                      {/* Body */}
                      <div className="grid items-start gap-3 px-5 pb-4 pt-4 lg:grid-cols-2">
                        {installationGroups.length ? installationGroups.map(([installationName, installationAreas], installationIndex) => {
                          const installationAlerts = installationAreas.reduce((total, area) => total + (area.activeAlertsCount ?? 0), 0);
                          const installationPending = installationAreas.reduce((total, area) => total + (area.pendingValidationsCount ?? 0), 0);
                          const installationNeedsReview = installationAlerts > 0 || installationPending > 0;
                          return (
                            <section
                              key={installationName}
                              className={`rounded-xl border px-4 py-3 ${
                                installationNeedsReview
                                  ? 'border-rose-400/35 bg-rose-50/45 dark:border-rose-400/24 dark:bg-rose-950/12'
                                  : 'border-slate-200/80 bg-slate-50/45 dark:border-white/10 dark:bg-white/[0.025]'
                              }`}
                            >
                              <div className="mb-2.5 flex items-center justify-between gap-3">
                                <span className="min-w-0">
                                  <span className="block text-[8px] font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-cyan-200">Instalação {installationIndex + 1}</span>
                                  <span className="mt-0.5 block truncate text-[12px] font-bold text-slate-900 dark:text-slate-100">{installationName}</span>
                                </span>
                                {installationNeedsReview ? (
                                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-rose-400/40 bg-rose-50/80 px-2 py-1 text-[9px] font-bold text-rose-800 dark:border-rose-300/28 dark:bg-rose-950/34 dark:text-rose-100">
                                    <AlertTriangle className="h-3 w-3" />
                                    {installationAlerts > 0
                                      ? `${installationAlerts} alerta${installationAlerts !== 1 ? 's' : ''}`
                                      : `${installationPending} por validar`}
                                  </span>
                                ) : (
                                  <span className="inline-flex shrink-0 items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Operacional
                                  </span>
                                )}
                              </div>
                              <div className="grid gap-2">
                              {installationAreas.map((area) => {
                                const areaAlerts = area.activeAlertsCount ?? 0;
                                const areaPending = area.pendingValidationsCount ?? 0;
                                const areaNeedsReview = areaAlerts > 0 || areaPending > 0;
                                return (
                                  <button
                                    key={area.id || area.name}
                                    type="button"
                                    onClick={() => onChooseProject(project, area)}
                                    className={`group relative flex min-h-16 items-center justify-between gap-3 overflow-hidden rounded-xl border px-4 py-3 text-left transition-all ${
                                      areaNeedsReview
                                        ? 'border-rose-400/55 bg-[linear-gradient(105deg,rgba(255,241,242,0.98)_0%,rgba(255,228,230,0.9)_58%,rgba(254,205,211,0.72)_100%)] shadow-[inset_2px_0_0_rgba(225,29,72,0.78),0_12px_30px_-24px_rgba(190,18,60,0.7)] hover:border-rose-500/70 hover:shadow-[inset_2px_0_0_rgba(225,29,72,0.88),0_16px_34px_-22px_rgba(190,18,60,0.82)] dark:border-rose-400/42 dark:bg-[linear-gradient(105deg,rgba(76,5,25,0.88)_0%,rgba(45,13,29,0.78)_50%,rgba(15,23,42,0.94)_100%)] dark:shadow-[inset_2px_0_0_rgba(251,113,133,0.82),0_16px_36px_-24px_rgba(244,63,94,0.72)] dark:hover:border-rose-300/62'
                                        : 'border-slate-300/80 bg-[linear-gradient(105deg,rgba(248,250,252,0.98)_0%,rgba(241,245,249,0.92)_58%,rgba(224,242,254,0.68)_100%)] shadow-[0_12px_30px_-24px_rgba(15,23,42,0.38)] hover:border-sky-400/60 hover:shadow-[0_16px_34px_-22px_rgba(14,116,144,0.42)] dark:border-white/14 dark:bg-[linear-gradient(105deg,rgba(30,41,59,0.94)_0%,rgba(18,32,51,0.94)_54%,rgba(8,31,48,0.96)_100%)] dark:shadow-[0_16px_36px_-24px_rgba(0,0,0,0.7)] dark:hover:border-cyan-200/32'
                                    }`}
                                  >
                                    <span className="relative z-10 min-w-0">
                                      <span className="flex min-w-0 items-center gap-2">
                                        <span className={`block truncate text-[13px] font-bold ${
                                          areaNeedsReview
                                            ? 'text-rose-950 dark:text-rose-50'
                                            : 'text-slate-950 dark:text-slate-50'
                                        }`}>{area.name}</span>
                                        {!areaNeedsReview ? (
                                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/35 bg-emerald-50/75 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-emerald-700 dark:border-emerald-300/24 dark:bg-emerald-300/[0.08] dark:text-emerald-200">
                                            <CheckCircle2 className="h-2.5 w-2.5" />
                                            Operacional
                                          </span>
                                        ) : null}
                                      </span>
                                      <span className={`mt-0.5 block truncate text-[10px] font-medium ${
                                        areaNeedsReview
                                          ? 'text-rose-800/72 dark:text-rose-100/68'
                                          : 'text-slate-600 dark:text-slate-300/72'
                                      }`}>
                                        {area.species ? `${contextProfile.categoryLabel}: ${area.species}` : `${contextProfile.categoryLabel} por definir`}
                                        {' · '}
                                        {area.phase ? `${contextProfile.phaseLabel}: ${area.phase}` : `${contextProfile.phaseLabel} por definir`}
                                      </span>
                                    </span>
                                    <span className="relative z-10 flex shrink-0 items-center gap-2">
                                      {(area.unitsCount ?? 0) > 0 ? (
                                        <span className="hidden rounded-full border border-slate-300/70 bg-white/55 px-2 py-1 text-[9px] font-semibold text-slate-600 sm:inline-flex dark:border-white/12 dark:bg-white/[0.04] dark:text-slate-300">
                                          {area.unitsCount} unidade{area.unitsCount !== 1 ? 's' : ''}
                                        </span>
                                      ) : null}
                                      {areaNeedsReview ? (
                                        <span className="rounded-full border border-rose-400/52 bg-white/58 px-2.5 py-1 text-[10px] font-bold text-rose-800 shadow-sm dark:border-rose-300/34 dark:bg-rose-950/42 dark:text-rose-100">
                                          {areaAlerts > 0 ? `${areaAlerts} alerta${areaAlerts !== 1 ? 's' : ''}` : `${areaPending} por validar`}
                                        </span>
                                      ) : null}
                                      <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-0.5 ${
                                        areaNeedsReview
                                          ? 'text-rose-600 dark:text-rose-200'
                                          : 'text-sky-700 dark:text-cyan-200'
                                      }`} />
                                    </span>
                                  </button>
                                );
                              })}
                              </div>
                            </section>
                          );
                        }) : (
                          <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center dark:border-white/14">
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Sem áreas operacionais configuradas</p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Configura a instalação e as respetivas áreas para iniciar a monitorização.</p>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="space-y-3 border-t border-slate-200/75 bg-white/45 px-5 py-4 dark:border-white/10 dark:bg-white/[0.025]">
                        <div className="flex items-end justify-end gap-3">
                          <span className="shrink-0 text-right">
                            <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500">Atualizado</span>
                            <span className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300">{formatUpdatedAt(project.lastUpdatedAt)}</span>
                          </span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
          )}
        </section>
        <footer className="pointer-events-auto relative z-10 mt-auto flex flex-col items-center gap-2.5 px-4 pb-2 pt-10">
          <a
            href="https://aqlengineering.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <AqlSeahorseLogo
              alt="AQL Engineering"
              className="h-9 w-9 rounded-full border border-slate-200 bg-white object-cover p-1 shadow-lg dark:border-slate-700/50 dark:bg-slate-900"
            />
          </a>
          <p className="text-[10px] font-medium tracking-[0.18em] text-slate-500 dark:text-slate-400">AQL Engineering, lda.</p>
        </footer>
      </div>
    );
  }

  const toLocalDateInputValue = (value: string | null | undefined) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromLocalDateInputValue = (value: string | null | undefined) => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const startOfLocalDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const endOfLocalDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999);
  const startOfLocalWeek = (value: Date) => {
    const base = startOfLocalDay(value);
    const weekday = (base.getDay() + 6) % 7;
    base.setDate(base.getDate() - weekday);
    return base;
  };
  const endOfLocalWeek = (value: Date) => {
    const base = startOfLocalWeek(value);
    return new Date(base.getFullYear(), base.getMonth(), base.getDate() + 6, 23, 59, 59, 999);
  };
  const startOfLocalMonth = (value: Date) => new Date(value.getFullYear(), value.getMonth(), 1);
  const endOfLocalMonth = (value: Date) => new Date(value.getFullYear(), value.getMonth() + 1, 0, 23, 59, 59, 999);

  const normalizePhaseOption = (value: unknown): Phase | null => {
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-');
    if (normalized.includes('larv')) return 'larval';
    if (normalized.includes('juven')) return 'juvenile';
    if (normalized.includes('pre') || normalized.includes('grow')) return 'pre-grow';
    if (normalized.includes('adult')) return 'adult';
    return null;
  };

  type MvpTableScope = {
    clientId?: string | null;
    projectId?: string | null;
    species?: string | null;
    phase?: Phase | null;
    site?: string | null;
    tank?: string | null;
  };

  const KV_STORE_TABLE = 'kv_store_113fe7c2';

  const parseKvValue = (value: any) => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const pickValue = (row: any, keys: string[]) => {
    for (const key of keys) {
      const value = row?.[key] ?? row?.metadata?.[key];
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
  };

  const pickString = (row: any, keys: string[]) => {
    const value = pickValue(row, keys);
    return value === null ? null : String(value);
  };

  const pickNumber = (row: any, keys: string[]) => {
    const value = pickValue(row, keys);
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value.replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const readDateValue = (row: any) => {
    const metadata = row?.metadata && typeof row.metadata === 'object' ? row.metadata : null;
    return pickString(row, [
      'captured_at',
      'detected_at',
      'triggered_at',
      'last_seen_at',
      'last_read_at',
      'lastReadAt',
      'read_at',
      'measured_at',
      'recorded_at',
      'updated_at',
      'updatedAt',
      'created_at',
      'createdAt',
      'timestamp',
      'Timestamp'
    ]) ?? (metadata
      ? pickString(metadata, [
        'timestamp',
        'last_read_at',
        'lastReadAt',
        'recorded_at',
        'captured_at',
        'detected_at',
        'created_at',
        'updated_at'
      ])
      : null);
  };

  const byLatestDate = (a: any, b: any) => new Date(readDateValue(b) ?? 0).getTime() - new Date(readDateValue(a) ?? 0).getTime();

  const TABLE_ORDER_COLUMNS: Record<string, string> = {
    aql_alerts: 'triggered_at',
    aql_cases: 'created_at',
    aql_detections: 'detected_at',
    aql_sensor_readings: 'timestamp',
    aql_vision_captures: 'timestamp'
  };

  const fetchTableRows = async (table: string, limit = 200) => {
    let query = supabase.from(table).select('*');
    const orderColumn = TABLE_ORDER_COLUMNS[table];
    if (orderColumn) query = query.order(orderColumn, { ascending: false });
    const { data, error } = await query.limit(limit);
    if (error) {
      console.warn(`AQL Vision MVP: ${table} unavailable`, error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  };

  const fetchKvRows = async (pattern: string, limit = 100) => {
    const { data, error } = await supabase
      .from(KV_STORE_TABLE)
      .select('key,value')
      .ilike('key', pattern)
      .limit(limit);
    if (error) {
      console.warn(`AQL Vision MVP: ${KV_STORE_TABLE} unavailable`, error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  };

  const idFrom = (row: any, keys: string[]) => pickString(row, keys) ?? '';

  const normalizeKvSummary = (rawValue: any): MvpDashboardSummary | null => {
    const value = parseKvValue(rawValue);
    if (!value || typeof value !== 'object') return null;
    const source = value.dashboard ?? value.summary ?? value.status_summary ?? value;
    const latestCapture = source.latestCapture ?? source.latest_capture ?? source.capture ?? null;
    const sensors = source.sensors ?? source.sensor_summary ?? null;
    const device = source.device ?? source.kit ?? null;

    return {
      kitId: source.kit_id ?? source.kitId ?? null,
      kitName: source.kit_name ?? source.kitName ?? source.name ?? null,
      status: source.status ?? source.overall_status ?? source.state ?? null,
      reason: source.reason ?? source.status_reason ?? source.motivo ?? null,
      updatedAt: source.updated_at ?? source.updatedAt ?? source.timestamp ?? null,
      latestCapture: latestCapture ? {
        imageUrl: latestCapture.image_url ?? latestCapture.imageUrl ?? latestCapture.url ?? null,
        capturedAt: latestCapture.captured_at ?? latestCapture.capturedAt ?? latestCapture.timestamp ?? null,
        tank: latestCapture.tank ?? latestCapture.tank_name ?? null,
        phase: latestCapture.phase ?? latestCapture.biological_phase ?? null,
        species: latestCapture.species ?? null,
        detectionStatus: latestCapture.detection_status ?? latestCapture.detectionStatus ?? null
      } : null,
      alertsActive: source.alerts_active ?? source.active_alerts ?? null,
      pendingValidation: source.pending_validation ?? source.pendingValidation ?? null,
      sensors: sensors ? {
        temperature: typeof sensors.temperature === 'number' ? sensors.temperature : sensors.temp ?? null,
        ph: typeof sensors.ph === 'number' ? sensors.ph : sensors.pH ?? null,
        oxygen: sensors.oxygen ?? sensors.dissolved_oxygen ?? sensors.o2 ?? null,
        redox: sensors.redox ?? sensors.orp ?? sensors.redox_mv ?? sensors.orp_mv ?? null,
        status: sensors.status ?? sensors.state ?? null
      } : null,
      device: device ? {
        online: typeof device.online === 'boolean' ? device.online : null,
        lastCommunication: device.last_communication ?? device.lastCommunication ?? device.last_seen_at ?? null,
        heartbeatIntervalSeconds: device.heartbeat_interval_seconds ?? device.heartbeatIntervalSeconds ?? null,
        cameraOk: typeof device.camera_ok === 'boolean' ? device.camera_ok : typeof device.cameraOk === 'boolean' ? device.cameraOk : null,
        sensorsOk: typeof device.sensors_ok === 'boolean' ? device.sensors_ok : typeof device.sensorsOk === 'boolean' ? device.sensorsOk : null
      } : null
    };
  };

  const normalizeLegacyVisionAlert = (row: any): RawVisionAlert | null => {
    const value = parseKvValue(row?.value) ?? row;
    if (!value || typeof value !== 'object') return null;

    const legacyMetadata = value.metadata && typeof value.metadata === 'object' ? { ...value.metadata } : {};
    delete legacyMetadata.VisionKit;
    const id = String(value.alert_id ?? value.source_id ?? value.id ?? row.key?.replace(/^visionAlert:/, '') ?? crypto.randomUUID());
    const timestamp = String(value.detected_at ?? value.detectedAt ?? legacyMetadata.Timestamp ?? value.created_at ?? value.createdAt ?? value.updated_at ?? value.updatedAt ?? new Date().toISOString());
    const aiAnalysis = value.ai_analysis ?? value.aiAnalysis ?? null;
    const confidence = value.model_confidence ?? value.modelConfidence ?? aiAnalysis?.confidence ?? value.confidence ?? null;

    return {
      id,
      alert_id: id,
      client_id: value.client_id ?? legacyMetadata.clientId ?? undefined,
      project_id: value.project_id ?? legacyMetadata.project_id ?? legacyMetadata.projectId ?? undefined,
      site_id: value.site_id ?? legacyMetadata.site ?? undefined,
      tank_id: value.tank_id ?? legacyMetadata.tank ?? legacyMetadata.location ?? undefined,
      userId: value.userId,
      userEmail: value.user_email ?? value.userEmail,
      imageUrl: value.image_url ?? value.imageUrl ?? value.url ?? '',
      species: value.species ?? legacyMetadata.species ?? undefined,
      biological_phase: value.biological_phase ?? value.phase ?? legacyMetadata.phase ?? undefined,
      detected_at: timestamp,
      model_confidence: confidence,
      alert_type: value.alert_type ?? value.alertType ?? value.problem_name ?? value.problemName ?? 'A confirmar',
      severity: value.severity ?? legacyMetadata.severity ?? null,
      status: value.status ?? 'pending',
      problemName: value.problem_name ?? value.problemName ?? value.alert_type ?? 'A confirmar',
      problemDescription: value.problem_description ?? value.problemDescription,
      proposedSolution: value.proposed_solution ?? value.proposedSolution,
      inference: value.inference,
      aiAnalysis,
      createdAt: String(value.created_at ?? value.createdAt ?? timestamp),
      updatedAt: value.updated_at ?? value.updatedAt,
      validationResult: value.validationResult,
      validation_result: value.validation_result,
      anomalyType: value.anomalyType,
      anomaly_type: value.anomaly_type,
      validationNotes: value.validationNotes,
      validation_notes: value.validation_notes ?? value.lab_observations,
      validated_at: value.validated_at ?? value.validatedAt,
      validated_by: value.validated_by ?? value.validatedBy,
      metadata: {
        ...legacyMetadata,
        site: legacyMetadata.site ?? legacyMetadata.location,
        tank: legacyMetadata.tank ?? legacyMetadata.location,
        detection_status: value.inference || confidence ? 'Peixe detetado' : 'A confirmar',
        kv_key: row.key
      }
    };
  };

  const isFinalValidationStatus = (value: unknown) => {
    const status = String(value ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    return ['confirmed', 'validated', 'rejected', 'discarded', 'inconclusive', 'resolved', 'closed', 'inactive', 'resolvido', 'classificado'].includes(status);
  };

  const alertOperationalId = (alert: RawVisionAlert) =>
    String(alert.case_id ?? alert.id ?? alert.alert_id ?? '').trim();

  const alertHasFinalValidation = (alert: RawVisionAlert) =>
    isFinalValidationStatus(
      alert.status ??
      alert.validation_result ??
      alert.validationResult ??
      alert.metadata?.status ??
      alert.metadata?.validation_result
    );

  const isActiveAlertRow = (row: any) => {
    const status = row?.status ?? row?.state ?? row?.validation_result ?? row?.validationResult;
    if (isFinalValidationStatus(status)) return false;
    const resolvedAt = pickString(row, ['resolved_at', 'closed_at', 'validated_at']);
    const validationResult = pickString(row, ['validation_result', 'validationResult']);
    if (resolvedAt && validationResult) return false;
    return true;
  };

  type SensorMetricKey = 'temperature' | 'ph' | 'dissolved_oxygen' | 'redox' | 'salinity';

  const SENSOR_LIMITS: Record<SensorMetricKey, {
    label: string;
    unit: string;
    min: number | null;
    max: number | null;
    criticalMin?: number;
    criticalMax?: number;
    lowTitle: string;
    highTitle: string;
  }> = {
    temperature: {
      label: 'Temperatura',
      unit: '°C',
      min: 18,
      max: 22,
      criticalMin: 17,
      criticalMax: 30,
      lowTitle: 'Temperatura baixa',
      highTitle: 'Temperatura excessiva'
    },
    ph: {
      label: 'pH',
      unit: 'pH',
      min: 7,
      max: 8.2,
      criticalMin: 6.7,
      criticalMax: 8.5,
      lowTitle: 'pH abaixo do alvo',
      highTitle: 'pH acima do alvo'
    },
    dissolved_oxygen: {
      label: 'Oxigénio dissolvido',
      unit: 'mg/L',
      min: 6,
      max: null,
      criticalMin: 4,
      lowTitle: 'Oxigénio dissolvido baixo',
      highTitle: 'Oxigénio dissolvido fora do alvo'
    },
    redox: {
      label: 'Redox / ORP',
      unit: 'mV',
      min: 250,
      max: 400,
      criticalMin: 200,
      criticalMax: 450,
      lowTitle: 'Redox abaixo do alvo',
      highTitle: 'Redox acima do alvo'
    },
    salinity: {
      label: 'Salinidade',
      unit: 'PSU',
      min: 28,
      max: 37,
      criticalMin: 24,
      criticalMax: 40,
      lowTitle: 'Salinidade baixa',
      highTitle: 'Salinidade elevada'
    }
  };

  const sensorMetricFromType = (value: unknown): SensorMetricKey | null => {
    const normalized = String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    if (!normalized) return null;
    if (normalized.includes('temp')) return 'temperature';
    if (normalized === 'ph' || normalized.includes('ph')) return 'ph';
    if (normalized.includes('oxygen') || normalized.includes('oxigen') || normalized.includes('o2') || normalized.includes('dissolved')) return 'dissolved_oxygen';
    if (normalized.includes('redox') || normalized.includes('orp')) return 'redox';
    if (normalized.includes('salin')) return 'salinity';
    return null;
  };

  const classifySensorReadingAlert = (metric: SensorMetricKey, value: number) => {
    const config = SENSOR_LIMITS[metric];
    const below = config.min !== null && value < config.min;
    const above = config.max !== null && value > config.max;
    if (!below && !above) return null;

    const critical =
      (below && typeof config.criticalMin === 'number' && value < config.criticalMin) ||
      (above && typeof config.criticalMax === 'number' && value > config.criticalMax);

    return {
      title: below ? config.lowTitle : config.highTitle,
      severity: critical ? 'critical' : 'high',
      direction: below ? 'below' : 'above'
    } as const;
  };

  const readingNumber = (row: any, keys: string[]) => {
    if (!row) return null;
    const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    return pickNumber(row, keys) ?? pickNumber(metadata, keys);
  };

  const normalizeOperationalSensorValue = (metric: SensorMetricKey | null, value: number | null) => {
    if (value === null || !Number.isFinite(value)) return null;
    if (metric === 'ph') return value > 0 && value <= 14 ? value : null;
    if (metric === 'temperature') return value > 0 ? value : null;
    if (metric === 'dissolved_oxygen' || metric === 'salinity') return value > 0 ? value : null;
    if (metric === 'redox') return value === 0 ? null : value;
    return value;
  };

  const latestReadingFor = (readings: any[], sensors: any[], names: string[]) => {
    const sensorById = new Map(sensors.map((sensor) => [idFrom(sensor, ['sensor_id', 'id']), sensor]));
    return readings
      .filter((reading) => {
        const sensor = sensorById.get(idFrom(reading, ['sensor_id', 'id']));
        const metadata = reading?.metadata && typeof reading.metadata === 'object' ? reading.metadata : {};
        const sensorType = `${pickString(reading, ['sensor_type', 'type', 'metric', 'parameter'])
          ?? pickString(metadata, ['sensor_type', 'type', 'metric', 'parameter'])
          ?? pickString(sensor, ['sensor_type', 'type', 'name'])
          ?? ''}`.toLowerCase();
        const metric = sensorMetricFromType(sensorType);
        if (!names.some((name) => sensorType.includes(name))) return false;
        return normalizeOperationalSensorValue(metric, readingNumber(reading, ['value', 'reading_value', 'last_value', 'reading'])) !== null;
      })
      .sort(byLatestDate)[0] ?? null;
  };

  const normalizedLabel = (value: unknown) => slugify(String(value ?? ''));

  const sameId = (left: unknown, right: unknown) =>
    Boolean(left && right && String(left) === String(right));

  const rowIdSet = (rows: any[], keys: string[]) =>
    new Set(rows.map((row) => idFrom(row, keys)).filter(Boolean));

  const rowHasId = (row: any, keys: string[], ids: Set<string>) => {
    if (!ids.size) return false;
    return keys.some((key) => {
      const value = pickString(row, [key]);
      return value ? ids.has(value) : false;
    });
  };

  const matchesClient = (row: any, clientId?: string | null) =>
    !clientId || sameId(pickString(row, ['client_id']), clientId);

  const matchesProject = (row: any, projectId?: string | null) =>
    !projectId || sameId(pickString(row, ['project_id']), projectId);

  const matchesNameOrCode = (row: any, expected?: string | null) =>
    !expected || [row?.name, row?.code, row?.short_code].some((value) => normalizedLabel(value) === normalizedLabel(expected));

  const matchesSpecies = (row: any, expected?: string | null) =>
    !expected || normalizedLabel(pickString(row, ['species'])) === normalizedLabel(expected);

  const matchesPhase = (row: any, expected?: Phase | null) =>
    !expected || normalizePhaseOption(pickString(row, ['phase', 'biological_phase'])) === expected;

  const kvKitId = (row: any) => String(row?.key ?? '').match(/^aql:kit:([^:]+):status$/i)?.[1] ?? '';

  const heartbeatIntervalSecondsFor = (kit: any, device: any) => {
    const value = pickNumber(kit, ['heartbeat_interval_seconds', 'heartbeatIntervalSeconds'])
      ?? pickNumber(device, ['heartbeat_interval_seconds', 'heartbeatIntervalSeconds'])
      ?? pickNumber(kit, ['capture_interval_seconds'])
      ?? 120;
    return Math.max(15, value);
  };

  const heartbeatOnline = (lastCommunication: string | null | undefined, heartbeatIntervalSeconds: number) => {
    if (!lastCommunication) return null;
    const heartbeatMs = new Date(lastCommunication).getTime();
    if (!Number.isFinite(heartbeatMs)) return null;
    const ageMs = Date.now() - heartbeatMs;
    const staleAfterMs = Math.max(90, heartbeatIntervalSeconds * 3) * 1000;
    return ageMs <= staleAfterMs;
  };

  async function loadMvpVisionData(scope: MvpTableScope): Promise<{ alerts: RawVisionAlert[]; summary: MvpDashboardSummary | null }> {
    const [
      allCaseRows,
      allAlertRows,
      allCaptureRows,
      allDetectionRows,
      allSensorRows,
      allSensorReadingRows,
      allKitRows,
      allCameraRows,
      allDeviceRows,
      allZoneRows,
      allLocationRows,
      aqlKvRows,
      allLegacyAlertRows
    ] = await Promise.all([
      fetchTableRows('aql_cases'),
      fetchTableRows('aql_alerts'),
      fetchTableRows('aql_vision_captures'),
      fetchTableRows('aql_detections'),
      fetchTableRows('aql_sensors', 2000),
      fetchTableRows('aql_sensor_readings', 1000),
      fetchTableRows('aql_kits'),
      fetchTableRows('aql_cameras'),
      fetchTableRows('aql_devices'),
      fetchTableRows('aql_zones'),
      fetchTableRows('aql_locations'),
      fetchKvRows('aql:%'),
      fetchTableRows('aql_vision_alerts_legacy')
    ]);

    const hasLocationFilter = Boolean(scope.site);
    const hasTankFilter = Boolean(scope.tank);
    const hasZoneFilter = Boolean(scope.tank || scope.species || scope.phase);
    const hasProjectFilter = Boolean(scope.projectId);

    const locationRows = allLocationRows.filter((row) =>
      matchesClient(row, scope.clientId) &&
      matchesProject(row, scope.projectId) &&
      matchesNameOrCode(row, scope.site)
    );
    const locationIds = rowIdSet(locationRows, ['location_id', 'id']);

    const zoneRows = allZoneRows.filter((row) =>
      matchesClient(row, scope.clientId) &&
      matchesProject(row, scope.projectId) &&
      (!hasLocationFilter || rowHasId(row, ['location_id'], locationIds)) &&
      matchesNameOrCode(row, scope.tank) &&
      (hasTankFilter || matchesSpecies(row, scope.species)) &&
      (hasTankFilter || matchesPhase(row, scope.phase))
    );
    const zoneIds = rowIdSet(zoneRows, ['zone_id', 'id']);

    const kitRows = allKitRows.filter((row) =>
      matchesClient(row, scope.clientId) &&
      matchesProject(row, scope.projectId) &&
      (!hasLocationFilter || rowHasId(row, ['location_id'], locationIds) || rowHasId(row, ['zone_id'], zoneIds)) &&
      (!hasZoneFilter || rowHasId(row, ['zone_id'], zoneIds))
    );
    const kitIds = rowIdSet(kitRows, ['kit_id', 'id']);

    const cameraRows = allCameraRows.filter((row) =>
      rowHasId(row, ['kit_id'], kitIds) || rowHasId(row, ['zone_id'], zoneIds)
    );
    const cameraIds = rowIdSet(cameraRows, ['camera_id', 'id']);

    const sensorRows = allSensorRows.filter((row) => {
      const linkedKit = kitIds.size > 0 && rowHasId(row, ['kit_id'], kitIds);
      const linkedZone = zoneIds.size > 0 && rowHasId(row, ['zone_id'], zoneIds);
      return linkedKit || linkedZone;
    });
    const sensorDeviceIds = rowIdSet(sensorRows, ['device_id']);

    const deviceRows = allDeviceRows.filter((row) => {
      if (!matchesClient(row, scope.clientId)) return false;
      if (hasProjectFilter || hasLocationFilter || hasZoneFilter) {
        return rowHasId(row, ['kit_id'], kitIds) || rowHasId(row, ['device_id', 'id'], sensorDeviceIds);
      }
      return true;
    });
    const deviceIds = rowIdSet(deviceRows, ['device_id', 'id']);
    sensorDeviceIds.forEach((id) => deviceIds.add(id));

    const scopedSensorReadingRows = allSensorReadingRows.filter((row) => {
      const linkedDevice = rowHasId(row, ['device_id'], deviceIds);
      const linkedKit = rowHasId(row, ['kit_id'], kitIds);
      const linkedZone = rowHasId(row, ['zone_id', 'tank_id'], zoneIds);
      const linkedSensor = rowHasId(row, ['sensor_id'], rowIdSet(sensorRows, ['sensor_id', 'id']));
      const linkedLocation = rowHasId(row, ['location_id', 'site_id'], locationIds);
      const linkedToConfiguredScope = linkedSensor || linkedKit || linkedZone || linkedDevice || linkedLocation;
      if (!linkedToConfiguredScope) return false;
      if (scope.clientId && !matchesClient(row, scope.clientId) && !linkedSensor && !linkedKit && !linkedZone && !linkedDevice && !linkedLocation) return false;
      if (scope.projectId && !matchesProject(row, scope.projectId) && !linkedSensor && !linkedKit && !linkedZone && !linkedDevice && !linkedLocation) return false;
      if (hasLocationFilter && !linkedLocation && !linkedZone && !linkedKit && !linkedDevice && !linkedSensor) return false;
      if (hasZoneFilter && !linkedZone && !linkedKit && !linkedDevice && !linkedSensor) return false;
      return true;
    });
    const sensorReadingRows = Array.from(
      new Map(
        scopedSensorReadingRows
          .sort(byLatestDate)
          .map((row, index) => [idFrom(row, ['reading_id', 'id']) || `sensor-reading-${index}`, row])
      ).values()
    );

    const captureRows = allCaptureRows.filter((row) => {
      const linkedDevice = rowHasId(row, ['device_id'], deviceIds);
      const linkedZone = rowHasId(row, ['zone_id', 'tank_id'], zoneIds);
      const linkedLocation = rowHasId(row, ['location_id', 'site_id'], locationIds);

      if (scope.clientId && !matchesClient(row, scope.clientId) && !linkedDevice && !linkedZone) return false;
      if (scope.projectId && !matchesProject(row, scope.projectId) && !linkedDevice && !linkedZone) return false;
      if (hasLocationFilter && !linkedLocation && !linkedZone && !linkedDevice) return false;
      if (hasZoneFilter && !linkedZone && !linkedDevice) return false;
      if (scope.species && !matchesSpecies(row, scope.species) && !linkedZone && !linkedDevice) return false;
      if (scope.phase && !matchesPhase(row, scope.phase) && !linkedZone && !linkedDevice) return false;
      return true;
    });
    const captureIds = rowIdSet(captureRows, ['capture_id', 'vision_capture_id', 'id']);

    const detectionRows = allDetectionRows.filter((row) =>
      rowHasId(row, ['capture_id', 'vision_capture_id'], captureIds)
    );
    const detectionIds = rowIdSet(detectionRows, ['detection_id', 'id']);

    const alertRows = allAlertRows.filter((row) => {
      const linkedZone = rowHasId(row, ['zone_id', 'tank_id'], zoneIds);
      const linkedCamera = rowHasId(row, ['camera_id'], cameraIds);
      const linkedDevice = rowHasId(row, ['device_id'], deviceIds);
      const linkedLocation = rowHasId(row, ['location_id', 'site_id'], locationIds);

      if (scope.clientId && !matchesClient(row, scope.clientId) && !linkedZone && !linkedCamera && !linkedDevice) return false;
      if (scope.projectId && !matchesProject(row, scope.projectId) && !linkedZone && !linkedCamera && !linkedDevice) return false;
      if (hasLocationFilter && !linkedLocation && !linkedZone && !linkedCamera && !linkedDevice) return false;
      if (hasZoneFilter && !linkedZone && !linkedCamera && !linkedDevice) return false;
      return true;
    });
    const alertIds = rowIdSet(alertRows, ['alert_id', 'id']);

    const caseRows = allCaseRows.filter((row) => {
      const linkedAlert = rowHasId(row, ['alert_id'], alertIds);
      const linkedCapture = rowHasId(row, ['capture_id', 'source_capture_id'], captureIds);
      const linkedDetection = rowHasId(row, ['detection_id'], detectionIds);
      const linkedZone = rowHasId(row, ['zone_id', 'tank_id'], zoneIds);
      const linkedLocation = rowHasId(row, ['location_id', 'site_id'], locationIds);

      if (scope.clientId && !matchesClient(row, scope.clientId) && !linkedAlert && !linkedCapture && !linkedZone) return false;
      if (scope.projectId && !matchesProject(row, scope.projectId) && !linkedAlert && !linkedCapture && !linkedZone) return false;
      if (hasLocationFilter && !linkedLocation && !linkedZone && !linkedAlert && !linkedCapture) return false;
      if (hasZoneFilter && !linkedZone && !linkedAlert && !linkedCapture) return false;
      if (scope.species && !matchesSpecies(row, scope.species) && !linkedZone && !linkedCapture) return false;
      if (scope.phase && !matchesPhase(row, scope.phase) && !linkedZone && !linkedCapture) return false;
      if (!linkedAlert && !linkedCapture && !linkedDetection && scope.clientId && !matchesClient(row, scope.clientId)) return false;
      return true;
    });

    const zonesById = new Map(zoneRows.map((row) => [idFrom(row, ['zone_id', 'id']), row]));
    const locationsById = new Map(locationRows.map((row) => [idFrom(row, ['location_id', 'id']), row]));
    const alertsById = new Map(alertRows.map((row) => [idFrom(row, ['alert_id', 'id']), row]));
    const capturesById = new Map(captureRows.map((row) => [idFrom(row, ['capture_id', 'vision_capture_id', 'id']), row]));
    const sensorsById = new Map(sensorRows.map((row) => [idFrom(row, ['sensor_id', 'id']), row]));
    const detectionsByAlertId = new Map<string, any[]>();
    detectionRows.forEach((row) => {
      const key = idFrom(row, ['alert_id']);
      if (!key) return;
      detectionsByAlertId.set(key, [...(detectionsByAlertId.get(key) ?? []), row]);
    });

    const activeZone = zoneRows.find((zone) => matchesNameOrCode(zone, scope.tank))
      ?? zoneRows.find((zone) => scope.clientId && sameId(zone.client_id, scope.clientId))
      ?? zoneRows[0]
      ?? null;
    const activeKit = kitRows.find((kit) => activeZone && String(kit.zone_id ?? '') === String(activeZone.zone_id ?? activeZone.id ?? ''))
      ?? kitRows.find((kit) => scope.clientId && matchesClient(kit, scope.clientId))
      ?? kitRows[0]
      ?? null;
    const activeKitId = idFrom(activeKit, ['kit_id', 'id']);

    const findSensorForAlert = (params: {
      metadata?: Record<string, any> | null;
      row?: any;
      alertRow?: any;
      kit?: any;
      zone?: any;
      sensorType?: string | null;
    }) => {
      const metadata = params.metadata ?? {};
      const directId = pickString(metadata, ['sensor_id', 'sensorId'])
        ?? pickString(params.row, ['sensor_id', 'sensorId'])
        ?? pickString(params.alertRow, ['sensor_id', 'sensorId']);
      if (directId && sensorsById.has(directId)) return sensorsById.get(directId);

      const kitId = idFrom(params.kit, ['kit_id', 'id']) || pickString(metadata, ['kit_id', 'kitId']) || pickString(params.alertRow, ['kit_id', 'kitId']);
      const zoneId = idFrom(params.zone, ['zone_id', 'id']) || pickString(metadata, ['zone_id', 'tank_id']);
      const deviceId = pickString(metadata, ['device_id', 'deviceId']) ?? pickString(params.alertRow, ['device_id', 'deviceId']);
      const normalizedType = String(params.sensorType ?? pickString(metadata, ['sensor_type', 'sensorType', 'metric', 'parameter']) ?? '').toLowerCase();

      return sensorRows.find((sensor) => {
        const sameSensorKit = kitId && String(sensor.kit_id ?? '') === kitId;
        const sameSensorZone = zoneId && String(sensor.zone_id ?? '') === zoneId;
        const sameDevice = deviceId && String(sensor.device_id ?? '') === deviceId;
        const sensorType = String(sensor.sensor_type ?? sensor.type ?? sensor.name ?? '').toLowerCase();
        const matchesType = !normalizedType || sensorType.includes(normalizedType) || normalizedType.includes(sensorType);
        return (sameSensorKit || sameSensorZone || sameDevice) && matchesType;
      });
    };

    const mappedAqlCases = [...caseRows].sort(byLatestDate).map((caseRow): RawVisionAlert => {
      const alertRow = alertsById.get(idFrom(caseRow, ['alert_id'])) ?? {};
      const alertDetections = detectionsByAlertId.get(idFrom(caseRow, ['alert_id'])) ?? [];
      const detection = alertDetections.sort(byLatestDate)[0] ?? detectionRows.find((row) => idFrom(row, ['case_id']) === idFrom(caseRow, ['case_id'])) ?? {};
      const captureId = pickString(caseRow, ['capture_id', 'source_capture_id'])
        ?? pickString(alertRow, ['capture_id', 'vision_capture_id'])
        ?? pickString(detection, ['capture_id', 'vision_capture_id']);
      const captureRow = (captureId ? capturesById.get(captureId) : null) ?? {};
      const zone = zonesById.get(pickString(caseRow, ['zone_id']) ?? pickString(alertRow, ['zone_id', 'tank_id']) ?? pickString(captureRow, ['zone_id', 'tank_id']) ?? '')
        ?? activeZone
        ?? {};
      const location = locationsById.get(pickString(zone, ['location_id']) ?? pickString(caseRow, ['location_id']) ?? pickString(alertRow, ['location_id', 'site_id']) ?? '')
        ?? {};
      const kit = kitRows.find((row) => String(row.zone_id ?? '') === String(zone.zone_id ?? zone.id ?? '')) ?? activeKit ?? {};
      const timestamp = readDateValue(captureRow) ?? readDateValue(alertRow) ?? readDateValue(caseRow) ?? new Date().toISOString();
      const mergedMetadata = {
        ...(caseRow.metadata ?? {}),
        ...(alertRow.metadata ?? {})
      };
      const sensorType = pickString(mergedMetadata, ['sensor_type', 'sensorType']);
      const sensor = findSensorForAlert({ metadata: mergedMetadata, row: caseRow, alertRow, kit, zone, sensorType });
      const sensorValue = pickNumber(mergedMetadata, ['sensor_value', 'value', 'reading_value', 'calibrated_value', 'raw_value', 'last_value'])
        ?? pickNumber(caseRow, ['value', 'reading_value', 'calibrated_value', 'raw_value', 'last_value'])
        ?? pickNumber(alertRow, ['value', 'reading_value', 'calibrated_value', 'raw_value', 'last_value']);
      const storedAiAnalysis = mergedMetadata.ai_analysis ?? mergedMetadata.gemini_analysis?.analysis ?? alertRow.ai_analysis ?? caseRow.ai_analysis ?? null;
      const mappedAiAnalysis = storedAiAnalysis && typeof storedAiAnalysis === 'object'
        ? {
          confidence: typeof storedAiAnalysis.confidence === 'number' ? storedAiAnalysis.confidence : undefined,
          detectedIssues: Array.isArray(storedAiAnalysis.suspectedFindings)
            ? storedAiAnalysis.suspectedFindings.map(String)
            : Array.isArray(storedAiAnalysis.detectedIssues)
              ? storedAiAnalysis.detectedIssues.map(String)
              : Array.isArray(mergedMetadata.gemini_analysis_tags)
                ? mergedMetadata.gemini_analysis_tags.map(String)
                : undefined,
          recommendation: String(storedAiAnalysis.operationalAssessment ?? storedAiAnalysis.recommendedNextSteps ?? storedAiAnalysis.description ?? '').trim() || undefined
        }
        : null;

      return {
        id: idFrom(caseRow, ['case_id', 'id']),
        case_id: idFrom(caseRow, ['case_id', 'id']),
        alert_id: idFrom(caseRow, ['alert_id']),
        capture_id: captureId ?? undefined,
        detection_id: idFrom(detection, ['detection_id', 'id']) || undefined,
        kit_id: idFrom(kit, ['kit_id', 'id']) || undefined,
        client_id: pickString(caseRow, ['client_id']) ?? pickString(alertRow, ['client_id']) ?? undefined,
        project_id: pickString(caseRow, ['project_id']) ?? pickString(alertRow, ['project_id']) ?? pickString(captureRow, ['project_id']) ?? scope.projectId ?? undefined,
        site_id: pickString(caseRow, ['location_id']) ?? pickString(alertRow, ['site_id', 'location_id']) ?? undefined,
        tank_id: pickString(caseRow, ['zone_id']) ?? pickString(alertRow, ['tank_id', 'zone_id']) ?? undefined,
        imageUrl: pickString(caseRow, ['image_url', 'imageUrl'])
          ?? pickString(captureRow, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url'])
          ?? pickString(alertRow, ['image_url', 'imageUrl'])
          ?? pickString(detection, ['image_url', 'imageUrl'])
          ?? '',
        species: pickString(caseRow, ['species'])
          ?? pickString(caseRow.metadata, ['species'])
          ?? pickString(alertRow.metadata, ['species'])
          ?? pickString(captureRow, ['species'])
          ?? pickString(zone, ['species'])
          ?? scope.species
          ?? undefined,
        biological_phase: pickString(caseRow, ['biological_phase', 'phase'])
          ?? pickString(caseRow.metadata, ['biological_phase', 'phase'])
          ?? pickString(alertRow.metadata, ['biological_phase', 'phase'])
          ?? pickString(captureRow, ['biological_phase', 'phase'])
          ?? pickString(zone, ['phase'])
          ?? scope.phase
          ?? undefined,
        detected_at: timestamp,
        model_confidence: pickNumber(caseRow, ['confidence'])
          ?? pickNumber(detection, ['confidence', 'model_confidence'])
          ?? pickNumber(alertRow, ['confidence', 'model_confidence']),
        alert_type: sensorType
          ? pickString(caseRow, ['title', 'suspected_pathology']) ?? pickString(alertRow, ['alert_type', 'type'])
          : pickString(caseRow, ['suspected_pathology', 'title'])
          ?? pickString(alertRow, ['alert_type', 'type'])
          ?? pickString(detection, ['label', 'class_name', 'detection_type'])
          ?? null,
        severity: pickString(caseRow, ['severity']) ?? pickString(alertRow, ['severity']),
        status: pickString(caseRow, ['status']) as any,
        validation_result: pickString(caseRow, ['validation_result']) as any,
        anomaly_type: pickString(caseRow, ['anomaly_type']) as any,
        validation_notes: pickString(caseRow, ['validation_notes']) ?? pickString(caseRow, ['notes']) ?? undefined,
        validated_at: pickString(caseRow, ['validated_at']) ?? undefined,
        validated_by: pickString(caseRow, ['validated_by']) ?? undefined,
        problemName: pickString(caseRow, ['suspected_pathology', 'title']) ?? undefined,
        problemDescription: pickString(caseRow, ['notes', 'validation_notes']) ?? undefined,
        aiAnalysis: mappedAiAnalysis ?? undefined,
        createdAt: pickString(caseRow, ['created_at']) ?? timestamp,
        updatedAt: pickString(caseRow, ['updated_at']) ?? undefined,
        metadata: {
          ...mergedMetadata,
          source: pickString(mergedMetadata, ['source']) ?? (sensorType ? 'Leitura ambiental' : undefined),
          sensor_type: sensorType ?? undefined,
          sensor_id: idFrom(sensor, ['sensor_id', 'id']) || pickString(mergedMetadata, ['sensor_id', 'sensorId']) || undefined,
          sensor_name: pickString(sensor, ['name', 'sensor_name']) ?? pickString(mergedMetadata, ['sensor_name', 'sensor_label']) ?? undefined,
          sensor_value: sensorValue ?? undefined,
          unit: pickString(mergedMetadata, ['unit']) ?? pickString(sensor, ['unit']) ?? undefined,
          site: pickString(location, ['name']),
          tank: pickString(zone, ['name', 'code']),
          kit_id: idFrom(kit, ['kit_id', 'id']) || undefined,
          capture_id: captureId ?? undefined,
          temperature: pickNumber(captureRow, ['temperature', 'temp']),
          ph: pickNumber(captureRow, ['ph', 'pH']),
          oxygen: pickNumber(captureRow, ['oxygen', 'dissolved_oxygen', 'o2']),
          detection_status: alertDetections.length || Object.keys(detection).length ? 'Peixe detetado' : 'A confirmar'
        }
      };
    });
    const caseAlertIds = new Set(
      caseRows
        .map((row) => idFrom(row, ['alert_id']))
        .filter(Boolean)
    );
    const caseDetectionIds = new Set(
      caseRows
        .map((row) => idFrom(row, ['detection_id']))
        .filter(Boolean)
    );
    const caseCaptureIds = new Set(
      caseRows
        .flatMap((row) => [
          idFrom(row, ['capture_id']),
          idFrom(row, ['source_capture_id'])
        ])
        .filter(Boolean)
    );
    const mappedStandaloneAlerts = alertRows
      .filter((alertRow) => {
        const alertId = idFrom(alertRow, ['alert_id', 'id']);
        return Boolean(alertId) && !caseAlertIds.has(alertId);
      })
      .sort(byLatestDate)
      .map((alertRow): RawVisionAlert => {
        const alertId = idFrom(alertRow, ['alert_id', 'id']);
        const alertDetections = detectionsByAlertId.get(alertId) ?? [];
        const detection = alertDetections.sort(byLatestDate)[0] ?? {};
        const captureId = pickString(alertRow, ['capture_id', 'vision_capture_id'])
          ?? pickString(detection, ['capture_id', 'vision_capture_id']);
        const captureRow = (captureId ? capturesById.get(captureId) : null) ?? {};
        const zone = zonesById.get(
          pickString(alertRow, ['zone_id', 'tank_id']) ?? pickString(captureRow, ['zone_id', 'tank_id']) ?? ''
        ) ?? activeZone ?? {};
        const location = locationsById.get(
          pickString(zone, ['location_id']) ?? pickString(alertRow, ['location_id', 'site_id']) ?? ''
        ) ?? {};
        const kit = kitRows.find((row) => String(row.zone_id ?? '') === String(zone.zone_id ?? zone.id ?? '')) ?? activeKit ?? {};
        const timestamp = readDateValue(captureRow) ?? readDateValue(alertRow) ?? new Date().toISOString();
        const mergedMetadata = {
          ...(alertRow.metadata ?? {}),
          site: pickString(location, ['name']) ?? pickString(alertRow.metadata, ['site', 'location']),
          tank: pickString(zone, ['name', 'code']) ?? pickString(alertRow.metadata, ['tank', 'location']),
          kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(alertRow, ['kit_id']) || undefined,
          capture_id: captureId ?? undefined,
          temperature: pickNumber(captureRow, ['temperature', 'temp']),
          ph: pickNumber(captureRow, ['ph', 'pH']),
          oxygen: pickNumber(captureRow, ['oxygen', 'dissolved_oxygen', 'o2']),
          detection_status: alertDetections.length || Object.keys(detection).length ? 'Peixe detetado' : 'A confirmar'
        };
        const sensorType = pickString(mergedMetadata, ['sensor_type', 'sensorType', 'metric', 'parameter'])
          ?? pickString(alertRow, ['sensor_type', 'type', 'alert_type']);
        const sensor = findSensorForAlert({ metadata: mergedMetadata, alertRow, kit, zone, sensorType });
        const sensorValue = pickNumber(mergedMetadata, ['sensor_value', 'value', 'reading_value', 'calibrated_value', 'raw_value', 'last_value'])
          ?? pickNumber(alertRow, ['value', 'reading_value', 'calibrated_value', 'raw_value', 'last_value']);
        const storedAiAnalysis = mergedMetadata.ai_analysis ?? alertRow.ai_analysis ?? null;
        const mappedAiAnalysis = storedAiAnalysis && typeof storedAiAnalysis === 'object'
          ? {
            confidence: typeof storedAiAnalysis.confidence === 'number' ? storedAiAnalysis.confidence : undefined,
            detectedIssues: Array.isArray(storedAiAnalysis.suspectedFindings)
              ? storedAiAnalysis.suspectedFindings.map(String)
              : Array.isArray(storedAiAnalysis.detectedIssues)
                ? storedAiAnalysis.detectedIssues.map(String)
                : undefined,
            recommendation: String(storedAiAnalysis.operationalAssessment ?? storedAiAnalysis.recommendedNextSteps ?? storedAiAnalysis.description ?? '').trim() || undefined
          }
          : null;

        return {
          id: alertId,
          alert_id: alertId,
          capture_id: captureId ?? undefined,
          detection_id: idFrom(detection, ['detection_id', 'id']) || undefined,
          kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(alertRow, ['kit_id']) || undefined,
          client_id: pickString(alertRow, ['client_id']) ?? undefined,
          project_id: pickString(alertRow, ['project_id']) ?? pickString(captureRow, ['project_id']) ?? scope.projectId ?? undefined,
          site_id: pickString(alertRow, ['location_id', 'site_id']) ?? undefined,
          tank_id: pickString(alertRow, ['zone_id', 'tank_id']) ?? undefined,
          imageUrl: pickString(alertRow, ['image_url', 'imageUrl'])
            ?? pickString(captureRow, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url'])
            ?? pickString(detection, ['image_url', 'imageUrl'])
            ?? '',
          species: pickString(alertRow, ['species'])
            ?? pickString(alertRow.metadata, ['species'])
            ?? pickString(captureRow, ['species'])
            ?? pickString(zone, ['species'])
            ?? scope.species
            ?? undefined,
          biological_phase: pickString(alertRow, ['phase', 'biological_phase'])
            ?? pickString(alertRow.metadata, ['phase', 'biological_phase'])
            ?? pickString(captureRow, ['phase', 'biological_phase'])
            ?? pickString(zone, ['phase'])
            ?? scope.phase
            ?? undefined,
          detected_at: timestamp,
          model_confidence: pickNumber(alertRow, ['confidence', 'model_confidence'])
            ?? pickNumber(detection, ['confidence', 'model_confidence']),
          alert_type: pickString(alertRow, ['alert_type', 'type', 'title'])
            ?? pickString(detection, ['label', 'class_name', 'detection_type'])
            ?? null,
          severity: pickString(alertRow, ['severity']),
          status: (pickString(alertRow, ['status']) as any) ?? 'pending',
          validation_result: pickString(alertRow, ['validation_result']) as any,
          anomaly_type: pickString(alertRow, ['anomaly_type']) as any,
          validation_notes: pickString(alertRow, ['notes', 'message']) ?? undefined,
          validated_at: pickString(alertRow, ['validated_at']) ?? undefined,
          validated_by: pickString(alertRow, ['validated_by']) ?? undefined,
          problemName: pickString(alertRow, ['title', 'alert_type', 'type']) ?? undefined,
          problemDescription: pickString(alertRow, ['message', 'notes']) ?? undefined,
          aiAnalysis: mappedAiAnalysis ?? undefined,
          createdAt: pickString(alertRow, ['created_at', 'triggered_at']) ?? timestamp,
          updatedAt: pickString(alertRow, ['updated_at']) ?? undefined,
          metadata: {
            ...mergedMetadata,
            sensor_id: idFrom(sensor, ['sensor_id', 'id']) || pickString(mergedMetadata, ['sensor_id', 'sensorId']) || undefined,
            sensor_name: pickString(sensor, ['name', 'sensor_name']) ?? pickString(mergedMetadata, ['sensor_name', 'sensor_label']) ?? undefined,
            sensor_type: pickString(sensor, ['sensor_type', 'type']) ?? sensorType ?? undefined,
            sensor_value: sensorValue ?? undefined,
            unit: pickString(mergedMetadata, ['unit']) ?? pickString(sensor, ['unit']) ?? undefined
          }
        };
      });
    const alertDetectionIds = new Set(
      [...mappedAqlCases, ...mappedStandaloneAlerts]
        .map((alert) => alert.detection_id)
        .filter(Boolean)
    );
    const alertCaptureIds = new Set(
      [...mappedAqlCases, ...mappedStandaloneAlerts]
        .map((alert) => alert.capture_id)
        .filter(Boolean)
    );
    const mappedStandaloneDetections = detectionRows
      .filter((detection) => {
        const detectionId = idFrom(detection, ['detection_id', 'id']);
        const alertId = idFrom(detection, ['alert_id']);
        return Boolean(detectionId) && !caseDetectionIds.has(detectionId) && !alertDetectionIds.has(detectionId) && (!alertId || !caseAlertIds.has(alertId));
      })
      .sort(byLatestDate)
      .map((detection): RawVisionAlert => {
        const detectionId = idFrom(detection, ['detection_id', 'id']);
        const captureId = pickString(detection, ['capture_id', 'vision_capture_id']);
        const captureRow = (captureId ? capturesById.get(captureId) : null) ?? {};
        const zone = zonesById.get(pickString(captureRow, ['zone_id', 'tank_id']) ?? pickString(detection, ['zone_id', 'tank_id']) ?? '')
          ?? activeZone
          ?? {};
        const location = locationsById.get(pickString(zone, ['location_id']) ?? pickString(captureRow, ['location_id', 'site_id']) ?? '')
          ?? {};
        const kit = kitRows.find((row) => String(row.zone_id ?? '') === String(zone.zone_id ?? zone.id ?? '')) ?? activeKit ?? {};
        const timestamp = readDateValue(detection) ?? readDateValue(captureRow) ?? new Date().toISOString();
        const detectionLabel = pickString(detection, ['label', 'class_name', 'detection_type', 'title'])
          ?? pickString(detection.metadata, ['label', 'class_name', 'detection_type'])
          ?? 'Deteção visual';
        const confidence = pickNumber(detection, ['confidence', 'model_confidence'])
          ?? pickNumber(detection.metadata, ['confidence', 'model_confidence']);

        return {
          id: `visual-detection-${detectionId}`,
          alert_id: `visual-detection-${detectionId}`,
          capture_id: captureId ?? undefined,
          detection_id: detectionId,
          kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(captureRow, ['kit_id']) || undefined,
          client_id: pickString(captureRow, ['client_id']) ?? scope.clientId ?? undefined,
          project_id: pickString(captureRow, ['project_id']) ?? scope.projectId ?? undefined,
          site_id: pickString(captureRow, ['location_id', 'site_id']) ?? undefined,
          tank_id: pickString(captureRow, ['zone_id', 'tank_id']) ?? undefined,
          imageUrl: pickString(detection, ['image_url', 'imageUrl'])
            ?? pickString(captureRow, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url'])
            ?? '',
          species: pickString(detection, ['species'])
            ?? pickString(detection.metadata, ['species'])
            ?? pickString(captureRow, ['species'])
            ?? pickString(zone, ['species'])
            ?? scope.species
            ?? undefined,
          biological_phase: pickString(detection, ['phase', 'biological_phase'])
            ?? pickString(detection.metadata, ['phase', 'biological_phase'])
            ?? pickString(captureRow, ['phase', 'biological_phase'])
            ?? pickString(zone, ['phase'])
            ?? scope.phase
            ?? undefined,
          detected_at: timestamp,
          model_confidence: confidence,
          alert_type: detectionLabel,
          severity: pickString(detection, ['severity']) ?? pickString(detection.metadata, ['severity']) ?? 'medium',
          status: (pickString(detection, ['status'])
            ?? pickString(detection.metadata, ['status', 'validation_status'])
            ?? 'pending') as any,
          validation_result: pickString(detection, ['validation_result'])
            ?? pickString(detection.metadata, ['validation_result']) as any,
          anomaly_type: pickString(detection, ['anomaly_type'])
            ?? pickString(detection.metadata, ['anomaly_type']) as any,
          validation_notes: pickString(detection, ['validation_notes', 'notes'])
            ?? pickString(detection.metadata, ['validation_notes', 'notes'])
            ?? undefined,
          validated_at: pickString(detection, ['validated_at'])
            ?? pickString(detection.metadata, ['validated_at'])
            ?? undefined,
          problemName: detectionLabel,
          problemDescription: pickString(detection, ['notes', 'description'])
            ?? pickString(detection.metadata, ['notes', 'description'])
            ?? 'Deteção visual pendente de validação humana.',
          createdAt: pickString(detection, ['created_at']) ?? timestamp,
          updatedAt: pickString(detection, ['updated_at']) ?? undefined,
          metadata: {
            ...(captureRow.metadata ?? {}),
            ...(detection.metadata ?? {}),
            source: 'Visão computacional',
            site: pickString(location, ['name']) ?? scope.site ?? undefined,
            tank: pickString(zone, ['name', 'code']) ?? scope.tank ?? undefined,
            kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(captureRow, ['kit_id']) || undefined,
            capture_id: captureId ?? undefined,
            detection_id: detectionId,
            detection_status: confidence !== null ? `${detectionLabel} · ${Math.round(confidence <= 1 ? confidence * 100 : confidence)}%` : detectionLabel,
            yolo_bbox: detection.bbox ?? {
              x: pickNumber(detection, ['bbox_x']),
              y: pickNumber(detection, ['bbox_y']),
              width: pickNumber(detection, ['bbox_width']),
              height: pickNumber(detection, ['bbox_height'])
            }
          }
        };
      });
    const detectionCaptureIds = new Set(
      detectionRows
        .map((row) => idFrom(row, ['capture_id', 'vision_capture_id']))
        .filter(Boolean)
    );
    const mappedStandaloneCaptures = captureRows
      .filter((captureRow) => {
        const captureId = idFrom(captureRow, ['capture_id', 'vision_capture_id', 'id']);
        const imageUrl = pickString(captureRow, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url']);
        return Boolean(captureId && imageUrl) &&
          !caseCaptureIds.has(captureId) &&
          !alertCaptureIds.has(captureId) &&
          !detectionCaptureIds.has(captureId);
      })
      .sort(byLatestDate)
      .map((captureRow): RawVisionAlert => {
        const captureId = idFrom(captureRow, ['capture_id', 'vision_capture_id', 'id']);
        const zone = zonesById.get(pickString(captureRow, ['zone_id', 'tank_id']) ?? '')
          ?? activeZone
          ?? {};
        const location = locationsById.get(pickString(captureRow, ['location_id', 'site_id']) ?? pickString(zone, ['location_id']) ?? '')
          ?? {};
        const kit = kitRows.find((row) =>
          String(row.zone_id ?? '') === String(zone.zone_id ?? zone.id ?? '') ||
          String(row.kit_id ?? row.id ?? '') === String(pickString(captureRow, ['kit_id']) ?? '')
        ) ?? activeKit ?? {};
        const timestamp = readDateValue(captureRow) ?? new Date().toISOString();

        return {
          id: `visual-capture-${captureId}`,
          alert_id: `visual-capture-${captureId}`,
          capture_id: captureId,
          kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(captureRow, ['kit_id']) || undefined,
          client_id: pickString(captureRow, ['client_id']) ?? scope.clientId ?? undefined,
          project_id: pickString(captureRow, ['project_id']) ?? scope.projectId ?? undefined,
          site_id: pickString(captureRow, ['location_id', 'site_id']) ?? undefined,
          tank_id: pickString(captureRow, ['zone_id', 'tank_id']) ?? undefined,
          imageUrl: pickString(captureRow, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url']) ?? '',
          species: pickString(captureRow, ['species'])
            ?? pickString(captureRow.metadata, ['species'])
            ?? pickString(zone, ['species'])
            ?? scope.species
            ?? undefined,
          biological_phase: pickString(captureRow, ['phase', 'biological_phase'])
            ?? pickString(captureRow.metadata, ['phase', 'biological_phase'])
            ?? pickString(zone, ['phase'])
            ?? scope.phase
            ?? undefined,
          detected_at: timestamp,
          model_confidence: null,
          alert_type: 'Imagem capturada',
          severity: 'low',
          status: (pickString(captureRow, ['status'])
            ?? pickString(captureRow.metadata, ['status', 'validation_status'])
            ?? 'pending') as any,
          validation_result: pickString(captureRow, ['validation_result'])
            ?? pickString(captureRow.metadata, ['validation_result']) as any,
          anomaly_type: pickString(captureRow, ['anomaly_type'])
            ?? pickString(captureRow.metadata, ['anomaly_type']) as any,
          validation_notes: pickString(captureRow, ['validation_notes', 'notes'])
            ?? pickString(captureRow.metadata, ['validation_notes', 'notes'])
            ?? undefined,
          validated_at: pickString(captureRow, ['validated_at'])
            ?? pickString(captureRow.metadata, ['validated_at'])
            ?? undefined,
          problemName: 'Imagem capturada',
          problemDescription: 'Captura visual disponível para revisão operacional.',
          createdAt: pickString(captureRow, ['created_at']) ?? timestamp,
          updatedAt: pickString(captureRow, ['updated_at']) ?? undefined,
          metadata: {
            ...(captureRow.metadata ?? {}),
            source: 'Visão computacional',
            site: pickString(location, ['name']) ?? scope.site ?? undefined,
            tank: pickString(zone, ['name', 'code']) ?? scope.tank ?? undefined,
            kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(captureRow, ['kit_id']) || undefined,
            capture_id: captureId,
            detection_status: pickString(captureRow, ['detection_status', 'status']) ?? 'Imagem capturada'
          }
        };
      });
    const legacyAlerts = allLegacyAlertRows
      .map(normalizeLegacyVisionAlert)
      .filter((item): item is RawVisionAlert => {
        if (!item) return false;
        const itemClientId = item.client_id ?? item.metadata?.client_id ?? item.metadata?.clientId;
        const itemProjectId = item.project_id ?? item.metadata?.project_id ?? item.metadata?.projectId;
        if (scope.clientId && itemClientId !== scope.clientId) return false;
        if (scope.projectId && itemProjectId !== scope.projectId) return false;
        if (scope.site && normalizedLabel(item.metadata?.site ?? item.metadata?.location) !== normalizedLabel(scope.site)) return false;
        if (scope.tank && normalizedLabel(item.metadata?.tank ?? item.metadata?.location) !== normalizedLabel(scope.tank)) return false;
        if (scope.species && normalizedLabel(item.species ?? item.metadata?.species) !== normalizedLabel(scope.species)) return false;
        if (scope.phase && normalizePhaseOption(item.biological_phase ?? item.metadata?.phase) !== scope.phase) return false;
        return true;
      });

    const latestReadingByOperationalMetric = new Map<string, any>();
    sensorReadingRows
      .sort(byLatestDate)
      .forEach((row) => {
        const metadata = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
        const sensor = sensorsById.get(idFrom(row, ['sensor_id', 'id']));
        const metric = sensorMetricFromType(
          pickString(row, ['sensor_type', 'type', 'metric', 'parameter']) ??
          pickString(metadata, ['sensor_type', 'sensorType', 'type', 'metric', 'parameter']) ??
          pickString(sensor, ['sensor_type', 'type', 'name'])
        );
        if (!metric) return;
        const value = normalizeOperationalSensorValue(metric, readingNumber(row, ['value', 'reading_value', 'last_value', 'reading']));
        if (value === null) return;
        const sensorKey = idFrom(row, ['sensor_id', 'id']) || pickString(row, ['device_id']) || pickString(row, ['zone_id', 'tank_id']) || 'global';
        const key = `${metric}:${sensorKey}`;
        if (metadata.alert_hidden === true || metadata.hidden === true) {
          if (!latestReadingByOperationalMetric.has(key)) latestReadingByOperationalMetric.set(key, null);
          return;
        }
        if (!latestReadingByOperationalMetric.has(key)) {
          latestReadingByOperationalMetric.set(key, row);
        }
      });

    const mappedSensorReadingAlerts = Array.from(latestReadingByOperationalMetric.values())
      .filter(Boolean)
      .map((row): RawVisionAlert | null => {
        const metadata = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
        const sensor = sensorsById.get(idFrom(row, ['sensor_id', 'id']));
        const metric = sensorMetricFromType(
          pickString(row, ['sensor_type', 'type', 'metric', 'parameter']) ??
          pickString(metadata, ['sensor_type', 'sensorType', 'type', 'metric', 'parameter']) ??
          pickString(sensor, ['sensor_type', 'type', 'name'])
        );
        if (!metric) return null;
        const value = normalizeOperationalSensorValue(metric, readingNumber(row, ['value', 'reading_value', 'last_value', 'reading']));
        if (value === null) return null;
        const classification = classifySensorReadingAlert(metric, value);
        if (!classification) return null;

        const zoneId = pickString(row, ['zone_id', 'tank_id']) ?? pickString(sensor, ['zone_id', 'tank_id']);
        const zone = zonesById.get(zoneId ?? '') ?? activeZone ?? {};
        const locationId = pickString(row, ['location_id', 'site_id']) ?? pickString(zone, ['location_id']) ?? pickString(sensor, ['location_id', 'site_id']);
        const location = locationsById.get(locationId ?? '') ?? {};
        const kit = kitRows.find((item) =>
          String(item.zone_id ?? '') === String(zone?.zone_id ?? zone?.id ?? '') ||
          String(item.kit_id ?? item.id ?? '') === String(pickString(row, ['kit_id']) ?? pickString(sensor, ['kit_id']) ?? '')
        ) ?? activeKit ?? {};
        const readingId = idFrom(row, ['reading_id', 'id']) ||
          `${metric}-${pickString(row, ['sensor_id']) ?? pickString(row, ['device_id']) ?? 'reading'}-${readDateValue(row) ?? value}`;
        const timestamp = readDateValue(row) ?? new Date().toISOString();
        const config = SENSOR_LIMITS[metric];
        const unit = pickString(row, ['unit']) ?? pickString(metadata, ['unit']) ?? pickString(sensor, ['unit']) ?? config.unit;
        const target = config.max === null
          ? `>= ${config.min} ${config.unit}`
          : `${config.min}–${config.max} ${config.unit}`;
        const persistedValidationResult = pickString(metadata, ['validation_result', 'validationResult']);
        const persistedStatus = pickString(metadata, ['validation_status', 'status']);

        return {
          id: `sensor-reading-alert-${readingId}`,
          client_id: pickString(row, ['client_id']) ?? scope.clientId ?? undefined,
          project_id: pickString(row, ['project_id']) ?? scope.projectId ?? undefined,
          site_id: locationId ?? undefined,
          tank_id: zoneId ?? undefined,
          kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(row, ['kit_id']) || pickString(sensor, ['kit_id']) || undefined,
          imageUrl: '',
          species: pickString(row, ['species']) ?? pickString(metadata, ['species']) ?? pickString(zone, ['species']) ?? scope.species ?? undefined,
          biological_phase: pickString(row, ['phase', 'biological_phase']) ?? pickString(metadata, ['phase', 'biological_phase']) ?? pickString(zone, ['phase']) ?? scope.phase ?? undefined,
          detected_at: timestamp,
          model_confidence: null,
          alert_type: classification.title,
          severity: classification.severity,
          status: (persistedStatus ?? persistedValidationResult ?? 'pending') as RawVisionAlert['status'],
          validation_result: persistedValidationResult as RawVisionAlert['validation_result'],
          validation_notes: pickString(metadata, ['validation_notes', 'validationNotes']) ?? undefined,
          validated_at: pickString(metadata, ['validated_at', 'validatedAt']) ?? undefined,
          validated_by: pickString(metadata, ['validated_by', 'validatedBy']) ?? undefined,
          validated_by_email: pickString(metadata, ['validated_by_email', 'validatedByEmail']) ?? undefined,
          validated_by_name: pickString(metadata, ['validated_by_name', 'validatedByName']) ?? undefined,
          problemName: classification.title,
          problemDescription: `${config.label}: ${value.toFixed(metric === 'ph' ? 2 : 1)} ${unit}. Alvo biológico: ${target}.`,
          createdAt: timestamp,
          metadata: {
            ...metadata,
            source: 'Leitura ambiental',
            virtual_alert: true,
            virtual_alert_kind: 'sensor_reading',
            sensor_type: metric,
            sensor_id: idFrom(sensor, ['sensor_id', 'id']) || pickString(row, ['sensor_id']) || undefined,
            sensor_name: pickString(sensor, ['name', 'sensor_name']) ?? pickString(metadata, ['sensor_name', 'sensor_label']) ?? undefined,
            sensor_value: value,
            unit,
            target,
            threshold_min: config.min,
            threshold_max: config.max,
            direction: classification.direction,
            site: pickString(location, ['name']) ?? scope.site ?? undefined,
            tank: pickString(zone, ['name', 'code']) ?? scope.tank ?? undefined,
            kit_id: idFrom(kit, ['kit_id', 'id']) || pickString(row, ['kit_id']) || pickString(sensor, ['kit_id']) || undefined,
            reading_id: readingId,
            detection_status: 'Leitura ambiental'
          }
        };
      })
      .filter((item): item is RawVisionAlert => Boolean(item));

    const mappedCases = [...mappedAqlCases, ...mappedStandaloneAlerts, ...mappedStandaloneDetections, ...mappedStandaloneCaptures, ...legacyAlerts, ...mappedSensorReadingAlerts].sort(byLatestDate);

    const latestCapture = [...captureRows].sort(byLatestDate)[0] ?? null;
    const latestMappedAlert = mappedCases[0] ?? null;
    const latestTemp = latestReadingFor(sensorReadingRows, sensorRows, ['temperature', 'temperatura', 'temp']);
    const latestPh = latestReadingFor(sensorReadingRows, sensorRows, ['ph']);
    const latestOxygen = latestReadingFor(sensorReadingRows, sensorRows, ['oxygen', 'oxigen', 'oxigén', 'o2', 'dissolved']);
    const latestRedox = latestReadingFor(sensorReadingRows, sensorRows, ['redox', 'orp']);
    const latestSalinity = latestReadingFor(sensorReadingRows, sensorRows, ['salinity', 'salinidade', 'salinidad', 'salt', 'psu', 'ppt']);
    const latestSensorFallback = (names: string[]) =>
      sensorRows
        .filter((sensor) => {
          const sensorType = `${pickString(sensor, ['sensor_type', 'type', 'name']) ?? ''}`.toLowerCase();
          return names.some((name) => sensorType.includes(name));
        })
        .sort((a, b) => new Date(readDateValue(b) ?? 0).getTime() - new Date(readDateValue(a) ?? 0).getTime())[0] ?? null;
    const latestTempSensor = latestSensorFallback(['temperature', 'temperatura', 'temp']);
    const latestPhSensor = latestSensorFallback(['ph']);
    const latestOxygenSensor = latestSensorFallback(['oxygen', 'oxigen', 'oxigén', 'o2', 'dissolved']);
    const latestRedoxSensor = latestSensorFallback(['redox', 'orp']);
    const latestSalinitySensor = latestSensorFallback(['salinity', 'salinidade', 'salinidad', 'salt', 'psu', 'ppt']);
    const sensorReadingAuditRows = sensorReadingRows.map((row) => {
      const linkedSensor = sensorsById.get(idFrom(row, ['sensor_id', 'id']));
      const zoneId = pickString(row, ['zone_id', 'tank_id']);
      const locationId = pickString(row, ['location_id', 'site_id']);
      const linkedZone = zoneId ? zonesById.get(zoneId) : activeZone;
      const linkedLocation = locationId ? locationsById.get(locationId) : null;
      const metric = sensorMetricFromType(
        pickString(row, ['sensor_type', 'type', 'metric', 'parameter']) ??
        pickString(row.metadata, ['sensor_type', 'sensorType', 'type', 'metric', 'parameter']) ??
        pickString(linkedSensor, ['sensor_type', 'type', 'name'])
      );
      const value = normalizeOperationalSensorValue(metric, readingNumber(row, ['value', 'reading_value', 'last_value', 'reading']));
      return {
        id: idFrom(row, ['reading_id', 'id']) || null,
        clientId: pickString(row, ['client_id']) ?? scope.clientId ?? null,
        deviceId: pickString(row, ['device_id']) ?? null,
        sensorId: pickString(row, ['sensor_id']) ?? null,
        sensorType: pickString(row, ['sensor_type', 'type', 'metric', 'parameter']) ??
          pickString(row.metadata, ['sensor_type', 'sensorType', 'type', 'metric', 'parameter']) ??
          pickString(linkedSensor, ['sensor_type', 'type', 'name']) ??
          null,
        value,
        unit: pickString(row, ['unit']) ?? pickString(row.metadata, ['unit']) ?? pickString(linkedSensor, ['unit']) ?? null,
        timestamp: readDateValue(row),
        site: pickString(linkedLocation, ['name']) ?? scope.site ?? null,
        tank: pickString(linkedZone, ['name', 'code']) ?? scope.tank ?? null,
        source: row.metadata?.is_demo === true ? 'Demonstração' : 'Leitura ambiental',
        status: pickString(row, ['status']) ?? 'reading'
      };
    });
    const sensorReadingIds = new Set(
      sensorReadingAuditRows
        .map((row) => String(row.sensorId ?? '').trim())
        .filter(Boolean)
    );
    const sensorLastValueAuditRows = sensorRows
      .filter((sensor) => {
        const sensorId = idFrom(sensor, ['sensor_id', 'id']);
        return Boolean(sensorId) && !sensorReadingIds.has(sensorId);
      })
      .map((sensor) => {
        const metric = sensorMetricFromType(pickString(sensor, ['sensor_type', 'type', 'name']));
        const value = normalizeOperationalSensorValue(metric, pickNumber(sensor, ['last_value', 'value', 'reading_value']));
        const timestamp = readDateValue(sensor);
        if (!metric || value === null || !timestamp) return null;
        const zoneId = pickString(sensor, ['zone_id', 'tank_id']);
        const locationId = pickString(sensor, ['location_id', 'site_id']);
        const linkedZone = zoneId ? zonesById.get(zoneId) : activeZone;
        const linkedLocation = locationId ? locationsById.get(locationId) : null;
        return {
          id: idFrom(sensor, ['sensor_id', 'id']) || null,
          clientId: pickString(sensor, ['client_id']) ?? scope.clientId ?? null,
          deviceId: pickString(sensor, ['device_id']) ?? null,
          sensorId: idFrom(sensor, ['sensor_id', 'id']) || null,
          sensorType: pickString(sensor, ['sensor_type', 'type', 'name']) ?? null,
          value,
          unit: pickString(sensor, ['unit']) ?? null,
          timestamp,
          site: pickString(linkedLocation, ['name']) ?? scope.site ?? null,
          tank: pickString(linkedZone, ['name', 'code']) ?? scope.tank ?? null,
          source: 'Última leitura do sensor',
          status: pickString(sensor, ['status']) ?? 'last_value'
        };
      })
      .filter((row): row is NonNullable<typeof row> => Boolean(row));
    const effectiveSensorAuditRows = [...sensorReadingAuditRows, ...sensorLastValueAuditRows].sort((a, b) =>
      new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime()
    );
    const latestSensorReadingAt = effectiveSensorAuditRows[0]?.timestamp ?? readDateValue(sensorReadingRows[0]);
    const pendingValidation = mappedCases.filter((row) => !isFinalValidationStatus(row.status ?? row.validation_result ?? row.validationResult)).length;
    const activeAlerts = Math.max(alertRows.filter(isActiveAlertRow).length, pendingValidation);
    const latestCamera = cameraRows.find((row) => activeKitId && String(row.kit_id ?? '') === activeKitId) ?? cameraRows[0] ?? null;
    const latestDevice = deviceRows.find((row) => activeKit && String(row.kit_id ?? '') === activeKitId) ?? deviceRows[0] ?? null;
    const activeKitName = pickString(activeKit, ['name', 'serial_number']) ?? activeKitId;
    const lastHeartbeat = pickString(activeKit, ['heartbeat', 'last_heartbeat_at', 'last_seen_at']) ?? pickString(latestDevice, ['last_seen_at', 'last_communication']);
    const heartbeatIntervalSeconds = heartbeatIntervalSecondsFor(activeKit, latestDevice);
    const isHeartbeatOnline = heartbeatOnline(lastHeartbeat, heartbeatIntervalSeconds);
    const latestCaptureId = idFrom(latestCapture, ['capture_id', 'vision_capture_id', 'id']);
    const latestCaptureZone = latestCapture
      ? zonesById.get(pickString(latestCapture, ['zone_id', 'tank_id']) ?? '') ?? activeZone
      : activeZone;
    const hasCaptureDetections = latestCaptureId
      ? detectionRows.some((row) => idFrom(row, ['capture_id', 'vision_capture_id']) === latestCaptureId)
      : false;
    const hasOperationalData = Boolean(
      activeKit ||
      latestCapture ||
      latestMappedAlert ||
      alertRows.length ||
      sensorReadingRows.length ||
      sensorRows.length ||
      cameraRows.length ||
      deviceRows.length
    );
    const latestTempValue = normalizeOperationalSensorValue('temperature', readingNumber(latestTemp, ['temperature', 'temp', 'water_temperature', 'water_temperature_c', 'temperature_c', 'temp_c', 'value', 'reading_value', 'last_value', 'reading']) ?? pickNumber(latestTempSensor, ['last_value']));
    const latestPhValue = normalizeOperationalSensorValue('ph', readingNumber(latestPh, ['ph', 'pH', 'value', 'reading_value', 'last_value', 'reading']) ?? pickNumber(latestPhSensor, ['last_value']));
    const latestOxygenValue = normalizeOperationalSensorValue('dissolved_oxygen', readingNumber(latestOxygen, ['oxygen', 'dissolved_oxygen', 'do_mg_l', 'oxygen_mg_l', 'o2', 'value', 'reading_value', 'last_value', 'reading']) ?? pickNumber(latestOxygenSensor, ['last_value']));
    const latestRedoxValue = normalizeOperationalSensorValue('redox', readingNumber(latestRedox, ['redox', 'orp', 'redox_mv', 'orp_mv', 'value', 'reading_value', 'last_value', 'reading']) ?? pickNumber(latestRedoxSensor, ['last_value']));
    const latestSalinityValue = normalizeOperationalSensorValue('salinity', readingNumber(latestSalinity, ['salinity', 'salinidade', 'salinidad', 'salt', 'psu', 'ppt', 'value', 'reading_value', 'last_value', 'reading']) ?? pickNumber(latestSalinitySensor, ['last_value']));
    const hasValidSensorValue = [latestTempValue, latestPhValue, latestOxygenValue, latestRedoxValue, latestSalinityValue].some((value) => value !== null);
    const configuredTargetFor = (reading:any,fallbackSensor:any) => {
      const sensor=sensorsById.get(pickString(reading,['sensor_id']) || '') || fallbackSensor;
      if(!sensor)return undefined;
      const min=pickNumber(sensor,['recommended_min','min_threshold']);
      const max=pickNumber(sensor,['recommended_max','max_threshold']);
      return min===null&&max===null?undefined:{min,max};
    };
    const fallbackSummary: MvpDashboardSummary | null = hasOperationalData ? {
      kitId: activeKitId || null,
      kitName: activeKitName || null,
      zoneId: idFrom(activeZone, ['zone_id', 'id']) || null,
      status: pendingValidation > 0 ? 'Atenção' : activeAlerts > 0 ? 'Alerta' : 'OK',
      reason: pendingValidation > 0
        ? `${pendingValidation} ${pendingValidation === 1 ? 'caso pendente' : 'casos pendentes'} de validação`
        : latestCapture
          ? 'Resumo calculado a partir das tabelas operacionais'
          : latestMappedAlert
            ? 'Resumo temporário a partir da kv_store'
            : 'Sem captura recente',
      updatedAt: latestSensorReadingAt ?? readDateValue(latestCapture) ?? readDateValue(latestMappedAlert) ?? null,
      latestCapture: latestCapture ? {
        imageUrl: pickString(latestCapture, ['image_url', 'imageUrl', 'image_path', 'storage_path', 'url']),
        capturedAt: readDateValue(latestCapture),
        tank: pickString(latestCaptureZone, ['name', 'code']),
        phase: pickString(latestCapture, ['phase', 'biological_phase']) ?? pickString(latestCaptureZone, ['phase']),
        species: pickString(latestCapture, ['species']) ?? pickString(latestCaptureZone, ['species']) ?? scope.species,
        detectionStatus: hasCaptureDetections ? 'Peixe detetado' : 'A confirmar'
      } : latestMappedAlert ? {
        imageUrl: latestMappedAlert.imageUrl ?? latestMappedAlert.image_url ?? null,
        capturedAt: readDateValue(latestMappedAlert),
        tank: pickString(latestMappedAlert, ['tank_id']) ?? pickString(latestMappedAlert.metadata, ['tank', 'location']),
        phase: latestMappedAlert.biological_phase ?? pickString(latestMappedAlert.metadata, ['phase']),
        species: latestMappedAlert.species ?? pickString(latestMappedAlert.metadata, ['species']),
        detectionStatus: pickString(latestMappedAlert.metadata, ['detection_status']) ?? 'A confirmar'
      } : null,
      alertsActive: activeAlerts,
      pendingValidation,
      sensors: {
        temperature: latestTempValue,
        ph: latestPhValue,
        oxygen: latestOxygenValue,
        redox: latestRedoxValue,
        salinity: latestSalinityValue,
        status: hasValidSensorValue ? 'OK' : 'Sensores sem feed'
      },
      sensorTargets: {
        temperature:configuredTargetFor(latestTemp,latestTempSensor),
        ph:configuredTargetFor(latestPh,latestPhSensor),
        oxygen:configuredTargetFor(latestOxygen,latestOxygenSensor),
        redox:configuredTargetFor(latestRedox,latestRedoxSensor),
        salinity:configuredTargetFor(latestSalinity,latestSalinitySensor),
      },
      sensorReadings: effectiveSensorAuditRows,
      device: {
        online: isHeartbeatOnline ?? false,
        lastCommunication: lastHeartbeat,
        cameraOk: latestCamera ? Boolean(latestCamera.is_active ?? true) : null,
        sensorsOk: sensorRows.length ? sensorRows.some((sensor) => Boolean(sensor.is_active ?? true)) : null
      }
    } : null;

    const statusKvRows = aqlKvRows.filter((row) => kitIds.has(kvKitId(row)));
    const exactKvRow = activeKitId
      ? statusKvRows.find((row) => String(row.key ?? '') === `aql:kit:${activeKitId}:status`)
      : null;
    const latestKvRow = [...statusKvRows].sort((a, b) => {
      const aValue = parseKvValue(a.value);
      const bValue = parseKvValue(b.value);
      return new Date(readDateValue(bValue) ?? 0).getTime() - new Date(readDateValue(aValue) ?? 0).getTime();
    })[0] ?? null;
    const kvSummary = normalizeKvSummary((exactKvRow ?? latestKvRow)?.value);
    const mergedSummary = kvSummary ? {
      ...(fallbackSummary ?? {}),
      ...kvSummary,
      kitId: kvSummary.kitId ?? activeKitId,
      kitName: kvSummary.kitName ?? activeKitName,
      alertsActive: fallbackSummary?.alertsActive ?? kvSummary.alertsActive ?? null,
      pendingValidation: fallbackSummary?.pendingValidation ?? kvSummary.pendingValidation ?? null,
      sensorReadings: fallbackSummary?.sensorReadings ?? kvSummary.sensorReadings ?? null,
      sensors: {
        ...(kvSummary.sensors ?? {}),
        ...(fallbackSummary?.sensors ?? {}),
        status: fallbackSummary?.sensors?.status ?? kvSummary.sensors?.status ?? null
      }
    } : fallbackSummary;

    if (mergedSummary && fallbackSummary && activeAlerts === 0 && pendingValidation === 0 && kvSummary?.validation_result) {
      mergedSummary.status = fallbackSummary.status;
      mergedSummary.reason = fallbackSummary.reason;
    }

    if (mergedSummary?.device) {
      const mergedLastHeartbeat = mergedSummary.device.lastCommunication ?? lastHeartbeat;
      const mergedHeartbeatOnline = heartbeatOnline(mergedLastHeartbeat, heartbeatIntervalSeconds);
      mergedSummary.device = {
        ...mergedSummary.device,
        online: mergedHeartbeatOnline ?? false,
        lastCommunication: mergedLastHeartbeat
      };
      if (mergedHeartbeatOnline === false && mergedSummary.status === 'OK') {
        mergedSummary.status = 'Alerta';
        mergedSummary.reason = 'Unidade sem heartbeat recente';
      }
    }

    return {
      alerts: mappedCases,
      summary: mergedSummary
    };
  }

  export function VisionAlerts({
    apiUrl,
    publicAnonKey,
    onBack,
    demoMode = false,
    demoInitialView = 'operations',
    demoSelectedAlertId = null
  }: VisionAlertsProps) {
    const navigate = useNavigate();
    const language = useLanguage();
    const { clientSlug, projectSlug } = useParams<{ clientSlug?: string; projectSlug?: string }>();
    const {
      client,
      branding,
      availableClients,
      setClient,
      setAvailableClients,
      species,
      phase,
      site,
      tank,
      setSpecies,
      setPhase,
      setSite,
      setTank
    } = useGlobal();

    const [authToken, setAuthToken] = useState<string | null>(() => demoMode ? DEMO_AUTH_TOKEN : null);
    const [userEmail, setUserEmail] = useState<string | null>(() => demoMode ? DEMO_USER_EMAIL : null);
    const [authRecoveryChecked, setAuthRecoveryChecked] = useState(() => demoMode);

    const [alerts, setAlerts] = useState<RawVisionAlert[]>([]);
    const [dashboardSummary, setDashboardSummary] = useState<MvpDashboardSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentView, setCurrentView] = useState<VisionView>(demoInitialView);
    const [assistantOpen, setAssistantOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.localStorage.getItem('aql_vision_sidebar_collapsed') === 'true');
    const [scopeLocked, setScopeLocked] = useState(false);
    const [menuOrder, setMenuOrder] = useState<MainView[]>(() => readMenuOrderPreference());
    const [hiddenMenuItems, setHiddenMenuItems] = useState<MainView[]>(() => readHiddenMenuPreference());
    const [isFilterSidebarExpanded, setIsFilterSidebarExpanded] = useState(true);
    const [selectedClinicalCaseId, setSelectedClinicalCaseId] = useState<string | null>(null);
    const [bulkSelectedCaseIds, setBulkSelectedCaseIds] = useState<Set<string>>(() => new Set());
    const [bulkValidationOpen, setBulkValidationOpen] = useState(false);
    const [alertTypeFilter, setAlertTypeFilter] = useState<AlertTypeFilter>('all');
    const [statusFilter, setStatusFilter] = useState<'all' | ClinicalStatus>('all');
    const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
    const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
    const [alertSortMode, setAlertSortMode] = useState<AlertSortMode>('newest');
    const [alertViewMode, setAlertViewMode] = useState<AlertViewMode>(() => {
      const saved = window.localStorage.getItem('aql_vision_alert_view_mode');
      return saved === 'grid' ? 'grid' : 'list';
    });
    const [textFilter, setTextFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [dateFilterPreset, setDateFilterPreset] = useState<DateFilterPreset>('all');
    const [currentCalendarMonth, setCurrentCalendarMonth] = useState<Date>(() => new Date());
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
    const [showCookieSettings, setShowCookieSettings] = useState(false);
    const [cookiePreferences, setCookiePreferences] = useState<CookiePreferencesState | null>(() => readCookiePreferences());
    const [selectedAlertFromEmail, setSelectedAlertFromEmail] = useState<string | null>(demoSelectedAlertId);
    const [databaseScopeOptions, setDatabaseScopeOptions] = useState<DatabaseScopeOptions>(EMPTY_DATABASE_SCOPE_OPTIONS);
    const [routeProjectId, setRouteProjectId] = useState<string | null>(null);
    const [routeScopeLoading, setRouteScopeLoading] = useState(false);
    const [routeClientName, setRouteClientName] = useState<string | null>(null);
    const [clientProjectChoices, setClientProjectChoices] = useState<ClientProjectChoice[]>([]);
    const [clientProjectChoicesLoading, setClientProjectChoicesLoading] = useState(false);
    const [clientProfileRole, setClientProfileRole] = useState<string | null>(null);
    const [clientProfileName, setClientProfileName] = useState<string | null>(null);
    const [clientAdminModeOpen, setClientAdminModeOpen] = useState(false);
    const [activeOperationalAlertToast, setActiveOperationalAlertToast] = useState<ActiveOperationalAlertToast | null>(null);
    const [activeOperationalAlertClosing, setActiveOperationalAlertClosing] = useState(false);
    const [isProcessingDeepLink, setIsProcessingDeepLink] = useState(() => {
      if (demoMode) return false;
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('viewerToken') && urlParams.has('alertId');
    });

    const latestAlertIdRef = useRef<string | null>(null);
    const latestAlertToastSignatureRef = useRef<string | null>(null);
    const knownOperationalAlertIdsRef = useRef<Set<string>>(new Set());
    const activeOperationalAlertCloseTimeoutRef = useRef<number | null>(null);
    const isFirstLoadRef = useRef(true);
    const lastMainViewRef = useRef<MainView>('operations');
    const sectionNavRef = useRef<HTMLDivElement | null>(null);
    const currentDataScopeKeyRef = useRef('');
    const previousDataScopeKeyRef = useRef('');
    const [sectionNavCanScrollLeft, setSectionNavCanScrollLeft] = useState(false);
    const [sectionNavCanScrollRight, setSectionNavCanScrollRight] = useState(false);

    useEffect(() => {
      window.localStorage.setItem('aql_vision_sidebar_collapsed', String(sidebarCollapsed));
    }, [sidebarCollapsed]);

    useEffect(() => {
      if (demoMode) {
        setAuthRecoveryChecked(true);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('viewerToken') && urlParams.has('alertId')) {
        setAuthRecoveryChecked(true);
        return;
      }

      let active = true;
      const applySession = (session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']) => {
        if (!active) return;
        const sessionToken = session?.access_token ?? null;
        const sessionEmail = session?.user?.email ?? null;
        setAuthToken(sessionToken);
        setUserEmail(sessionEmail);

        if (sessionToken && sessionEmail) {
          localStorage.setItem('aql_auth_token', sessionToken);
          localStorage.setItem('aql_user_email', sessionEmail);
          localStorage.setItem('visionAlertsToken', sessionToken);
          localStorage.setItem('visionAlertsEmail', sessionEmail);
          return;
        }

        localStorage.removeItem('aql_auth_token');
        localStorage.removeItem('aql_user_email');
        localStorage.removeItem('visionAlertsToken');
        localStorage.removeItem('visionAlertsEmail');
      };

      const recoverSession = async () => {
        try {
          const { data } = await supabase.auth.getSession();
          applySession(data.session);
        } catch (error) {
          console.warn('AQL Vision: failed to recover auth session', error);
          applySession(null);
        } finally {
          if (active) setAuthRecoveryChecked(true);
        }
      };

      const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
        applySession(session);
        if (active) setAuthRecoveryChecked(true);
      });

      void recoverSession();
      return () => {
        active = false;
        authListener.subscription.unsubscribe();
      };
    }, [demoMode]);

	    useEffect(() => {
	      const syncMenuPreferences = () => {
	        setMenuOrder(readMenuOrderPreference());
        setHiddenMenuItems(readHiddenMenuPreference());
      };
      const handleStorage = (event: StorageEvent) => {
        if (event.key === MENU_ORDER_STORAGE_KEY || event.key === MENU_HIDDEN_STORAGE_KEY) syncMenuPreferences();
      };
      window.addEventListener(MENU_ORDER_EVENT, syncMenuPreferences);
      window.addEventListener('storage', handleStorage);
      return () => {
        window.removeEventListener(MENU_ORDER_EVENT, syncMenuPreferences);
        window.removeEventListener('storage', handleStorage);
	      };
	    }, []);

	    useEffect(() => () => {
	      if (activeOperationalAlertCloseTimeoutRef.current !== null) {
	        window.clearTimeout(activeOperationalAlertCloseTimeoutRef.current);
	      }
	    }, []);

	    const playOperationalAlertBuzzer = () => {
	      try {
	        const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
	        if (!AudioContextCtor) return;
	        const audioContext = new AudioContextCtor();

	        const scheduleAlarm = () => {
	          const now = audioContext.currentTime + 0.02;
	          const compressor = audioContext.createDynamicsCompressor();
	          compressor.threshold.setValueAtTime(-22, now);
	          compressor.knee.setValueAtTime(18, now);
	          compressor.ratio.setValueAtTime(8, now);
	          compressor.attack.setValueAtTime(0.003, now);
	          compressor.release.setValueAtTime(0.18, now);
	          compressor.connect(audioContext.destination);

	          const pulseOffsets = [0, 0.28, 0.56, 0.96, 1.24, 1.52];

	          pulseOffsets.forEach((offset, index) => {
	            const start = now + offset;
	            const end = start + 0.18;
	            const pulseGain = audioContext.createGain();
	            pulseGain.gain.setValueAtTime(0.0001, start);
	            pulseGain.gain.exponentialRampToValueAtTime(index < 3 ? 0.14 : 0.11, start + 0.018);
	            pulseGain.gain.exponentialRampToValueAtTime(0.0001, end);
	            pulseGain.connect(compressor);

	            const highTone = audioContext.createOscillator();
	            highTone.type = 'square';
	            highTone.frequency.setValueAtTime(index % 2 === 0 ? 880 : 660, start);
	            highTone.frequency.linearRampToValueAtTime(index % 2 === 0 ? 960 : 590, end);
	            highTone.connect(pulseGain);
	            highTone.start(start);
	            highTone.stop(end);

	            const lowTone = audioContext.createOscillator();
	            lowTone.type = 'sawtooth';
	            lowTone.frequency.setValueAtTime(index % 2 === 0 ? 220 : 185, start);
	            lowTone.connect(pulseGain);
	            lowTone.start(start);
	            lowTone.stop(end);
	          });

	          const warningTail = audioContext.createOscillator();
	          const tailGain = audioContext.createGain();
	          const tailStart = now + 1.86;
	          warningTail.type = 'triangle';
	          warningTail.frequency.setValueAtTime(440, tailStart);
	          warningTail.frequency.exponentialRampToValueAtTime(330, tailStart + 0.28);
	          tailGain.gain.setValueAtTime(0.0001, tailStart);
	          tailGain.gain.exponentialRampToValueAtTime(0.055, tailStart + 0.02);
	          tailGain.gain.exponentialRampToValueAtTime(0.0001, tailStart + 0.3);
	          warningTail.connect(tailGain);
	          tailGain.connect(compressor);
	          warningTail.start(tailStart);
	          warningTail.stop(tailStart + 0.32);

	          window.setTimeout(() => {
	            void audioContext.close().catch(() => undefined);
	          }, 2500);
	        };

	        if (audioContext.state === 'suspended') {
	          void audioContext.resume().then(scheduleAlarm).catch(() => undefined);
	        } else {
	          scheduleAlarm();
	        }
	      } catch {
	        // Browsers may block audio until user interaction; the visual alert remains authoritative.
	      }
	    };

	    const closeOperationalAlertToast = () => {
	      if (!activeOperationalAlertToast) return;
	      setActiveOperationalAlertClosing(true);
	      if (activeOperationalAlertCloseTimeoutRef.current !== null) {
	        window.clearTimeout(activeOperationalAlertCloseTimeoutRef.current);
	      }
	      activeOperationalAlertCloseTimeoutRef.current = window.setTimeout(() => {
	        setActiveOperationalAlertToast(null);
	        setActiveOperationalAlertClosing(false);
	        activeOperationalAlertCloseTimeoutRef.current = null;
	      }, 260);
	    };

	    useEffect(() => {
	      setMenuOrder(readMenuOrderPreference());
      setHiddenMenuItems(readHiddenMenuPreference());
    }, [currentView]);

    const cases = useMemo(() => normalizeAlertsToCases(alerts), [alerts]);
    const routeClientSlug = clientSlug ? slugify(clientSlug) : null;
    const routeProjectSlug = projectSlug ? slugify(projectSlug) : null;
    const currentDataScopeKey = [
      client?.id ?? '',
      routeClientSlug ?? '',
      routeProjectSlug ?? '',
      routeProjectId ?? '',
      species ?? '',
      phase ?? '',
      site ?? '',
      tank ?? ''
    ].join('|');
    currentDataScopeKeyRef.current = currentDataScopeKey;

    useEffect(() => {
      if (previousDataScopeKeyRef.current === currentDataScopeKey) return;
      previousDataScopeKeyRef.current = currentDataScopeKey;
      knownOperationalAlertIdsRef.current = new Set();
      latestAlertToastSignatureRef.current = null;
      latestAlertIdRef.current = null;
      isFirstLoadRef.current = true;
    }, [currentDataScopeKey]);

    useEffect(() => {
      if (!tank && scopeLocked) setScopeLocked(false);
    }, [scopeLocked, tank]);

    const shouldShowClientProjectGate = Boolean(
      !demoMode &&
      authToken &&
      userEmail &&
      routeClientSlug &&
      !routeProjectSlug &&
      !clientAdminModeOpen &&
      !isProcessingDeepLink
    );

    useEffect(() => {
      if (!shouldShowClientProjectGate || clientProjectChoicesLoading || clientProjectChoices.length !== 1 || !routeClientSlug) return;
      const [onlyProject] = clientProjectChoices;
      if (!projectHasSingleOperationalPath(onlyProject)) return;
      const onlyArea = onlyProject.operationalAreas?.length === 1 ? onlyProject.operationalAreas[0] : null;
      setSite(onlyArea?.locationName ?? onlyProject.locationLabel ?? onlyProject.primaryInstallation ?? null);
      setTank(onlyArea?.name ?? null);
      setSpecies(onlyArea?.species ?? onlyProject.speciesFocus ?? null);
      setPhase((onlyArea?.phase as Phase | null) ?? (onlyProject.defaultPhase as Phase | null) ?? null);
      setScopeLocked(true);
      navigate(`/${routeClientSlug}/${projectChoiceSlug(onlyProject)}`);
    }, [shouldShowClientProjectGate, clientProjectChoicesLoading, clientProjectChoices, routeClientSlug, navigate, setSite, setTank, setSpecies, setPhase, setScopeLocked]);

    useEffect(() => {
      if (!demoMode) return;
      const demoClient = {
        id: 'demo-aql-pregado',
        name: 'AQL Demo Aquaculture',
        slug: 'demo-pregado',
        species_focus: 'Pregado',
        default_phase: 'adult' as Phase,
        primary_color: '#38bdf8',
        secondary_color: '#0f766e'
      };
      setAvailableClients([demoClient]);
      setClient(demoClient);
      setSpecies('Pregado');
      setPhase('adult');
      setSite('Unidade Piloto');
      setTank('T-04');
      setAuthToken(DEMO_AUTH_TOKEN);
      setUserEmail(DEMO_USER_EMAIL);
      setCurrentView(demoInitialView);
      setSelectedAlertFromEmail(demoSelectedAlertId);
      setRouteProjectId(null);
      setRouteScopeLoading(false);
      setRouteClientName(demoClient.name);
      setClientProjectChoices([]);
      setClientProjectChoicesLoading(false);
      setClientProfileRole('admin');
      setClientProfileName('Demo');
      setClientAdminModeOpen(false);
    }, [availableClients.length, client?.id, demoInitialView, demoMode, demoSelectedAlertId, setAvailableClients, setClient, setPhase, setSite, setSpecies, setTank]);

    useEffect(() => {
      if (demoMode) {
        setDatabaseScopeOptions({
          species: ['Pregado'],
          phases: ['adult'],
          sites: ['Unidade Piloto'],
          tanks: ['T-04'],
          tanksBySite: { 'Unidade Piloto': ['T-04'] },
          speciesIdsByLabel: {},
          phaseIdsByLabel: {},
          siteIdsByLabel: {},
          tankIdsByLabel: {},
          kitIdsByTankLabel: {}
        });
        return;
      }

      if (!routeClientSlug) {
        setDatabaseScopeOptions(EMPTY_DATABASE_SCOPE_OPTIONS);
        setRouteProjectId(null);
        setRouteScopeLoading(false);
        setRouteClientName(null);
        setClientProjectChoices([]);
        setClientProjectChoicesLoading(false);
        setClientProfileRole(null);
        setClientProfileName(null);
        setClientAdminModeOpen(false);
        return;
      }

      let active = true;
      setRouteScopeLoading(true);
      setDatabaseScopeOptions(EMPTY_DATABASE_SCOPE_OPTIONS);
      setRouteProjectId(null);
      setAlerts([]);
      setDashboardSummary(null);
      setLoading(Boolean(routeProjectSlug));
      setRouteClientName(null);
      setClientProjectChoices([]);
      setClientProjectChoicesLoading(true);
      setClientProfileRole(null);
      setClientProfileName(null);

      const loadDatabaseScope = async () => {
        try {
          const { data: clientRows } = await supabase
            .from('aql_clients')
            .select('*')
            .eq('is_active', true)
            .order('name');

          const clientRow = Array.isArray(clientRows)
            ? clientRows.find((row: any) =>
              slugify(String(row.slug ?? '')) === routeClientSlug ||
              slugify(String(row.name ?? '')) === routeClientSlug
            )
            : null;
          const clientId = clientRow?.client_id
            ? String(clientRow.client_id)
            : client && (slugify(client.slug) === routeClientSlug || slugify(client.name) === routeClientSlug)
              ? client.id
              : null;
          const clientName = clientRow?.name
            ? String(clientRow.name)
            : client && (slugify(client.slug) === routeClientSlug || slugify(client.name) === routeClientSlug)
              ? client.name
              : null;

          let projectId: string | null = null;
          let projectSpecies: string | null = null;
          let projectPhase: Phase | null = null;
          let projectRows: any[] = [];

          if (clientId || routeProjectSlug) {
            let projectQuery = supabase
              .from('aql_projects')
              .select('*')
              .eq('is_active', true)
              .order('name');

            if (clientId) projectQuery = projectQuery.eq('client_id', clientId);

            const { data: projects } = await projectQuery;
            projectRows = Array.isArray(projects) ? projects : [];
            const project = routeProjectSlug
              ? projectRows.find((row: any) => projectMatchesRoute(row, routeProjectSlug, routeClientSlug)) ?? null
              : null;
            projectId = project?.project_id ? String(project.project_id) : null;
            projectSpecies = project?.species_focus ? String(project.species_focus) : null;
            projectPhase = normalizePhaseOption(project?.default_phase);
          }

          let locationsQuery = supabase
            .from('aql_locations')
            .select('*')
            .eq('is_active', true)
            .order('name');

          if (projectId) locationsQuery = locationsQuery.eq('project_id', projectId);
          else if (clientId) locationsQuery = locationsQuery.eq('client_id', clientId);

          const { data: locations } = await locationsQuery;
          const locationRows = Array.isArray(locations) ? locations : [];
          const locationNameById = new Map<string, string>();
          const siteIdsByLabel: Record<string, string> = {};
          locationRows.forEach((row: any) => {
            const id = String(row.location_id ?? '').trim();
            const name = String(row.name ?? '').trim();
            if (id && name) {
              locationNameById.set(id, name);
              siteIdsByLabel[scopeLabelKey(name)] = id;
            }
          });

          let zonesQuery = supabase
            .from('aql_zones')
            .select('*')
            .eq('is_active', true)
            .order('name');

          if (projectId) zonesQuery = zonesQuery.eq('project_id', projectId);
          else if (clientId) zonesQuery = zonesQuery.eq('client_id', clientId);

          const { data: zones } = await zonesQuery;
          const zoneRows = Array.isArray(zones) ? zones : [];
          const tankIdsByLabel: Record<string, string> = {};
          const tanksBySite = zoneRows.reduce<Record<string, string[]>>((acc, row: any) => {
            const siteName = locationNameById.get(String(row.location_id ?? '').trim());
            const tankName = String(row.name ?? row.code ?? '').trim();
            const tankId = String(row.zone_id ?? row.id ?? '').trim();
            if (tankName && tankId) tankIdsByLabel[scopeLabelKey(tankName)] = tankId;
            if (!siteName || !tankName) return acc;
            acc[siteName] = Array.from(new Set([...(acc[siteName] ?? []), tankName])).sort((a, b) => a.localeCompare(b));
            return acc;
          }, {});

          let speciesQuery = supabase.from('aql_species').select('*').order('name');
          if (projectId) speciesQuery = speciesQuery.eq('project_id', projectId);
          else if (clientId) speciesQuery = speciesQuery.eq('client_id', clientId);
          const { data: speciesRowsRaw } = await speciesQuery;
          const speciesRows = Array.isArray(speciesRowsRaw) ? speciesRowsRaw : [];
          const speciesIdsByLabel = speciesRows.reduce<Record<string, string>>((acc, row: any) => {
            const id = String(row.species_id ?? row.id ?? '').trim();
            const labels = [row.common_name, row.name, row.code].map((value) => String(value ?? '').trim()).filter(Boolean);
            labels.forEach((label) => {
              if (id) acc[scopeLabelKey(label)] = id;
            });
            return acc;
          }, {});

          let phasesQuery = supabase.from('aql_phases').select('*').order('name');
          if (projectId) phasesQuery = phasesQuery.eq('project_id', projectId);
          else if (clientId) phasesQuery = phasesQuery.eq('client_id', clientId);
          const { data: phaseRowsRaw } = await phasesQuery;
          const phaseRows = Array.isArray(phaseRowsRaw) ? phaseRowsRaw : [];
          const phaseIdsByLabel = phaseRows.reduce<Record<string, string>>((acc, row: any) => {
            const id = String(row.phase_id ?? row.id ?? '').trim();
            const labels = [row.name, row.code, normalizePhaseOption(row.name), normalizePhaseOption(row.code)].map((value) => String(value ?? '').trim()).filter(Boolean);
            labels.forEach((label) => {
              if (id) acc[scopeLabelKey(label)] = id;
            });
            return acc;
          }, {});

          let kitsQuery = supabase.from('aql_kits').select('*');
          if (projectId) kitsQuery = kitsQuery.eq('project_id', projectId);
          else if (clientId) kitsQuery = kitsQuery.eq('client_id', clientId);
          const { data: kitRowsRaw } = await kitsQuery;
          const kitRows = Array.isArray(kitRowsRaw) ? kitRowsRaw : [];
          const kitIdsByTankLabel = kitRows.reduce<Record<string, string>>((acc, row: any) => {
            const kitId = String(row.kit_id ?? '').trim();
            const zoneId = String(row.zone_id ?? '').trim();
            const zone = zoneRows.find((zoneRow: any) => String(zoneRow.zone_id ?? zoneRow.id ?? '') === zoneId);
            const tankName = String(zone?.name ?? zone?.code ?? '').trim();
            if (kitId && tankName) acc[scopeLabelKey(tankName)] = kitId;
            return acc;
          }, {});
          const [
            allCameraRows,
            allSensorRows,
            allDeviceRows,
            allCaptureRows,
            allAlertRows,
            allCaseRows,
            allSensorReadingRows
          ] = await Promise.all([
            fetchTableRows('aql_cameras', 1000),
            fetchTableRows('aql_sensors', 2000),
            fetchTableRows('aql_devices', 1000),
            fetchTableRows('aql_vision_captures', 1000),
            fetchTableRows('aql_alerts', 1000),
            fetchTableRows('aql_cases', 1000),
            fetchTableRows('aql_sensor_readings', 2000)
          ]);
          const projectLocationRows = (projectIdValue: string) => locationRows.filter((row: any) => String(row.project_id ?? '').trim() === projectIdValue);
          const projectZoneRows = (projectIdValue: string) => zoneRows.filter((row: any) => String(row.project_id ?? '').trim() === projectIdValue);
          const projectKitRows = (projectIdValue: string, zoneIds: Set<string>) => kitRows.filter((row: any) =>
            String(row.project_id ?? '').trim() === projectIdValue ||
            zoneIds.has(String(row.zone_id ?? '').trim())
          );
          const rowLabelMatches = (row: any, keys: string[], labels: Set<string>) => {
            if (!labels.size) return false;
            return keys.some((key) => {
              const value = pickString(row, [key]);
              return value ? labels.has(scopeLabelKey(value)) : false;
            });
          };
          const rowBelongsToProject = (
            row: any,
            projectIdValue: string,
            zoneIds: Set<string>,
            kitIds: Set<string>,
            deviceIds = new Set<string>(),
            zoneLabels = new Set<string>(),
            locationIds = new Set<string>(),
            locationLabels = new Set<string>()
          ) =>
            sameId(pickString(row, ['project_id']), projectIdValue) ||
            rowHasId(row, ['zone_id', 'tank_id'], zoneIds) ||
            rowHasId(row, ['kit_id'], kitIds) ||
            rowHasId(row, ['device_id', 'id'], deviceIds) ||
            rowHasId(row, ['location_id', 'site_id'], locationIds) ||
            rowLabelMatches(row, ['zone_name', 'zone_label', 'tank', 'tank_name', 'tankName', 'location'], zoneLabels) ||
            rowLabelMatches(row, ['site', 'site_name', 'location', 'location_name'], locationLabels);
          const latestDate = (rows: any[]) => rows
            .map((row) => readDateValue(row))
            .filter(Boolean)
            .sort((a, b) => new Date(String(b)).getTime() - new Date(String(a)).getTime())[0] ?? null;
          const formatCapacityLabel = (row: any) => {
            const value = pickString(row, ['capacity_label', 'capacityLabel', 'capacity', 'production_capacity'])
              ?? pickNumber(row, ['capacity_tons', 'capacity_kg', 'planned_capacity', 'max_biomass_kg']);
            if (value === null || value === undefined || value === '') return null;
            return typeof value === 'number' ? `${value.toLocaleString('pt-PT')} capacidade` : String(value);
          };
          const formatProjectLocationLabel = (projectRow: any, locationsForProject: any[]) => {
            const primaryLocation = String(locationsForProject[0]?.name ?? '').trim();
            if (primaryLocation) return primaryLocation;
            const city = pickString(projectRow, ['city', 'locality', 'location_city']);
            const country = pickString(projectRow, ['country', 'location_country']);
            const namedLocation = pickString(projectRow, ['location', 'location_name', 'site', 'main_location', 'address']);
            return [namedLocation, city, country].map((value) => String(value ?? '').trim()).filter(Boolean).join(' · ') || null;
          };
          const buildProjectChoice = (row: any): ClientProjectChoice | null => {
            const id = String(row.project_id ?? row.id ?? '').trim();
            if (!id) return null;
            const locationsForProject = projectLocationRows(id);
            const locationLabel = formatProjectLocationLabel(row, locationsForProject);
            const zonesForProject = projectZoneRows(id);
            const zoneIds = new Set(zonesForProject.map((zone: any) => String(zone.zone_id ?? zone.id ?? '').trim()).filter(Boolean));
            const kitsForProject = projectKitRows(id, zoneIds);
            const kitIds = new Set(kitsForProject.map((kit: any) => String(kit.kit_id ?? kit.id ?? '').trim()).filter(Boolean));
            const locationIds = new Set(locationsForProject.map((location: any) => String(location.location_id ?? location.id ?? '').trim()).filter(Boolean));
            const locationLabels = new Set(locationsForProject.map((location: any) => scopeLabelKey(location.name)).filter(Boolean));
            const zoneLabels = new Set(zonesForProject.flatMap((zone: any) => [zone.name, zone.code].map(scopeLabelKey)).filter(Boolean));
            const cameraRows = allCameraRows.filter((camera: any) => rowBelongsToProject(camera, id, zoneIds, kitIds, new Set<string>(), zoneLabels, locationIds, locationLabels));
            const sensorRows = allSensorRows.filter((sensor: any) => rowBelongsToProject(sensor, id, zoneIds, kitIds, new Set<string>(), zoneLabels, locationIds, locationLabels));
            const projectSensorsById = new Map(sensorRows.map((sensor: any) => [idFrom(sensor, ['sensor_id', 'id']), sensor]));
            const deviceIds = new Set([
              ...sensorRows.map((sensor: any) => String(sensor.device_id ?? '').trim()).filter(Boolean),
              ...cameraRows.map((camera: any) => String(camera.device_id ?? '').trim()).filter(Boolean)
            ]);
            const deviceRows = allDeviceRows.filter((device: any) => rowBelongsToProject(device, id, zoneIds, kitIds, deviceIds, zoneLabels, locationIds, locationLabels));
            const captureRows = allCaptureRows.filter((capture: any) => rowBelongsToProject(capture, id, zoneIds, kitIds, deviceIds, zoneLabels, locationIds, locationLabels));
            const sensorReadingRows = allSensorReadingRows.filter((reading: any) => rowBelongsToProject(reading, id, zoneIds, kitIds, deviceIds, zoneLabels, locationIds, locationLabels));
            const alertRows = allAlertRows.filter((alert: any) => rowBelongsToProject(alert, id, zoneIds, kitIds, deviceIds, zoneLabels, locationIds, locationLabels));
            const alertIds = rowIdSet(alertRows, ['alert_id', 'id']);
            const caseRows = allCaseRows.filter((caseRow: any) =>
              rowBelongsToProject(caseRow, id, zoneIds, kitIds, deviceIds, zoneLabels, locationIds, locationLabels) ||
              rowHasId(caseRow, ['alert_id'], alertIds)
            );
            const sensorReadingAlertRows = Array.from(
              sensorReadingRows
                .sort(byLatestDate)
                .reduce<Map<string, any>>((acc, reading: any) => {
                  const metadata = reading?.metadata && typeof reading.metadata === 'object' ? reading.metadata : {};
                  if (metadata.alert_hidden === true || metadata.hidden === true) return acc;
                  const sensor = projectSensorsById.get(idFrom(reading, ['sensor_id', 'id']));
                  const metric = sensorMetricFromType(
                    pickString(reading, ['sensor_type', 'type', 'metric', 'parameter']) ??
                    pickString(metadata, ['sensor_type', 'sensorType', 'type', 'metric', 'parameter']) ??
                    pickString(sensor, ['sensor_type', 'type', 'name'])
                  );
                  if (!metric) return acc;
                  const value = normalizeOperationalSensorValue(metric, readingNumber(reading, ['value', 'reading_value', 'last_value', 'reading']));
                  if (value === null || !classifySensorReadingAlert(metric, value)) return acc;
                  const sensorKey = idFrom(reading, ['sensor_id', 'id']) || pickString(reading, ['device_id']) || pickString(reading, ['zone_id', 'tank_id']) || 'global';
                  const key = `${metric}:${sensorKey}`;
                  if (!acc.has(key)) acc.set(key, reading);
                  return acc;
                }, new Map<string, any>())
                .values()
            );
            const activeAlertsCount = alertRows.filter(isActiveAlertRow).length + sensorReadingAlertRows.length;
            const hasCriticalSensorReading = sensorReadingAlertRows.some((reading: any) => {
              const metadata = reading?.metadata && typeof reading.metadata === 'object' ? reading.metadata : {};
              const sensor = projectSensorsById.get(idFrom(reading, ['sensor_id', 'id']));
              const metric = sensorMetricFromType(
                pickString(reading, ['sensor_type', 'type', 'metric', 'parameter']) ??
                pickString(metadata, ['sensor_type', 'sensorType', 'type', 'metric', 'parameter']) ??
                pickString(sensor, ['sensor_type', 'type', 'name'])
              );
              if (!metric) return false;
              const value = normalizeOperationalSensorValue(metric, readingNumber(reading, ['value', 'reading_value', 'last_value', 'reading']));
              return value !== null && classifySensorReadingAlert(metric, value)?.severity === 'critical';
            });
            const pendingValidationsCount = caseRows.filter((caseRow: any) => !alertHasFinalValidation(caseRow)).length;
            const latestCaptureByKit = new Map<string, any>();
            captureRows.sort(byLatestDate).forEach((capture: any) => {
              const key = String(capture.kit_id ?? capture.device_id ?? capture.zone_id ?? capture.tank_id ?? '').trim();
              if (key && !latestCaptureByKit.has(key)) latestCaptureByKit.set(key, capture);
            });
            const offlineUnitsCount = kitsForProject.filter((kit: any) => {
              const device = deviceRows.find((item: any) => String(item.kit_id ?? '') === String(kit.kit_id ?? kit.id ?? '')) ?? {};
              const heartbeatIntervalSeconds = heartbeatIntervalSecondsFor(kit, device);
              const lastCommunication = pickString(device, ['last_communication', 'lastCommunication', 'last_seen_at', 'heartbeat_at', 'updated_at'])
                ?? pickString(kit, ['last_communication', 'lastCommunication', 'last_seen_at', 'heartbeat_at', 'updated_at']);
              const online = heartbeatOnline(lastCommunication, heartbeatIntervalSeconds);
              const unitKey = String(kit.kit_id ?? kit.id ?? kit.zone_id ?? '').trim();
              const latestCapture = latestCaptureByKit.get(unitKey) ?? latestCaptureByKit.get(String(kit.zone_id ?? '').trim());
              const latestCaptureAt = readDateValue(latestCapture);
              const feedAgeMs = latestCaptureAt ? Date.now() - new Date(latestCaptureAt).getTime() : Number.POSITIVE_INFINITY;
              const feedStale = !latestCaptureAt || feedAgeMs > 24 * 60 * 60 * 1000;
              return online === false || (!cameraRows.length ? false : feedStale);
            }).length;
            const status: ClientProjectChoice['status'] = offlineUnitsCount > 0 && activeAlertsCount === 0
              ? 'offline'
              : hasCriticalSensorReading || alertRows.some((alert: any) => ['critical', 'high'].includes(String(alert.severity ?? alert.level ?? '').toLowerCase()))
                ? 'critical'
                : activeAlertsCount > 0 || pendingValidationsCount > 0
                  ? 'attention'
                  : 'operational';
            const speciesFocus = pickString(row, ['species_focus', 'speciesFocus'])
              ?? uniqueSorted(zonesForProject.map((zone: any) => pickString(zone, ['species'])))[0]
              ?? null;
            const defaultPhase = pickString(row, ['default_phase', 'defaultPhase'])
              ?? uniqueSorted(zonesForProject.map((zone: any) => normalizePhaseOption(pickString(zone, ['phase', 'biological_phase']))))[0]
              ?? null;
            const projectAlertIds = rowIdSet(alertRows, ['alert_id', 'id']);
            const countZoneAlerts = (zone: any) => {
              const zoneId = String(zone.zone_id ?? zone.id ?? '').trim();
              const zoneScopedIds = new Set([zoneId].filter(Boolean));
              const zoneScopedLabels = new Set([zone.name, zone.code].map(scopeLabelKey).filter(Boolean));
              const zoneKits = kitsForProject.filter((kit: any) => String(kit.zone_id ?? '').trim() === zoneId);
              const zoneKitIds = new Set(zoneKits.map((kit: any) => String(kit.kit_id ?? kit.id ?? '').trim()).filter(Boolean));
              const zoneDeviceIds = new Set([
                ...sensorRows
                  .filter((sensor: any) => String(sensor.zone_id ?? '').trim() === zoneId || zoneKitIds.has(String(sensor.kit_id ?? '').trim()))
                  .map((sensor: any) => String(sensor.device_id ?? '').trim())
                  .filter(Boolean),
                ...cameraRows
                  .filter((camera: any) => String(camera.zone_id ?? '').trim() === zoneId || zoneKitIds.has(String(camera.kit_id ?? '').trim()))
                  .map((camera: any) => String(camera.device_id ?? '').trim())
                  .filter(Boolean)
              ]);
              const zoneAlerts = alertRows.filter((alert: any) =>
                rowHasId(alert, ['zone_id', 'tank_id'], zoneScopedIds) ||
                rowHasId(alert, ['kit_id'], zoneKitIds) ||
                rowHasId(alert, ['device_id'], zoneDeviceIds) ||
                rowLabelMatches(alert, ['zone_name', 'zone_label', 'tank', 'tank_name', 'tankName', 'location'], zoneScopedLabels)
              );
              const zoneSensorReadingAlerts = sensorReadingAlertRows.filter((reading: any) =>
                rowHasId(reading, ['zone_id', 'tank_id'], zoneScopedIds) ||
                rowHasId(reading, ['kit_id'], zoneKitIds) ||
                rowHasId(reading, ['device_id'], zoneDeviceIds) ||
                rowLabelMatches(reading, ['zone_name', 'zone_label', 'tank', 'tank_name', 'tankName', 'location'], zoneScopedLabels)
              );
              const zoneAlertIds = rowIdSet(zoneAlerts, ['alert_id', 'id']);
              const zoneCases = caseRows.filter((caseRow: any) =>
                rowHasId(caseRow, ['zone_id', 'tank_id'], zoneScopedIds) ||
                rowHasId(caseRow, ['kit_id'], zoneKitIds) ||
                rowHasId(caseRow, ['device_id'], zoneDeviceIds) ||
                rowHasId(caseRow, ['alert_id'], zoneAlertIds) ||
                (!zoneAlertIds.size && rowHasId(caseRow, ['alert_id'], projectAlertIds) && rowLabelMatches(caseRow, ['zone_name', 'zone_label', 'tank', 'tank_name', 'tankName', 'location'], zoneScopedLabels)) ||
                rowLabelMatches(caseRow, ['zone_name', 'zone_label', 'tank', 'tank_name', 'tankName', 'location'], zoneScopedLabels)
              );
              return {
                activeAlertsCount: zoneAlerts.filter(isActiveAlertRow).length + zoneSensorReadingAlerts.length,
                pendingValidationsCount: zoneCases.filter((caseRow: any) => !alertHasFinalValidation(caseRow)).length
              };
            };

            return {
              id,
              name: String(row.name ?? 'Projeto sem nome'),
              slug: String(row.slug ?? ''),
              contextType: normalizeOperationalContextType(row.context_type),
              description: row.description ? String(row.description) : null,
              coverImageUrl: pickString(row, ['cover_image_url', 'coverImageUrl']),
              speciesFocus,
              defaultPhase,
              status,
              primaryInstallation: locationLabel,
              locationLabel,
              installationsCount: locationsForProject.length,
              operationalAreasCount: zonesForProject.length,
              monitoringUnitsCount: kitsForProject.length,
              activeAlertsCount,
              pendingValidationsCount,
              offlineUnitsCount,
              lastUpdatedAt: latestDate([...alertRows, ...caseRows, ...captureRows, ...deviceRows, ...kitsForProject, row]),
              hasVision: cameraRows.length > 0 || captureRows.length > 0,
              hasSensors: sensorRows.length > 0,
              capacityLabel: formatCapacityLabel(row),
              installations: locationsForProject.map((location: any) => ({
                id: String(location.location_id ?? location.id ?? location.name ?? ''),
                name: String(location.name ?? 'Instalação sem nome'),
                latitude: pickNumber(location, ['latitude', 'lat']),
                longitude: pickNumber(location, ['longitude', 'lng', 'lon']),
                status
              })),
              operationalAreas: zonesForProject.map((zone: any) => {
                const zoneId = String(zone.zone_id ?? zone.id ?? '').trim();
                const zoneKits = kitsForProject.filter((kit: any) => String(kit.zone_id ?? '').trim() === zoneId);
                const zoneAlertSummary = countZoneAlerts(zone);
                return {
                  id: zoneId || String(zone.name ?? ''),
                  name: String(zone.name ?? zone.code ?? 'Área operacional sem nome'),
                  code: zone.code ? String(zone.code) : null,
                  locationName: locationNameById.get(String(zone.location_id ?? '').trim()) ?? null,
                  species: pickString(zone, ['species']),
                  phase: normalizePhaseOption(pickString(zone, ['phase', 'biological_phase'])),
                  unitsCount: zoneKits.length,
                  activeAlertsCount: zoneAlertSummary.activeAlertsCount,
                  pendingValidationsCount: zoneAlertSummary.pendingValidationsCount,
                  status: zoneAlertSummary.activeAlertsCount > 0 || zoneAlertSummary.pendingValidationsCount > 0 ? 'attention' : status
                };
              }),
              monitoringUnits: kitsForProject.map((kit: any) => {
                const kitZone = zonesForProject.find((zone: any) => String(zone.zone_id ?? zone.id ?? '').trim() === String(kit.zone_id ?? '').trim());
                const device = deviceRows.find((item: any) => String(item.kit_id ?? '') === String(kit.kit_id ?? kit.id ?? '')) ?? {};
                const heartbeatIntervalSeconds = heartbeatIntervalSecondsFor(kit, device);
                const lastCommunication = pickString(device, ['last_communication', 'lastCommunication', 'last_seen_at', 'heartbeat_at', 'updated_at'])
                  ?? pickString(kit, ['last_communication', 'lastCommunication', 'last_seen_at', 'heartbeat_at', 'updated_at']);
                const online = heartbeatOnline(lastCommunication, heartbeatIntervalSeconds);
                return {
                  id: String(kit.kit_id ?? kit.id ?? kit.name ?? ''),
                  name: String(kit.name ?? kit.kit_id ?? 'Unidade edge'),
                  zoneName: kitZone ? String(kitZone.name ?? kitZone.code ?? '') : null,
                  type: pickString(kit, ['kit_type', 'type']),
                  status: online === false ? 'offline' : status,
                  heartbeat: lastCommunication
                };
              })
            };
          };

          const zoneSpeciesIds = new Set(zoneRows.map((row: any) => String(row.species_id ?? '').trim()).filter(Boolean));
          const zonePhaseIds = new Set(zoneRows.map((row: any) => String(row.phase_id ?? '').trim()).filter(Boolean));
          const projectSpeciesRows = projectId
            ? speciesRows.filter((row: any) => {
              const rowProjectId = String(row.project_id ?? '').trim();
              const rowSpeciesId = String(row.species_id ?? row.id ?? '').trim();
              return rowProjectId === projectId || (rowSpeciesId && zoneSpeciesIds.has(rowSpeciesId));
            })
            : speciesRows;
          const projectPhaseRows = projectId
            ? phaseRows.filter((row: any) => {
              const rowProjectId = String(row.project_id ?? '').trim();
              const rowPhaseId = String(row.phase_id ?? row.id ?? '').trim();
              return rowProjectId === projectId || (rowPhaseId && zonePhaseIds.has(rowPhaseId));
            })
            : phaseRows;
          const speciesById = new Map(
            speciesRows
              .map((row: any) => [
                String(row.species_id ?? row.id ?? '').trim(),
                String(row.common_name ?? row.name ?? row.code ?? '').trim()
              ] as const)
              .filter(([id, label]) => Boolean(id && label))
          );
          const phaseById = new Map(
            phaseRows
              .map((row: any) => [
                String(row.phase_id ?? row.id ?? '').trim(),
                normalizePhaseOption(row.name ?? row.code)
              ] as const)
              .filter(([id, phaseOption]) => Boolean(id && phaseOption))
          );

          const derivedSpeciesOptions = uniqueSorted([
            projectSpecies,
            ...projectSpeciesRows.map((row: any) => row.common_name ?? row.name),
            ...zoneRows.map((row: any) => row.species ?? speciesById.get(String(row.species_id ?? '').trim()))
          ]);
          const derivedPhaseOptions = PHASE_ORDER.filter((phaseOption) => [
            projectPhase,
            ...projectPhaseRows.map((row: any) => normalizePhaseOption(row.name ?? row.code)),
            ...zoneRows.map((row: any) => normalizePhaseOption(row.phase) ?? phaseById.get(String(row.phase_id ?? '').trim()))
          ].includes(phaseOption));

          const nextOptions: DatabaseScopeOptions = {
            species: derivedSpeciesOptions,
            phases: derivedPhaseOptions,
            sites: uniqueSorted(locationRows.map((row: any) => row.name)),
            tanks: uniqueSorted(Object.values(tanksBySite).flat()),
            tanksBySite,
            speciesIdsByLabel,
            phaseIdsByLabel,
            siteIdsByLabel,
            tankIdsByLabel,
            kitIdsByTankLabel
          };

          if (active) {
            let matchedProfile: any = null;
            let allowedProjectIds: string[] | null = null;

            if (clientId && userEmail) {
              const { data: sessionData } = await supabase.auth.getSession();
              const authUserId = sessionData.session?.user?.id ?? '';
              let userQuery = supabase
                .from('aql_users')
                .select('user_id,role,auth_user_id,email,client_id,project_id,is_active,full_name,user_name,username')
                .eq('is_active', true)
                .limit(1);

              if (authUserId) {
                userQuery = userQuery.or(`auth_user_id.eq.${authUserId},email.eq.${userEmail}`);
              } else {
                userQuery = userQuery.eq('email', userEmail);
              }

              const { data: userRows } = await userQuery;
              const candidateProfile = Array.isArray(userRows) ? userRows[0] : null;
              if (candidateProfile?.user_id && String(candidateProfile.client_id ?? '') !== clientId) {
                const { data: clientAccessRows } = await supabase
                  .from('aql_user_clients')
                  .select('user_id')
                  .eq('user_id', candidateProfile.user_id)
                  .eq('client_id', clientId)
                  .limit(1);
                matchedProfile = Array.isArray(clientAccessRows) && clientAccessRows.length > 0
                  ? candidateProfile
                  : null;
              } else {
                matchedProfile = candidateProfile;
              }
              if (!matchedProfile) {
                allowedProjectIds = [];
              }

              if (matchedProfile?.user_id) {
                const { data: accessRows } = await supabase
                  .from('aql_user_projects')
                  .select('project_id')
                  .eq('user_id', matchedProfile.user_id);
                const projectIds = Array.from(new Set(
                  (Array.isArray(accessRows) ? accessRows : [])
                    .map((row: any) => String(row.project_id ?? '').trim())
                    .filter(Boolean)
                ));
                if (projectIds.length > 0) {
                  allowedProjectIds = projectIds;
                } else if (matchedProfile.project_id) {
                  allowedProjectIds = [String(matchedProfile.project_id)];
                }
              }
            }

            const visibleProjectRows = allowedProjectIds
              ? projectRows.filter((row: any) => allowedProjectIds.includes(String(row.project_id ?? row.id ?? '')))
              : projectRows;
            const visibleProjectChoices = visibleProjectRows.map(buildProjectChoice).filter(Boolean) as ClientProjectChoice[];

            if (routeProjectSlug && projectId && allowedProjectIds && !allowedProjectIds.includes(projectId)) {
              setRouteProjectId(null);
              setRouteClientName(clientName);
              setClientProjectChoices(visibleProjectChoices);
              setClientProfileRole(matchedProfile?.role ? String(matchedProfile.role) : null);
              setClientProfileName(
                matchedProfile
                  ? String(matchedProfile.full_name ?? matchedProfile.user_name ?? matchedProfile.username ?? '').trim() || null
                  : null
              );
              setDatabaseScopeOptions(EMPTY_DATABASE_SCOPE_OPTIONS);
              setClientProjectChoicesLoading(false);
              setRouteScopeLoading(false);
              navigate(`/${routeClientSlug}`);
              return;
            }

            setRouteProjectId(projectId);
            setRouteClientName(clientName);
            setClientProjectChoices(visibleProjectChoices);

            setClientProfileRole(matchedProfile?.role ? String(matchedProfile.role) : null);
            setClientProfileName(
              matchedProfile
                ? String(matchedProfile.full_name ?? matchedProfile.user_name ?? matchedProfile.username ?? '').trim() || null
                : null
            );

            setDatabaseScopeOptions(nextOptions);
            setClientProjectChoicesLoading(false);
            setRouteScopeLoading(false);
          }
        } catch (error) {
          console.error('Failed to load database scope options:', error);
          if (active) {
            setRouteProjectId(null);
            setRouteScopeLoading(false);
            setRouteClientName(null);
            setClientProjectChoices([]);
            setClientProfileRole(null);
            setClientProfileName(null);
            setDatabaseScopeOptions(EMPTY_DATABASE_SCOPE_OPTIONS);
            setClientProjectChoicesLoading(false);
          }
        }
      };

      loadDatabaseScope();

      return () => {
        active = false;
      };
    }, [client?.id, client?.name, client?.slug, demoMode, navigate, routeClientSlug, routeProjectSlug, userEmail]);

    useEffect(() => {
      const alertClients = buildClientCatalog(alerts);
      const sourceClients = availableClients.length > 0 ? availableClients : alertClients;
      const clientCatalog = Array.from(
        sourceClients.reduce((map, item) => {
          map.set(item.id, item);
          return map;
        }, new Map<string, (typeof alertClients)[number]>()).values()
      );

      const clients = routeClientSlug
        ? clientCatalog.filter((item) => slugify(item.slug) === routeClientSlug || slugify(item.name) === routeClientSlug)
        : clientCatalog;

      const currentIds = availableClients.map((item) => item.id).join('|');
      const nextIds = clients.map((item) => item.id).join('|');

      if (currentIds !== nextIds) {
        setAvailableClients(clients);
      }

      if (!client || !clients.some((item) => item.id === client.id)) {
        setClient(clients[0] ?? null);
      }
    }, [alerts, availableClients, client, routeClientSlug, setAvailableClients, setClient]);

    const scopedCases = useMemo(() => {
      if (demoMode) return cases;
      const filtered = applyGlobalScope(cases, {
        clientId: client?.id ?? null,
        projectId: routeProjectId,
        species,
        phase,
        site,
        tank
      });

      // `loadMvpVisionData` already receives the active operational scope. If a
      // secondary normalization mismatch drops everything here, prefer the fetched
      // scope data over a false empty state.
      return filtered.length === 0 && cases.length > 0 ? cases : filtered;
    }, [cases, client, demoMode, phase, routeProjectId, site, species, tank]);
    const caseScopeOptions = useMemo(
      () => collectScopeOptions(cases.filter((item) =>
        (!client || item.clientId === client.id) &&
        (!routeProjectId || item.projectId === routeProjectId)
      )),
      [cases, client, routeProjectId]
    );
    const tankCaseOptions = useMemo(
      () => collectScopeOptions(cases.filter((item) =>
        (!client || item.clientId === client.id) &&
        (!routeProjectId || item.projectId === routeProjectId) &&
        (!site || item.site === site)
      )).tanks,
      [cases, client, routeProjectId, site]
    );
    const scopeOptions = useMemo(() => {
      const databaseTanks = site && databaseScopeOptions.tanksBySite[site]
        ? databaseScopeOptions.tanksBySite[site]
        : databaseScopeOptions.tanks;
      const useDatabaseBiologyScope = Boolean(routeProjectId && (databaseScopeOptions.species.length || databaseScopeOptions.phases.length));
      const projectSpeciesOptions = routeProjectId && databaseScopeOptions.species.length === 1
        ? databaseScopeOptions.species
        : null;
      const projectPhaseOptions = routeProjectId && databaseScopeOptions.phases.length === 1
        ? databaseScopeOptions.phases
        : null;
      const speciesOptions = projectSpeciesOptions
        ?? (useDatabaseBiologyScope
          ? databaseScopeOptions.species
          : uniqueSorted([...databaseScopeOptions.species, ...caseScopeOptions.species]));
      const phaseOptions = projectPhaseOptions
        ?? PHASE_ORDER.filter((phaseOption) => (
          useDatabaseBiologyScope
            ? databaseScopeOptions.phases
            : [...databaseScopeOptions.phases, ...caseScopeOptions.phases]
        ).includes(phaseOption));

      return {
        species: speciesOptions,
        phases: phaseOptions,
        sites: uniqueSorted([...databaseScopeOptions.sites, ...caseScopeOptions.sites]),
        tanks: uniqueSorted([...databaseTanks, ...tankCaseOptions])
      };
    }, [caseScopeOptions, databaseScopeOptions, routeProjectId, site, tankCaseOptions]);

    useEffect(() => {
      if (species && scopeOptions.species.length && !scopeOptions.species.includes(species)) setSpecies(null);
      if (phase && scopeOptions.phases.length && !scopeOptions.phases.includes(phase)) setPhase(null);
      if (site && scopeOptions.sites.length && !scopeOptions.sites.includes(site)) setSite(null);
      if (tank && scopeOptions.tanks.length && !scopeOptions.tanks.includes(tank)) setTank(null);
    }, [phase, scopeOptions, setPhase, setSite, setSpecies, setTank, site, species, tank]);

    useEffect(() => {
      if (dateFilterPreset !== 'custom' || !dateFilter || scopedCases.length === 0) return;
      const selectedDate = fromLocalDateInputValue(dateFilter);
      if (!selectedDate) {
        setDateFilter('');
        setDateFilterPreset('all');
        return;
      }
      // Remove a verificação do "hasMatchesForSelectedDate" para evitar o reset forçado.
    }, [dateFilter, dateFilterPreset, scopedCases]);

    const dateFilterLabel = useMemo(() => {
      if (dateFilterPreset === 'today') return 'Hoje';
      if (dateFilterPreset === 'week') return 'Esta semana';
      if (dateFilterPreset === 'month') return 'Este mês';
      if (dateFilterPreset === 'custom' && dateFilter) {
        const selectedDate = fromLocalDateInputValue(dateFilter);
        if (!selectedDate) return 'Escolher data';
        return new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(selectedDate);
      }
      return 'Todas as datas';
    }, [dateFilter, dateFilterPreset]);

    const formatCalendarDate = (dateInstance: Date): string => {
      const year = dateInstance.getFullYear();
      const month = String(dateInstance.getMonth() + 1).padStart(2, '0');
      const dayOfMonth = String(dateInstance.getDate()).padStart(2, '0');
      return `${year}-${month}-${dayOfMonth}`;
    };

  const dateCalendarDays = useMemo(() => {
  // Usa o mês de navegação atual em vez de ficar preso ao dateFilter
  const baseDate = currentCalendarMonth;
  const monthStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  

  // Ajuste para começar na Segunda-feira correta da grelha
  const startOffset = (monthStart.getDay() + 6) % 7;
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - startOffset);
  
  return Array.from({ length: 35 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return {
      date: day,
      value: formatCalendarDate(day),
      inMonth: day.getMonth() === baseDate.getMonth(),
      isSelected: dateFilterPreset === 'custom' && dateFilter === formatCalendarDate(day),
      isToday: formatCalendarDate(day) === formatCalendarDate(new Date())
    };
  });
}, [currentCalendarMonth, dateFilter, dateFilterPreset]);

    const filteredCases = useMemo(() => {
      return scopedCases.filter((item) => {
        if (!matchesAlertTypeFilter(item, alertTypeFilter)) return false;
        if (statusFilter !== 'all' && item.validationStatus !== statusFilter) return false;
        if (severityFilter !== 'all' && item.severity !== severityFilter) return false;
        if (quickFilter === 'pending' && item.validationStatus !== 'pending') return false;
        if (quickFilter === 'high_priority' && item.severity !== 'critical' && item.severity !== 'high') return false;
        if (quickFilter === 'low_confidence' && (item.confidence === null || item.confidence >= 75)) return false;
        if (quickFilter === 'confirmed' && item.validationStatus !== 'confirmed') return false;
        if (textFilter) {
          const query = textFilter.toLowerCase();
          const corpus = `${item.alertType ?? ''} ${item.suspectedPathology} ${item.notes} ${item.site} ${item.tank} ${item.species ?? ''} ${item.phase ?? ''}`.toLowerCase();
          if (!corpus.includes(query)) return false;
        }
        if (dateFilterPreset !== 'all') {
          const itemDate = new Date(item.capturedAt);
          if (Number.isNaN(itemDate.getTime())) return false;

          const now = new Date();
          if (dateFilterPreset === 'today') {
            if (itemDate < startOfLocalDay(now) || itemDate > endOfLocalDay(now)) return false;
          } else if (dateFilterPreset === 'week') {
            if (itemDate < startOfLocalWeek(now) || itemDate > endOfLocalWeek(now)) return false;
          } else if (dateFilterPreset === 'month') {
            if (itemDate < startOfLocalMonth(now) || itemDate > endOfLocalMonth(now)) return false;
          } else if (dateFilterPreset === 'custom') {
            if (!dateFilter) return false;
            const itemDateValue = toLocalDateInputValue(item.capturedAt);
            if (!itemDateValue || itemDateValue !== dateFilter) return false;
          }
        }
        return true;
      });
    }, [alertTypeFilter, dateFilter, dateFilterPreset, quickFilter, scopedCases, severityFilter, statusFilter, textFilter]);
    const caseMatchesSelectedAlert = (item: (typeof cases)[number]) =>
      Boolean(
        selectedAlertFromEmail &&
        [
          item.id,
          item.rawAlert?.id,
          item.rawAlert?.case_id,
          item.rawAlert?.alert_id
        ].some((value) => value && String(value) === selectedAlertFromEmail)
      );

    const sortedFilteredCases = useMemo(() => {
      const dateMs = (item: (typeof filteredCases)[number]) => {
        const value = new Date(item.capturedAt).getTime();
        return Number.isFinite(value) ? value : 0;
      };

      return [...filteredCases].sort((a, b) => {
        if (alertSortMode === 'oldest') return dateMs(a) - dateMs(b);
        if (alertSortMode === 'severity') {
          const severityDelta = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
          return severityDelta || dateMs(b) - dateMs(a);
        }
        if (alertSortMode === 'confidence') {
          const confidenceDelta = (a.confidence ?? 101) - (b.confidence ?? 101);
          return confidenceDelta || dateMs(b) - dateMs(a);
        }
        if (alertSortMode === 'tank') {
          const tankDelta = String(a.tank ?? '').localeCompare(String(b.tank ?? ''), 'pt');
          return tankDelta || dateMs(b) - dateMs(a);
        }
        return dateMs(b) - dateMs(a);
      });
    }, [alertSortMode, filteredCases]);

    useEffect(() => {
      if (!sortedFilteredCases.length) {
        setSelectedClinicalCaseId(null);
        return;
      }
      if (!selectedClinicalCaseId || !sortedFilteredCases.some((item) => item.id === selectedClinicalCaseId)) {
        setSelectedClinicalCaseId(sortedFilteredCases[0].id);
      }
    }, [selectedClinicalCaseId, sortedFilteredCases]);

    const selectedClinicalCase = useMemo(
      () => sortedFilteredCases.find((item) => item.id === selectedClinicalCaseId) ?? sortedFilteredCases[0] ?? null,
      [selectedClinicalCaseId, sortedFilteredCases]
    );
    const selectableBulkCases = useMemo(
      () => sortedFilteredCases.filter((item) => item.validationStatus === 'pending'),
      [sortedFilteredCases]
    );
    const bulkSelectedCases = useMemo(
      () => selectableBulkCases.filter((item) => bulkSelectedCaseIds.has(item.id)),
      [bulkSelectedCaseIds, selectableBulkCases]
    );
    const UUID_PATTERN_LOCAL = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const pickUuidLocal = (...values: unknown[]) =>
      values.map((value) => String(value ?? '').trim()).find((value) => UUID_PATTERN_LOCAL.test(value)) ?? null;
    const bulkValidationTargets = useMemo<BulkValidationTarget[]>(() =>
      bulkSelectedCases.map((item) => {
        const raw = item.rawAlert ?? {};
        const caseId = pickUuidLocal(raw.case_id, raw.id);
        const alertId = pickUuidLocal(raw.alert_id, raw.id);
        const sensorReadingId = pickUuidLocal(
          raw.metadata?.reading_id,
          raw.metadata?.sensor_reading_id,
          raw.metadata?.readingId
        );
        const detectionId = pickUuidLocal(raw.detection_id, raw.metadata?.detection_id, raw.metadata?.detectionId);
        const captureId = pickUuidLocal(raw.capture_id, raw.metadata?.capture_id, raw.metadata?.captureId);
        return {
          id: item.id,
          case_id: caseId,
          alert_id: alertId,
          detection_id: detectionId,
          capture_id: captureId,
          sensor_reading_id: caseId ? null : sensorReadingId,
          kit_id: raw.kit_id ?? raw.metadata?.kit_id ?? raw.metadata?.kitId ?? null
        };
      }),
      [bulkSelectedCases]
    );
    const toggleBulkCase = (caseId: string) => {
      setBulkSelectedCaseIds((current) => {
        const next = new Set(current);
        if (next.has(caseId)) next.delete(caseId);
        else next.add(caseId);
        return next;
      });
    };
    const clearBulkSelection = () => setBulkSelectedCaseIds(new Set());
    const selectVisiblePendingCases = () => {
      setBulkSelectedCaseIds(new Set(selectableBulkCases.map((item) => item.id)));
      if (selectableBulkCases.length > 0) setBulkValidationOpen(true);
    };
    useEffect(() => {
      setBulkSelectedCaseIds((current) => {
        if (!current.size) return current;
        const visibleIds = new Set(selectableBulkCases.map((item) => item.id));
        const next = new Set([...current].filter((id) => visibleIds.has(id)));
        return next.size === current.size ? current : next;
      });
    }, [selectableBulkCases]);
    const showClinicalDetailPanel = sortedFilteredCases.length > 0 && sortedFilteredCases.length <= 3;
    const selectedCaseSensorType = getAlertSensorType(selectedClinicalCase?.rawAlert) ?? '';
    const selectedCaseIsSensor = Boolean(selectedCaseSensorType);
    const selectedCaseSensorLabel =
      selectedCaseSensorType === 'temperature'
        ? 'Temperatura'
        : selectedCaseSensorType === 'ph'
          ? 'pH'
          : selectedCaseSensorType === 'dissolved_oxygen'
            ? 'Oxigénio dissolvido'
            : selectedCaseSensorType === 'redox'
              ? 'Redox / ORP'
              : 'Sensor';
    const selectedCaseSensorValue =
      selectedClinicalCase?.rawAlert.metadata?.sensor_value ??
      selectedClinicalCase?.rawAlert.metadata?.value ??
      selectedClinicalCase?.rawAlert.metadata?.calibrated_value ??
      selectedClinicalCase?.rawAlert.metadata?.raw_value ??
      null;
    const selectedCaseSensorDescription = String(
      [
        selectedClinicalCase?.rawAlert.metadata?.sensor_description,
        selectedClinicalCase?.rawAlert.metadata?.rule_message,
        selectedClinicalCase?.rawAlert.metadata?.message,
        selectedClinicalCase?.rawAlert.metadata?.description,
        selectedClinicalCase?.rawAlert.message,
        selectedClinicalCase?.rawAlert.title,
        selectedClinicalCase?.rawAlert.metadata?.sensor_name,
        selectedClinicalCase?.rawAlert.metadata?.sensor_label,
        selectedClinicalCase?.suspectedPathology
      ].find((value) => {
        if (value === null || value === undefined || value === '') return false;
        if (selectedCaseSensorType === 'temperature' && isTechnicalTemperatureAlias(value)) return false;
        return true;
      }) ?? selectedCaseSensorLabel
    ).trim();
    const selectedCaseRuleCondition = String(
      selectedClinicalCase?.rawAlert.metadata?.rule_condition ??
      selectedClinicalCase?.rawAlert.metadata?.condition ??
      ''
    ).trim();
    const selectedCaseRuleThreshold =
      selectedClinicalCase?.rawAlert.metadata?.rule_threshold ??
      selectedClinicalCase?.rawAlert.metadata?.target_value ??
      selectedClinicalCase?.rawAlert.metadata?.threshold ??
      null;
    const selectedCaseRuleLabel =
      selectedCaseRuleThreshold !== null && selectedCaseRuleThreshold !== undefined && selectedCaseRuleThreshold !== ''
        ? `${selectedCaseRuleCondition || 'limite'} ${selectedCaseRuleThreshold}${selectedClinicalCase?.rawAlert.metadata?.unit ? ` ${selectedClinicalCase.rawAlert.metadata.unit}` : ''}`
        : selectedCaseSensorDescription || 'Regra automática';

    useEffect(() => {
      window.localStorage.setItem('aql_vision_alert_view_mode', alertViewMode);
    }, [alertViewMode]);

    const visionAppIcon = <AqlVisionMark className="h-full w-full object-contain" />;

    const queueSummary = useMemo(() => ({
      all: scopedCases.length,
      visual: scopedCases.filter((item) => hasVisualEvidence(item.rawAlert) || Boolean(item.image)).length,
      sensor: scopedCases.filter((item) => isSensorAlertCase(item)).length,
      temperature: scopedCases.filter((item) => sensorTypeFromAlertCase(item) === 'temperature').length,
      ph: scopedCases.filter((item) => sensorTypeFromAlertCase(item) === 'ph').length,
      dissolvedOxygen: scopedCases.filter((item) => sensorTypeFromAlertCase(item) === 'dissolved_oxygen').length,
      redox: scopedCases.filter((item) => sensorTypeFromAlertCase(item) === 'redox').length,
      pending: scopedCases.filter((item) => item.validationStatus === 'pending').length,
      rejected: scopedCases.filter((item) => item.validationStatus === 'rejected').length,
      critical: scopedCases.filter((item) => item.severity === 'critical').length,
      high: scopedCases.filter((item) => item.severity === 'high').length,
      medium: scopedCases.filter((item) => item.severity === 'medium').length,
      low: scopedCases.filter((item) => item.severity === 'low').length,
      highPriority: scopedCases.filter((item) => item.severity === 'critical' || item.severity === 'high').length,
      lowConfidence: scopedCases.filter((item) => item.confidence !== null && item.confidence < 75).length,
      inconclusive: scopedCases.filter((item) => item.validationStatus === 'inconclusive').length,
      confirmed: scopedCases.filter((item) => item.validationStatus === 'confirmed').length
    }), [scopedCases]);

    const assistantPage = useMemo<AqlAssistantPage>(() => {
      if (currentView === 'clinical') return 'alerts';
      if (currentView === 'sensors') return 'dashboard';
      if (currentView === 'analytics') return 'analyses';
      if (currentView === 'growth') return 'growth';
      return 'dashboard';
    }, [currentView]);

    const assistantTimeWindow = useMemo<AqlAssistantTimeWindow>(() => {
      if (assistantPage === 'dashboard') return '24h';
      if (assistantPage === 'alerts') return '7d';
      return '30d';
    }, [assistantPage]);

    const activeCriticalAlertCount = useMemo(
      () => scopedCases.filter((item) => item.validationStatus === 'pending' && item.severity === 'critical').length,
      [scopedCases]
    );

    const assistantSummary = useMemo<AqlAssistantLocalSummary>(() => ({
      activeAlerts: queueSummary.pending,
      activeCriticalAlerts: activeCriticalAlertCount,
      deviceOfflineCount: dashboardSummary?.device?.online === false ? 1 : 0,
      sensorOutOfRangeCount: dashboardSummary?.sensors?.status && !/^ok$/i.test(String(dashboardSummary.sensors.status)) ? 1 : 0,
      lowMeasurementConfidence: queueSummary.lowConfidence > 0,
      recentPathologyCases: scopedCases.filter((item) => item.validationStatus !== 'rejected').length
    }), [activeCriticalAlertCount, dashboardSummary, queueSummary.lowConfidence, queueSummary.pending, scopedCases]);

    const assistantContextValue = useMemo(() => {
      const firstScopedCase = scopedCases[0] ?? null;
      const firstRawAlert = firstScopedCase?.rawAlert ?? null;
      const speciesLabel = species ?? client?.species_focus ?? null;
      const phaseLabel = phase ?? client?.default_phase ?? null;
      const speciesId = speciesLabel ? databaseScopeOptions.speciesIdsByLabel[scopeLabelKey(speciesLabel)] ?? null : null;
      const phaseId = phaseLabel ? databaseScopeOptions.phaseIdsByLabel[scopeLabelKey(phaseLabel)] ?? null : null;
      const locationId = site
        ? databaseScopeOptions.siteIdsByLabel[scopeLabelKey(site)] ?? firstRawAlert?.site_id ?? firstRawAlert?.metadata?.location_id ?? null
        : null;
      const zoneId = tank
        ? databaseScopeOptions.tankIdsByLabel[scopeLabelKey(tank)] ?? firstRawAlert?.tank_id ?? firstRawAlert?.metadata?.zone_id ?? dashboardSummary?.zoneId ?? null
        : dashboardSummary?.zoneId ?? null;
      const kitId = tank
        ? databaseScopeOptions.kitIdsByTankLabel[scopeLabelKey(tank)] ?? dashboardSummary?.kitId ?? firstRawAlert?.kit_id ?? firstRawAlert?.metadata?.kit_id ?? null
        : dashboardSummary?.kitId ?? firstRawAlert?.kit_id ?? firstRawAlert?.metadata?.kit_id ?? null;

      return {
        client_id: client?.id ?? null,
        project_id: routeProjectId,
        location_id: locationId,
        zone_id: zoneId,
        kit_id: kitId,
        species_id: speciesId,
        phase_id: phaseId,
        current_page: assistantPage,
        time_window: assistantTimeWindow,
        client_name: client?.name ?? null,
        species_label: speciesLabel,
        phase_label: phaseLabel,
        location_label: site,
        zone_label: tank,
        kit_label: dashboardSummary?.kitName ?? kitId
      };
    }, [assistantPage, assistantTimeWindow, client, dashboardSummary?.kitId, dashboardSummary?.kitName, dashboardSummary?.zoneId, databaseScopeOptions, phase, routeProjectId, scopedCases, site, species, tank]);

    const handleLogout = () => {
      if (demoMode) {
        onBack();
        return;
      }

      void supabase.auth.signOut().catch(() => undefined);
      localStorage.removeItem('visionAlertsToken');
      localStorage.removeItem('visionAlertsEmail');
      localStorage.removeItem('aql_auth_token');
      localStorage.removeItem('aql_user_email');
      if (!routeClientSlug) {
        localStorage.removeItem('aql_login_client_confirmed_vision-alerts');
      }
      setClientAdminModeOpen(false);
      setAuthRecoveryChecked(true);
      setAuthToken(null);
      setUserEmail(null);
      if (routeClientSlug) {
        navigate(`/${routeClientSlug}`);
      }
    };

    const handleHeaderBack = () => {
      if (!demoMode && routeClientSlug && routeProjectSlug) {
        navigate(`/${routeClientSlug}`);
        return;
      }
      onBack();
    };

    const navigateToView = (view: VisionView) => {
      setCurrentView(view);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
      if (currentView !== 'settings') lastMainViewRef.current = currentView;
    }, [currentView]);

    const updateCookiePreferences = (nextPreferences: CookiePreferencesState) => {
      saveCookiePreferences(nextPreferences);
      setCookiePreferences(nextPreferences);
    };

    const acceptAllCookies = () => {
      updateCookiePreferences({
        necessary: true,
        preferences: true,
        analytics: true,
        consentedAt: new Date().toISOString(),
        version: 1
      });
      setShowCookieSettings(false);
    };

    const rejectOptionalCookies = () => {
      updateCookiePreferences({
        necessary: true,
        preferences: false,
        analytics: false,
        consentedAt: new Date().toISOString(),
        version: 1
      });
      setShowCookieSettings(false);
    };

    useEffect(() => {
      const syncPrivacyFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        setShowPrivacyPolicy(urlParams.get('page') === 'privacy');
      };
      syncPrivacyFromUrl();
      window.addEventListener('popstate', syncPrivacyFromUrl);
      return () => window.removeEventListener('popstate', syncPrivacyFromUrl);
    }, []);

    const checkDeepLink = async () => {
      if (demoMode) {
        setIsProcessingDeepLink(false);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const viewerToken = urlParams.get('viewerToken');
      const alertId = urlParams.get('alertId');

      if (!viewerToken || !alertId) {
        setIsProcessingDeepLink(false);
        return;
      }

      setIsProcessingDeepLink(true);
      try {
        const res = await fetch(`${apiUrl}/vision-viewer-token/verify`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ viewerToken, alertId })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.valid) throw new Error('invalid');
        localStorage.setItem('visionAlertsToken', viewerToken);
        localStorage.setItem('visionAlertsEmail', 'viewer@aqlvision.pt');
        setAuthToken(viewerToken);
        setUserEmail('viewer@aqlvision.pt');
        setSelectedAlertFromEmail(alertId);
        navigateToView('clinical');
        toast.success('Acesso direto ao alerta ativo.');
      } catch (_error) {
        toast.error('Invalid or expired direct access link.');
      } finally {
        setIsProcessingDeepLink(false);
      }
    };

    useEffect(() => {
      checkDeepLink();
    }, [apiUrl, demoMode]);

    useEffect(() => {
      if (!selectedAlertFromEmail || !scopedCases.length) return;
      const timer = window.setTimeout(() => setSelectedAlertFromEmail(null), 1200);
      return () => window.clearTimeout(timer);
    }, [scopedCases.length, selectedAlertFromEmail]);

    const fetchAlerts = async (isPolling = false) => {
      if (demoMode) {
        const demoAlerts = getDemoVisionAlerts();
        setAlerts(demoAlerts);
        setDashboardSummary(getDemoDashboardSummary(demoAlerts));
        setLoading(false);
        return;
      }

      if (routeProjectSlug && routeScopeLoading) {
        if (!isPolling) setLoading(true);
        return;
      }

      if (routeProjectSlug && !routeProjectId) {
        if (!isPolling) {
          setAlerts([]);
          setDashboardSummary(null);
          setLoading(false);
        }
        return;
      }

      const requestScopeKey = currentDataScopeKeyRef.current;
      if (!isPolling) setLoading(true);
      try {
        let nextAlerts: RawVisionAlert[] = [];
        const mvpData = await loadMvpVisionData({
          clientId: client?.id ?? null,
          projectId: routeProjectId,
          species,
          phase,
          site,
          tank
        });

        nextAlerts = mvpData.alerts;

        if (nextAlerts.length === 0) {
          const fallbackParams = new URLSearchParams();
          if (client?.id) fallbackParams.set('client_id', client.id);
          if (routeProjectId) fallbackParams.set('project_id', routeProjectId);
          if (tank) {
            const tankZoneId = databaseScopeOptions.tankIdsByLabel[scopeLabelKey(tank)];
            if (tankZoneId) fallbackParams.set('zone_id', tankZoneId);
          }

          const fallbackResponse = await fetch(`${apiUrl}/vision-alerts?${fallbackParams.toString()}`, {
            headers: {
              Authorization: `Bearer ${publicAnonKey}`,
              'X-Session-Token': authToken ?? ''
            }
          });

          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json().catch(() => ({}));
            const fallbackAlerts = Array.isArray(fallbackData.alerts) ? fallbackData.alerts as RawVisionAlert[] : [];
            if (fallbackAlerts.length > 0) {
              nextAlerts = fallbackAlerts;
            }
          }
        }

        if (requestScopeKey !== currentDataScopeKeyRef.current) return;

        setAlerts(nextAlerts);
        setDashboardSummary(mvpData.summary);

        const previouslyKnownAlertIds = knownOperationalAlertIdsRef.current;
        const nextAlertIds = nextAlerts.map(alertOperationalId).filter(Boolean);
        const newlyDiscoveredAlertIds = new Set(
          nextAlertIds.filter((alertId) => !previouslyKnownAlertIds.has(alertId))
        );

        const newestAlert = nextAlerts[0];
        const newestAlertId = newestAlert ? alertOperationalId(newestAlert) : '';
        if (newestAlertId) {
          const alertTitle = String(newestAlert.problemName ?? newestAlert.problemDescription ?? 'Novo alerta operacional');
          const alertValue = [
            newestAlert.metadata?.sensor_value,
            newestAlert.metadata?.value,
            newestAlert.metadata?.calibrated_value,
            newestAlert.metadata?.raw_value,
            newestAlert.metadata?.reading_value,
            newestAlert.metadata?.last_value
          ].map(formatAlertNumber).find(Boolean);
          const alertValueLabel = alertValue ? `${alertValue}${formatAlertUnit(newestAlert.metadata?.unit)}` : null;
          const alertLocation = readableAlertLabel(
            newestAlert.metadata?.tank,
            newestAlert.metadata?.tankName,
            newestAlert.metadata?.zone_label,
            newestAlert.metadata?.zone_name,
            newestAlert.metadata?.location,
            tank
          );
          const alertSeverityKey = String(newestAlert.severity ?? '').trim();
          const alertSeverity = SEVERITY_LABELS[alertSeverityKey] ?? alertSeverityKey;
          const alertBody = [
            alertSeverity ? `Severidade: ${alertSeverity}` : null,
            alertLocation ? `Tanque: ${alertLocation}` : null
          ].filter(Boolean).join(' · ') || 'Novo evento requer validação operacional.';
          const alertToastSignature = [
            newestAlertId,
            alertValueLabel,
            alertTitle,
            alertSeverity,
            alertLocation
          ].join('|');

          const isGenuinelyNewAlert = newlyDiscoveredAlertIds.has(newestAlertId);
          const isPendingOperationalAlert = !alertHasFinalValidation(newestAlert);

          if (
            !isFirstLoadRef.current &&
            isGenuinelyNewAlert &&
            isPendingOperationalAlert &&
            alertToastSignature !== latestAlertToastSignatureRef.current
          ) {
            setActiveOperationalAlertToast({
              signature: alertToastSignature,
              valueLabel: alertValueLabel,
              title: alertTitle,
              body: alertBody
            });
            setActiveOperationalAlertClosing(false);
            if (activeOperationalAlertCloseTimeoutRef.current !== null) {
              window.clearTimeout(activeOperationalAlertCloseTimeoutRef.current);
              activeOperationalAlertCloseTimeoutRef.current = null;
            }
            playOperationalAlertBuzzer();

            try {
              showNotification('AQL Vision - Novo Alerta', {
                body: `${alertValueLabel ? `${alertValueLabel} · ${alertTitle}` : alertTitle}${alertBody ? ` · ${alertBody}` : ''}`,
                icon: '/favicon.ico'
              });
            } catch (err) {
              console.error('Failed to show native notification:', err);
            }

            try {
              const savedEmails = localStorage.getItem('visionAlertsNotificationEmails');
              const emails = savedEmails ? JSON.parse(savedEmails) : [];
              if (Array.isArray(emails) && emails.length > 0) {
                Promise.all(
                  emails.map((email) =>
                    fetch(`${apiUrl}/vision-notification-dispatch`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${publicAnonKey}`
                      },
                      body: JSON.stringify({
                        event: 'alert_created',
                        emails: [email],
                        channels: ['email'],
                        title: 'AQL Vision - Novo Alerta',
                        body: 'Uma nova anomalia foi detetada no sistema.',
                        url: window.location.pathname
                      })
                    })
                  )
                ).catch(console.error);
              }
            } catch (err) {
              console.error('Error sending alert email', err);
            }
          }

          latestAlertToastSignatureRef.current = alertToastSignature;
          latestAlertIdRef.current = newestAlertId;
        }

        knownOperationalAlertIdsRef.current = new Set([...previouslyKnownAlertIds, ...nextAlertIds]);
        isFirstLoadRef.current = false;
      } catch (_error) {
        if (requestScopeKey === currentDataScopeKeyRef.current) {
          toast.error('Falha ao atualizar alertas.');
        }
      } finally {
        if (!isPolling && requestScopeKey === currentDataScopeKeyRef.current) setLoading(false);
      }
    };

    useEffect(() => {
      if (demoMode) {
        fetchAlerts();
        return;
      }

      if (shouldShowClientProjectGate) {
        setLoading(false);
        return;
      }

      if (routeProjectSlug && routeScopeLoading) {
        setLoading(true);
        return;
      }

      if (routeProjectSlug && !routeProjectId) {
        setAlerts([]);
        setDashboardSummary(null);
        setLoading(false);
        return;
      }

      fetchAlerts();
      const pollInterval = window.setInterval(() => fetchAlerts(true), 5000);
      const channel = supabase
        .channel('vision-alerts-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: KV_STORE_TABLE }, (payload: any) => {
          if (payload.new?.key?.startsWith('aql:kit:')) fetchAlerts();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'aql_vision_captures' }, () => fetchAlerts(true))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'aql_alerts' }, () => fetchAlerts(true))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'aql_cases' }, () => fetchAlerts(true))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'aql_sensor_readings' }, () => fetchAlerts(true))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'aql_devices' }, () => fetchAlerts(true))
        .subscribe();

      return () => {
        window.clearInterval(pollInterval);
        supabase.removeChannel(channel);
      };
    }, [authToken, client?.id, demoMode, phase, routeProjectId, routeProjectSlug, routeScopeLoading, shouldShowClientProjectGate, site, species, tank]);

    const unitsNavLabel = language === 'en'
      ? 'Units'
      : 'Unidades';
    const unitsNavMobileLabel = language === 'en'
      ? 'Units'
      : 'Unid.';
    const unitsNavTitle = language === 'en'
      ? 'Real-time monitoring of cameras, sensors and devices.'
      : language === 'es'
        ? 'Monitorización en tiempo real de cámaras, sensores y dispositivos.'
        : 'Monitorização em tempo real de câmaras, sensores e dispositivos.';

    const baseSectionItems: SectionItem[] = demoMode
      ? [
          { id: 'operations', label: t('vision', 'dashboard', language), icon: LayoutDashboard },
          { id: 'clinical', label: t('vision', 'alerts', language), icon: AlertTriangle },
          { id: 'sensors', label: t('vision', 'sensors', language), icon: Droplets },
          { id: 'growth', label: t('vision', 'growth', language), icon: TrendingUp },
          { id: 'live', label: unitsNavLabel, icon: Cpu, title: unitsNavTitle, mobileLabel: unitsNavMobileLabel },
          { id: 'analytics', label: t('vision', 'analytics', language), icon: Activity }
        ]
      : [
          { id: 'operations', label: t('vision', 'dashboard', language), icon: LayoutDashboard },
          { id: 'clinical', label: t('vision', 'alerts', language), icon: AlertTriangle },
          { id: 'sensors', label: t('vision', 'sensors', language), icon: Droplets },
          { id: 'growth', label: t('vision', 'growth', language), icon: TrendingUp },
          { id: 'live', label: unitsNavLabel, icon: Cpu, title: unitsNavTitle, mobileLabel: unitsNavMobileLabel },
          { id: 'analytics', label: t('vision', 'analytics', language), icon: Activity },
        ];
    const sectionItems = orderSectionItems(baseSectionItems, menuOrder, hiddenMenuItems);

    useEffect(() => {
      const element = sectionNavRef.current;
      if (!element) return;

      const updateScrollState = () => {
        const maxScrollLeft = element.scrollWidth - element.clientWidth;
        setSectionNavCanScrollLeft(element.scrollLeft > 8);
        setSectionNavCanScrollRight(maxScrollLeft - element.scrollLeft > 8);
      };

      updateScrollState();
      element.addEventListener('scroll', updateScrollState, { passive: true });
      window.addEventListener('resize', updateScrollState);

      return () => {
        element.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
      };
    }, [sectionItems.length]);

    useEffect(() => {
      const activeButton = sectionNavRef.current?.querySelector<HTMLButtonElement>(`[data-nav-id="${currentView}"]`);
      activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, [currentView]);

    const scrollSectionNav = (direction: 'left' | 'right') => {
      const element = sectionNavRef.current;
      if (!element) return;
      const amount = Math.max(220, Math.floor(element.clientWidth * 0.7));
      element.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    const sectionNav = (
      <div className="flex min-w-0 items-center gap-2">
        <div className="hidden shrink-0 xl:flex">
          <button
            type="button"
            onClick={() => scrollSectionNav('left')}
            disabled={!sectionNavCanScrollLeft}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background/75 text-muted-foreground transition-all hover:border-border hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-35"
            aria-label="Scroll sections left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
        <div className="relative min-w-0 flex-1 overflow-hidden rounded-[22px] border border-slate-200/55 bg-white/45 px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.62),0_8px_24px_rgba(15,23,42,0.035)] backdrop-blur-md dark:border-white/[0.06] dark:bg-slate-950/25 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-card via-card/92 to-transparent opacity-100 transition-opacity" style={{ opacity: sectionNavCanScrollLeft ? 1 : 0 }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-card via-card/92 to-transparent opacity-100 transition-opacity" style={{ opacity: sectionNavCanScrollRight ? 1 : 0 }} />
          <div
            ref={sectionNavRef}
            className="scrollbar-none flex min-w-0 items-center gap-2 overflow-x-auto scroll-smooth px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
          {sectionItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            const showAlertPulse = item.id === 'clinical' && queueSummary.pending > 0;
            const horizontalNavClasses = `group relative z-0 inline-flex shrink-0 items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${
                  isActive
                    ? 'border-sky-300/90 bg-sky-400 text-slate-950 shadow-[0_10px_24px_-16px_rgba(56,189,248,0.85)] dark:border-sky-300/80 dark:bg-sky-500 dark:text-white dark:shadow-[0_10px_24px_-16px_rgba(14,165,233,0.6)]'
                    : 'border-border/50 bg-card/40 text-foreground/70 hover:border-border/80 hover:bg-card/70 hover:text-foreground dark:border-border/40 dark:bg-card/25 dark:text-foreground/65 dark:hover:border-border/70 dark:hover:bg-card/50 dark:hover:text-foreground'
                }`;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => navigateToView(item.id)}
                data-nav-id={item.id}
                data-demo-tour={`nav-${item.id}`}
                title={item.title ?? item.label}
                aria-label={item.title ?? item.label}
                className={horizontalNavClasses}
              >
                {showAlertPulse ? (
                  <span
                    className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center"
                    aria-label={`${queueSummary.pending} alertas pendentes de validação`}
                    title={`${queueSummary.pending} alertas pendentes de validação`}
                  >
                    <span className="relative inline-flex h-2.5 w-2.5 animate-pulse rounded-full border border-white/80 bg-red-500 dark:border-slate-950" />
                  </span>
                ) : null}
                <Icon className={`h-4 w-4 transition-opacity ${isActive ? 'opacity-95' : 'opacity-60 group-hover:opacity-80'}`} />
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            );
          })}
          </div>
        </div>
        <div className="hidden shrink-0 xl:flex">
          <button
            type="button"
            onClick={() => scrollSectionNav('right')}
            disabled={!sectionNavCanScrollRight}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background/75 text-muted-foreground transition-all hover:border-border hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-35"
            aria-label="Scroll sections right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );

    const mobileSectionNav = (
      <nav
        className="fixed inset-x-0 bottom-0 z-50 border-t border-border/70 bg-card/92 px-3 pb-[calc(env(safe-area-inset-bottom)+0.65rem)] pt-2 shadow-[0_-18px_48px_rgba(15,23,42,0.16)] backdrop-blur-2xl md:hidden"
        aria-label="Navegação principal AQL Vision"
      >
        <div className="mx-auto grid max-w-lg grid-cols-6 gap-1">
          {sectionItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            const showAlertPulse = item.id === 'clinical' && queueSummary.pending > 0;
            const mobileLabel = item.mobileLabel ?? (item.id === 'operations'
              ? 'Dash'
              : item.id === 'clinical'
                ? 'Alertas'
                : item.id === 'sensors'
                  ? 'Sens.'
                    : item.id === 'growth'
                      ? 'Cresc.'
                       : item.id === 'analytics'
                        ? 'Anál.'
                          : item.label);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => navigateToView(item.id)}
                title={item.title ?? item.label}
                className={`relative flex min-h-14 min-w-0 flex-col items-center justify-center gap-1.5 rounded-xl border px-2 text-[10px] font-semibold transition-all ${
                  isActive
                    ? 'border-sky-300/90 bg-sky-400 text-slate-950 shadow-[0_12px_24px_-16px_rgba(56,189,248,0.8)] dark:border-sky-300/80 dark:bg-sky-500 dark:text-white dark:shadow-[0_12px_24px_-16px_rgba(14,165,233,0.6)]'
                    : 'border-border/50 bg-card/40 text-foreground/70 hover:border-border/80 hover:bg-card/70 hover:text-foreground dark:border-border/40 dark:bg-card/25 dark:text-foreground/65 dark:hover:border-border/70 dark:hover:bg-card/50 dark:hover:text-foreground'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                {showAlertPulse ? (
                  <span
                    className="absolute right-1.5 top-1.5 flex h-3 w-3 items-center justify-center"
                    aria-label={`${queueSummary.pending} alertas pendentes de validação`}
                    title={`${queueSummary.pending} alertas pendentes de validação`}
                  >
                    <span className="relative inline-flex h-2.5 w-2.5 animate-pulse rounded-full border border-white/80 bg-red-500 dark:border-slate-950" />
                  </span>
                ) : null}
                <Icon className={`h-5 w-5 shrink-0 transition-opacity ${isActive ? 'opacity-95' : 'opacity-70'}`} />
                <span className="w-full truncate text-center leading-none">{mobileLabel}</span>
              </button>
            );
          })}
        </div>
      </nav>
    );

	    const sidebarToggle = (
	      <button
	        type="button"
	        onClick={() => setSidebarCollapsed((value) => !value)}
	        className={`aql-vision-sidebar-toggle group z-40 hidden h-16 shrink-0 border-b bg-card transition-[width,color,background-color] duration-200 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/45 md:flex md:items-center md:gap-3 ${
	          sidebarCollapsed ? 'w-[3.75rem] md:justify-center' : 'w-[15.5rem] px-3 md:justify-start'
	        }`}
	        style={{
	          borderColor: `${branding.primaryColor}20`
	        }}
	        aria-label={sidebarCollapsed ? 'Expandir menu lateral' : 'Colapsar menu lateral'}
	        data-collapsed={sidebarCollapsed}
	        title={sidebarCollapsed ? 'Expandir menu' : 'Colapsar menu'}
	      >
	        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors group-hover:bg-muted/55 group-hover:text-foreground">
	          {sidebarCollapsed ? <ChevronRight className="h-4.5 w-4.5" /> : <ChevronLeft className="h-4.5 w-4.5" />}
	        </span>
	        {!sidebarCollapsed && (
	          <span className="truncate text-sm font-bold text-muted-foreground transition-colors group-hover:text-foreground">Colapsar</span>
	        )}
	      </button>
	    );

    const sidebarNavButtonClasses = (isActive: boolean) => {
      return `w-full border-0 px-6 ${
        isActive
          ? 'bg-sky-500 text-white shadow-[inset_4px_0_0_rgba(255,255,255,0.72),0_12px_28px_-20px_rgba(14,165,233,0.9)] dark:bg-sky-500 dark:text-white dark:shadow-[inset_4px_0_0_rgba(255,255,255,0.72),0_12px_28px_-20px_rgba(14,165,233,0.72)]'
          : 'bg-transparent text-foreground/70 hover:bg-muted/55 hover:text-foreground dark:text-foreground/68 dark:hover:bg-white/[0.045] dark:hover:text-foreground'
      }`;
    };

    const sidebarNav = (
      <aside
        className={`aql-vision-sidebar sticky top-[7.5rem] hidden h-[calc(100dvh-7.5rem)] shrink-0 border-r border-border/70 bg-card shadow-[12px_0_34px_rgba(15,23,42,0.06)] transition-[width] duration-200 md:flex md:flex-col ${
          sidebarCollapsed ? 'w-[3.75rem]' : 'w-[15.5rem]'
        }`}
        aria-label="Navegação principal AQL Vision"
        data-collapsed={sidebarCollapsed}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3 py-3">

          <nav className="flex min-h-0 flex-1 flex-col gap-1">
            {sectionItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              const showAlertPulse = item.id === 'clinical' && queueSummary.pending > 0;
              return (
                <button
                  key={item.id}

                  type="button"
                  onClick={() => navigateToView(item.id)}
                  data-nav-id={item.id}
                  data-demo-tour={`nav-${item.id}`}
                  title={item.title ?? item.label}
                  aria-label={item.title ?? item.label}
                  aria-current={isActive ? 'page' : undefined}
                  className={`group relative flex min-h-12 items-center gap-3 text-sm font-semibold transition-all ${
                    sidebarCollapsed ? 'justify-center' : ''
                  } ${sidebarNavButtonClasses(isActive)}`}
                >
                  {showAlertPulse ? (
                    <span
                      className={`absolute flex h-3 w-3 items-center justify-center ${sidebarCollapsed ? 'right-2 top-2' : 'right-3 top-2.5'}`}
                      aria-label={`${queueSummary.pending} alertas pendentes de validação`}
                      title={`${queueSummary.pending} alertas pendentes de validação`}
                    >
                      <span className="relative inline-flex h-2.5 w-2.5 animate-pulse rounded-full border border-white/80 bg-red-500 dark:border-slate-950" />
                    </span>
                  ) : null}
                  <Icon className={`h-4.5 w-4.5 shrink-0 ${isActive ? 'opacity-95' : 'opacity-70 group-hover:opacity-90'}`} />
                  {!sidebarCollapsed ? <span className="min-w-0 flex-1 truncate text-left">{item.label}</span> : null}
                </button>
              );
            })}
          </nav>

          <div className="mt-auto border-t border-border/70 pt-3">
            <button
              type="button"
              onClick={() => navigateToView('settings')}
              title="Configuração"
              aria-label="Configuração"
              aria-current={currentView === 'settings' ? 'page' : undefined}
              className={`flex min-h-12 items-center gap-3 text-sm font-semibold transition-all ${
                sidebarCollapsed ? 'justify-center' : ''
              } ${sidebarNavButtonClasses(currentView === 'settings')}`}
            >
              <SettingsIcon className="h-4.5 w-4.5 shrink-0" />
              {!sidebarCollapsed ? <span className="min-w-0 flex-1 truncate text-left">Configuração</span> : null}
            </button>
          </div>
        </div>
      </aside>
    );

    const isRouteScoped = Boolean(routeClientSlug && routeProjectSlug);
    const filterTriggerClass = 'h-10 rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-800 shadow-sm dark:border-border dark:bg-muted/40 dark:text-foreground *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1';
    const filterSelectItemClass = 'py-2.5 pl-3 pr-10 text-sm [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1';
    const filterOptionLayoutClass = 'grid w-full min-w-0 grid-cols-[0.5rem_minmax(0,1fr)_2.75rem] items-center gap-3';
    const filterOptionWithIconLayoutClass = 'grid w-full min-w-0 grid-cols-[1rem_0.5rem_minmax(0,1fr)_2.75rem] items-center gap-3';
    const filterStateDotClass = 'h-2 w-2 rounded-full';
    const filterCountClass = 'inline-flex h-7 min-w-11 justify-center  px-2.5 text-center text-[11px] font-bold leading-7 tabular-nums text-slate-700  dark:text-slate-200';

    const clearFilters = () => {
      setTextFilter('');
      setAlertTypeFilter('all');
      setStatusFilter('all');
      setSeverityFilter('all');
      setQuickFilter('all');
      setDateFilter('');
      setDateFilterPreset('all');
    };
    if (!authToken || !userEmail) {
      if (isProcessingDeepLink || !authRecoveryChecked) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background">
            <AqlVisionLoader label={isProcessingDeepLink ? 'A validar acesso...' : 'A recuperar sessão...'} />
          </div>
        );
      }

      return (
        <UniversalSmartLogin
          apiUrl={apiUrl}
          publicAnonKey={publicAnonKey}
          appName="AQL Vision"
          appId="vision-alerts"
          appIcon={visionAppIcon}
          appGradient="from-orange-500 to-orange-600"
          onLoginSuccess={(token, email) => {
            setAuthToken(token);
            setUserEmail(email);
            localStorage.setItem('aql_auth_token', token);
            localStorage.setItem('aql_user_email', email);
            localStorage.setItem('visionAlertsToken', token);
            localStorage.setItem('visionAlertsEmail', email);
          }}
          onBack={onBack}
          requireClientSelection={!routeClientSlug}
        />
      );
    }

    if (!demoMode && authToken && userEmail && routeClientSlug && !routeProjectSlug && clientAdminModeOpen) {
      return (
        <div className="flex min-h-screen flex-col bg-background text-foreground">
	        <UniversalAppHeader
	          appName="AQL Vision"
	          appIcon={visionAppIcon}
            visionLabUrl="https://aqlapps.com/vision-lab"
            userEmail={`${routeClientName ?? client?.name ?? routeClientSlug} • Administração`}
            onSignOut={handleLogout}
            compactSignOut={true}
            compactHeader={true}
            onBack={() => setClientAdminModeOpen(false)}
            apiUrl={apiUrl}
            authToken={authToken}
            notificationUserEmail={userEmail}
            className="aql-vision-header"
            contentContainerClassName="w-full px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10"
            showClientBadge={false}
            showBrandBadge={false}
          />
          <main className="aql-adaptive-shell aql-config-shell flex min-h-0 flex-1 w-full p-0">
            <ConfigurationModule
              accessMode="client"
              clientSlug={routeClientSlug}
              projectSlug={null}
              onMenuOrderChange={(order) => setMenuOrder(normalizeMenuOrder(order))}
              onMenuVisibilityChange={(hiddenItems) => setHiddenMenuItems(normalizeHiddenMenuItems(hiddenItems))}
              portalAccess={{
                email: userEmail,
                authUserId: '',
                accessToken: authToken,
                role: clientProfileRole,
                clientId: client?.id ?? null,
                mode: 'client',
                clientSlug: routeClientSlug
              }}
            />
          </main>
        </div>
      );
    }

    if (shouldShowClientProjectGate && routeClientSlug) {
      return (
        <ClientProjectGate
          clientName={routeClientName ?? client?.name ?? routeClientSlug}
          clientSlug={routeClientSlug}
          userName={clientProfileName}
          userEmail={userEmail}
          projects={clientProjectChoices}
          loading={clientProjectChoicesLoading}
          onChooseProject={(project, area) => {
            if (area) {
              setSite(area.locationName ?? project.locationLabel ?? project.primaryInstallation ?? null);
              setTank(area.name ?? null);
              setSpecies(area.species ?? project.speciesFocus ?? null);
              setPhase((area.phase as Phase | null) ?? (project.defaultPhase as Phase | null) ?? null);
              setScopeLocked(true);
            } else {
              setSite(null);
              setTank(null);
              setSpecies(null);
              setPhase(null);
              setScopeLocked(false);
            }
            navigate(`/${routeClientSlug}/${projectChoiceSlug(project)}`);
          }}
          onOpenAdmin={() => setClientAdminModeOpen(true)}
          onLogout={handleLogout}
          onBack={onBack}
        />
      );
    }

    if (showPrivacyPolicy) {
      return (
        <>
          <PrivacyPolicy onBack={() => setShowPrivacyPolicy(false)} />
          <CookiePreferencesDialog
            isOpen={showCookieSettings}
            preferences={cookiePreferences ?? defaultCookiePreferences()}
            onClose={() => setShowCookieSettings(false)}
            onSave={updateCookiePreferences}
          />
        </>
      );
    }

    return (
      <div className="aql-vision-shell min-h-screen bg-background text-foreground">
        <UniversalAppHeader
          appName="AQL Vision"
          appIcon={visionAppIcon}
          visionLabUrl="https://aqlapps.com/vision-lab"
          userEmail={client ? `${client.name} • ${species ?? client.species_focus ?? 'All species'}` : 'Biological decision support'}
          onSignOut={handleLogout}
          compactSignOut={true}
          compactHeader={true}
          onBack={currentView === 'settings' ? () => navigateToView(lastMainViewRef.current) : handleHeaderBack}
          apiUrl={apiUrl}
          authToken={authToken}
          notificationUserEmail={userEmail}
          className="aql-vision-header"
          contentContainerClassName="w-full px-3"
          showClientBadge={false}
          showBrandBadge={false}
          onOpenSettings={() => navigateToView('settings')}
          onOpenAssistant={() => setAssistantOpen((value) => !value)}
	          assistantActive={assistantOpen}
	        />

	        {activeOperationalAlertToast ? (
	          <div className="pointer-events-none fixed inset-x-0 top-6 z-[2147483647] flex justify-center px-4">
	            <div
	              role="alertdialog"
	              aria-live="assertive"
	              aria-label="Novo alerta operacional"
	              className={`aql-alert-toast-panel ${activeOperationalAlertClosing ? 'aql-alert-toast-panel-out' : 'aql-alert-toast-panel-in'} pointer-events-auto relative block w-[min(92vw,760px)] overflow-hidden rounded-xl border border-white/20 bg-black px-8 py-6 text-left text-white shadow-[0_34px_100px_rgba(0,0,0,0.78)] ring-1 ring-red-400/35`}
	            >
	              <div className="relative z-10 flex items-start gap-4">
	                <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-red-400/45 bg-red-500/20 text-red-200">
	                  <AlertTriangle className="h-6 w-6" />
	                </span>
	                <button
	                  type="button"
	                  onClick={() => setCurrentView('clinical')}
	                  className="min-w-0 flex-1 text-left"
	                >
	                  <span className="block text-2xl font-black tracking-normal text-white">Novo alerta detetado</span>
	                  <span className="mt-2 block text-3xl font-black leading-8 text-white">
	                    {activeOperationalAlertToast.valueLabel ?? activeOperationalAlertToast.title}
	                  </span>
	                  {activeOperationalAlertToast.valueLabel ? (
	                    <span className="mt-1 block text-base font-bold leading-6 text-white/80">
	                      {activeOperationalAlertToast.title}
	                    </span>
	                  ) : null}
	                  <span className="mt-1 block text-base font-bold leading-6 text-white/70">
	                    {activeOperationalAlertToast.body}
	                  </span>
	                </button>
	                <button
	                  type="button"
	                  onClick={closeOperationalAlertToast}
	                  className="ml-auto mt-1 shrink-0 rounded-lg border border-red-300/60 bg-red-500/18 px-4 py-2 text-sm font-black uppercase tracking-[0.12em] text-red-50 transition-colors hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-200/80"
	                >
	                  OK
	                </button>
	              </div>
	            </div>
	          </div>
	        ) : null}

	        <div className="aql-vision-body flex min-h-[calc(100dvh-3.5rem)] w-full flex-col">
          {currentView !== 'settings' ? (
            <div className="aql-vision-scope-row sticky top-14 z-40 flex w-full bg-card">
              {sidebarToggle}
              <div className="flex-1">
                <GlobalScopeBar
                  speciesOptions={scopeOptions.species}
                  phaseOptions={scopeOptions.phases}
                  siteOptions={scopeOptions.sites}
                  tankOptions={scopeOptions.tanks}
                  locked={scopeLocked}
                />
              </div>
            </div>
          ) : (
            <div className="fixed left-0 top-14 z-40 hidden md:flex">
              {sidebarToggle}
            </div>
          )}

          <div className="flex min-h-0 flex-1 w-full">
            {sidebarNav}

            <main data-view={currentView} className={'aql-vision-main ' + (currentView === 'settings'
              ? 'w-full min-w-0 flex-1 pb-0 pt-0'
              : currentView === 'clinical'
                ? 'w-full min-w-0 flex-1 p-0'
                : 'w-full min-w-0 flex-1 px-4 pb-24 pt-5 sm:px-7 lg:px-8 md:pb-6 lg:pt-7 xl:px-10 2xl:px-12'
            )}>
          <style>
            {`
	              @keyframes aqlVisionFloatIn {
	                0% {
	                  opacity: 0;
	                  transform: translate3d(0, 18px, 0) scale(0.985);
	                  filter: blur(12px);
                }
                100% {
                  opacity: 1;
                  transform: translate3d(0, 0, 0) scale(1);
	                  filter: blur(0);
	                }
	              }

		              @keyframes aqlAlertToastPanelIn {
		                0% {
		                  opacity: 0;
		                  transform: translate3d(0, -34px, 0) scale(0.94);
		                  filter: blur(10px);
		                }
		                62% {
		                  opacity: 1;
		                  transform: translate3d(0, 4px, 0) scale(1.015);
		                  filter: blur(0);
		                }
		                100% {
		                  opacity: 1;
		                  transform: translate3d(0, 0, 0) scale(1);
		                  filter: blur(0);
		                }
		              }

			              @keyframes aqlAlertToastPanelOut {
			                0% {
			                  opacity: 1;
		                  transform: translate3d(0, 0, 0) scale(1);
		                  filter: blur(0);
		                }
		                100% {
		                  opacity: 0;
		                  transform: translate3d(0, -24px, 0) scale(0.96);
			                  filter: blur(8px);
			                }
			              }

		              .aql-alert-toast-panel-in {
		                animation: aqlAlertToastPanelIn 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
		                transform-origin: top center;
		              }

		              .aql-alert-toast-panel-out {
		                animation: aqlAlertToastPanelOut 240ms cubic-bezier(0.7, 0, 0.84, 0) both;
		                transform-origin: top center;
		              }

	              .aql-vision-page-in,
	              .aql-vision-panel-in,
	              .aql-vision-alert-grid > * {
	                animation: aqlVisionFloatIn 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
              }

	              .aql-vision-shell {
	                background:
	                  radial-gradient(circle at 18% 8%, rgba(58, 134, 255, 0.11), transparent 38%),
	                  radial-gradient(circle at 82% 86%, rgba(0, 200, 150, 0.08), transparent 42%),
	                  linear-gradient(180deg, #f7fbff 0%, #f3f8fc 48%, #f6faf8 100%);
	                background-attachment: fixed;
	              }

	              .aql-vision-header {
	                background: rgba(255, 255, 255, 0.95);
	                border-bottom-color: rgba(226, 232, 240, 0.9);
	                box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
	              }

		              .aql-vision-scope-bar,
		              .aql-vision-scope-bar > div {
		                background: #ffffff;
		                opacity: 1;
		                backdrop-filter: none;
		                -webkit-backdrop-filter: none;
		                border-color: rgba(58, 134, 255, 0.12);
		                box-shadow: 0 1px 0 rgba(58, 134, 255, 0.08);
		              }

		              .aql-vision-sidebar-toggle {
		                background:
		                  linear-gradient(180deg, #ffffff, #fbfdff);
		                opacity: 1;
		                backdrop-filter: none;
		                -webkit-backdrop-filter: none;
		                border-color: rgba(58, 134, 255, 0.12);
		                box-shadow: 0 1px 0 rgba(58, 134, 255, 0.08);
		              }

	              .aql-vision-header button:not([data-nav-id]) {
	                background-color: rgba(255, 255, 255, 0.24);
                border-color: rgba(58, 134, 255, 0.06);
                color: rgba(71, 85, 105, 0.86);
              }

              .aql-vision-header button:not([data-nav-id]):hover {
                background-color: rgba(255, 255, 255, 0.50);
                border-color: rgba(58, 134, 255, 0.14);
              }

	              .aql-vision-sidebar {
	                background:
	                  linear-gradient(180deg, #ffffff, #fbfdff);
	                border-right-color: rgba(58, 134, 255, 0.12);
	                backdrop-filter: none;
	                -webkit-backdrop-filter: none;
	              }

              .aql-vision-dashboard-card {
                background:
                  linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.82));
                backdrop-filter: blur(6px);
                border: 1px solid rgba(58, 134, 255, 0.11);
                box-shadow:
                  0 1px 2px rgba(15, 23, 42, 0.045),
                  0 16px 38px rgba(58, 134, 255, 0.065),
                  0 10px 24px rgba(15, 23, 42, 0.035);
              }

              .aql-vision-metric-row,
              .aql-vision-sensor-card {
                background: rgba(246, 249, 253, 0.86);
                border-color: rgba(58, 134, 255, 0.11);
                box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
              }


	              :root[data-theme='dark-industrial'] .aql-vision-shell {
	                background:
	                  radial-gradient(1200px 800px at 15% -10%, rgba(var(--aql-brand-primary-rgb), 0.10), transparent 55%),
	                  radial-gradient(900px 700px at 100% 0%, rgba(var(--aql-brand-secondary-rgb), 0.08), transparent 50%),
	                  var(--background);
	              }

              :root[data-theme='dark-industrial'] .aql-vision-header {
                background: #151f31;
                box-shadow: none;
              }

	              :root[data-theme='dark-industrial'] .aql-vision-sidebar {
	                background:
	                  linear-gradient(180deg, rgba(21, 31, 49, 0.94), rgba(13, 21, 36, 0.96));
	                border-right-color: rgba(148, 163, 184, 0.12);
	                box-shadow: 12px 0 34px rgba(2, 6, 23, 0.18);
	              }

		              :root[data-theme='dark-industrial'] .aql-vision-scope-bar,
		              :root[data-theme='dark-industrial'] .aql-vision-scope-bar > div {
		                background:
		                  linear-gradient(180deg, rgba(21, 31, 49, 0.96), rgba(15, 23, 42, 0.96));
		                border-color: rgba(148, 163, 184, 0.14);
		                box-shadow: 0 1px 0 rgba(148, 163, 184, 0.08);
		              }

		              :root[data-theme='dark-industrial'] .aql-vision-sidebar-toggle {
		                background:
		                  linear-gradient(180deg, rgba(21, 31, 49, 0.94), rgba(13, 21, 36, 0.96));
		                border-color: rgba(148, 163, 184, 0.12);
		                box-shadow: 0 1px 0 rgba(148, 163, 184, 0.08);
		              }

	              :root[data-theme='dark-industrial'] .aql-vision-scope-bar button,
	              :root[data-theme='dark-industrial'] .aql-vision-scope-bar [role='combobox'] {
	                background-color: rgba(30, 41, 59, 0.82);
	                color: rgba(241, 245, 249, 0.9);
	                border-color: rgba(148, 163, 184, 0.12);
	              }

	              :root[data-theme='dark-industrial'] .aql-vision-scope-bar svg,
	              :root[data-theme='dark-industrial'] .aql-vision-sidebar-toggle svg,
	              :root[data-theme='dark-industrial'] .aql-vision-sidebar-toggle span {
	                color: rgba(203, 213, 225, 0.82);
	              }

              :root[data-theme='dark-industrial'] .aql-vision-header button:not([data-nav-id]) {
                background-color: rgba(255, 255, 255, 0.018);
                border-color: rgba(148, 163, 184, 0.08);
                color: rgba(226, 232, 240, 0.72);
              }

              :root[data-theme='dark-industrial'] .aql-vision-header button:not([data-nav-id]):hover {
                background-color: rgba(56, 189, 248, 0.055);
                border-color: rgba(125, 211, 252, 0.16);
                color: rgba(248, 250, 252, 0.92);
              }

              :root[data-theme='dark-industrial'] .aql-vision-dashboard-card {
                background: var(--card);
                border-color: var(--border);
                box-shadow: 0 1px 2px rgba(2, 6, 23, 0.16);
              }

              :root[data-theme='dark-industrial'] .aql-vision-metric-row,
              :root[data-theme='dark-industrial'] .aql-vision-sensor-card {
                background: rgba(255, 255, 255, 0.03);
                border-color: var(--border);
              }

              .aql-vision-panel-in:nth-child(1) { animation-delay: 40ms; }
              .aql-vision-panel-in:nth-child(2) { animation-delay: 110ms; }
              .aql-vision-panel-in:nth-child(3) { animation-delay: 180ms; }
              .aql-vision-panel-in:nth-child(4) { animation-delay: 250ms; }

              .aql-vision-alert-grid > *:nth-child(1) { animation-delay: 80ms; }
              .aql-vision-alert-grid > *:nth-child(2) { animation-delay: 125ms; }
              .aql-vision-alert-grid > *:nth-child(3) { animation-delay: 170ms; }
              .aql-vision-alert-grid > *:nth-child(4) { animation-delay: 215ms; }
              .aql-vision-alert-grid > *:nth-child(n + 5) { animation-delay: 260ms; }

		              @media (prefers-reduced-motion: reduce) {
		                .aql-vision-page-in,
		                .aql-vision-panel-in,
		                .aql-vision-alert-grid > * {
	                  animation: none !important;
	                  transform: none !important;
	                  filter: none !important;
	                }

			                .aql-alert-toast-panel-in,
			                .aql-alert-toast-panel-out {
			                  animation: none !important;
			                }
		              }
            `}
          </style>

          {!loading && currentView === 'settings' && (
            <section key="settings" className="aql-vision-page-in flex min-h-[calc(100dvh-4.5rem)] flex-col overflow-hidden bg-background">
              <ConfigurationModule
                onMenuOrderChange={(order) => setMenuOrder(normalizeMenuOrder(order))}
                onMenuVisibilityChange={(hiddenItems) => setHiddenMenuItems(normalizeHiddenMenuItems(hiddenItems))}
              />
            </section>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <AqlVisionLoader
                label="A carregar dados do cliente..."
                detail="A preparar estado da unidade, capturas e alertas."
              />
            </div>
          ) : null}

          {!loading && currentView === 'operations' && (
            <div key="operations" className="aql-vision-page-in aql-vision-dashboard-page">
              <VisionDashboard
                cases={scopedCases}
                summary={dashboardSummary}
                sensorReadings={dashboardSummary?.sensorReadings ?? null}
                demoMode={demoMode}
                sensorScope={{
                  clientId: client?.id ?? null,
                  projectId: routeProjectId,
                  tankId: dashboardSummary?.zoneId ?? (tank ? databaseScopeOptions.tankIdsByLabel[scopeLabelKey(tank)] : null) ?? null
                }}
                onOpenAlerts={() => navigateToView('clinical')}
                onOpenLatestCapture={(alertId) => {
                  setSelectedAlertFromEmail(alertId ?? null);
                  navigateToView('clinical');
                }}
                onOpenLiveCamera={() => navigateToView('live')}
              />
            </div>
          )}

	          {!loading && currentView === 'live' && (
	            <div key="live" className="aql-vision-page-in">
	              <VisionDemo
                embedded
                onBack={() => navigateToView('operations')}
                onOpenAlerts={(alertId) => {
                  setSelectedAlertFromEmail(alertId ?? null);
                  navigateToView('clinical');
                }}
                initialAlertId={selectedAlertFromEmail}
                demoMode={demoMode}
                clientId={client?.id ?? null}
                projectId={routeProjectId}
                zoneId={dashboardSummary?.zoneId ?? (tank ? databaseScopeOptions.tankIdsByLabel[scopeLabelKey(tank)] : null) ?? null}
              />
	            </div>
	          )}

	          {bulkValidationOpen && bulkSelectedCases[0] ? (
	            <ClassifyAlertDialog
	              isOpen={bulkValidationOpen}
	              onClose={() => setBulkValidationOpen(false)}
	              alert={bulkSelectedCases[0].rawAlert as any}
	              apiUrl={apiUrl}
	              authToken={authToken ?? ''}
	              publicAnonKey={publicAnonKey}
	              bulkTargets={bulkValidationTargets}
	              bulkLabel={`${bulkSelectedCases.length} alertas selecionados`}
	              onUpdate={() => {
	                setBulkValidationOpen(false);
	                clearBulkSelection();
	                fetchAlerts();
	              }}
	            />
	          ) : null}

	          {!loading && currentView === 'analytics' && (
	            <div key="analytics" className="aql-vision-page-in">
              <VisionAnalytics alerts={scopedCases as any} />
            </div>
          )}

          {!loading && currentView === 'sensors' && (
            <div key="sensors" className="aql-vision-page-in">
              <VisionSensorsDashboard
                embedded
                demoMode={demoMode}
                cases={scopedCases}
                sensorReadings={dashboardSummary?.sensorReadings ?? null}
                scope={{
                  clientId: client?.id ?? null,
                  projectId: routeProjectId,
                  tankId: dashboardSummary?.zoneId ?? (tank ? databaseScopeOptions.tankIdsByLabel[scopeLabelKey(tank)] : null) ?? null
                }}
                deviceOnline={dashboardSummary?.device?.online ?? false}
              />
            </div>
          )}

          {!loading && currentView === 'growth' && (
            <div key="growth" className="aql-vision-page-in">
              <VisionGrowthMonitor
                embedded
                demoMode={demoMode}
                scope={{
                  clientId: client?.id ?? null,
                  projectId: routeProjectId,
                  species,
                  phase,
                  site,
                  tank
                }}
              />
            </div>
          )}

          {!loading && currentView === 'clinical' && (
            <>
            <div className="flex gap-0 min-h-screen">
              {/* LEFT SIDEBAR WITH FILTERS */}
              <aside className={`border-r border-border bg-card transition-all duration-300 ease-out flex flex-col ${
                isFilterSidebarExpanded ? 'w-72' : 'w-16'
              }`}>
                {/* SIDEBAR HEADER */}
                <div className="border-b border-border h-14 px-3 flex items-center justify-between">
                  <h3 className={`text-sm font-semibold uppercase tracking-widest text-foreground transition-all duration-300 ${
                    isFilterSidebarExpanded ? 'opacity-100' : 'opacity-0 hidden'
                  }`}>
                    Filtros
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsFilterSidebarExpanded(!isFilterSidebarExpanded)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={isFilterSidebarExpanded ? 'Colapsar filtros' : 'Expandir filtros'}
                  >
                    {isFilterSidebarExpanded ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>

                {/* SIDEBAR FILTERS CONTENT */}
                {isFilterSidebarExpanded && (
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {/* TIPO FILTER */}
                    <div className="space-y-2">
                      <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/85">Tipo</label>
                      <Select value={alertTypeFilter} onValueChange={(value) => setAlertTypeFilter(value as AlertTypeFilter)}>
                        <SelectTrigger className="h-9 w-full rounded-lg border-border bg-muted/30 text-sm !border-0">
                          <SelectValue placeholder="Selecionar tipo..." />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { value: 'all', label: 'Todos os tipos', count: queueSummary.all, icon: Activity, dot: 'bg-slate-400/70' },
                            { value: 'visual', label: 'Visão / imagem', count: queueSummary.visual, icon: Eye, dot: 'bg-cyan-300' },
                            { value: 'sensor', label: 'Todos os sensores', count: queueSummary.sensor, icon: Droplets, dot: 'bg-emerald-300' },
                            { value: 'temperature', label: 'Temperatura', count: queueSummary.temperature, icon: Thermometer, dot: 'bg-orange-300' },
                            { value: 'ph', label: 'pH', count: queueSummary.ph, icon: Droplets, dot: 'bg-sky-300' },
                            { value: 'dissolved_oxygen', label: 'Oxigénio dissolvido', count: queueSummary.dissolvedOxygen, icon: Waves, dot: 'bg-emerald-300' },
                            { value: 'redox', label: 'Redox / ORP', count: queueSummary.redox, icon: Zap, dot: 'bg-purple-300' }
                          ].map((option) => {
                            const Icon = option.icon;
                            return (
                              <SelectItem key={option.value} value={option.value} className={filterSelectItemClass}>
                                <span className={filterOptionWithIconLayoutClass}>
                                  <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                  <span className={`${filterStateDotClass} ${option.dot}`} />
                                  <span className="min-w-0 truncate">{option.label}</span>
                                  <span className={filterCountClass}>
                                    {option.count}
                                  </span>
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* ESTADO FILTER */}
                    <div className="space-y-2">
                      <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/85">Estado</label>
                      <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | ClinicalStatus)}>
                        <SelectTrigger className="h-9 w-full rounded-lg border-border bg-muted/30 text-sm !border-0">
                          <SelectValue placeholder="Selecionar estado..." />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { value: 'all', label: 'Todos os estados', count: scopedCases.length, dot: 'bg-slate-400/70' },
                            { value: 'pending', label: 'Pendente', count: queueSummary.pending, dot: 'bg-sky-300' },
                            { value: 'confirmed', label: 'Confirmado', count: queueSummary.confirmed, dot: 'bg-emerald-300' },
                            { value: 'rejected', label: 'Rejeitado', count: queueSummary.rejected, dot: 'bg-slate-400' },
                            { value: 'inconclusive', label: 'Inconclusivo', count: queueSummary.inconclusive, dot: 'bg-cyan-300' }
                          ].map((option) => (
                            <SelectItem key={option.value} value={option.value} className={filterSelectItemClass}>
                              <span className={filterOptionLayoutClass}>
                                <span className={`${filterStateDotClass} ${option.dot}`} />
                                <span className="min-w-0 truncate">{option.label}</span>
                                <span className={filterCountClass}>
                                  {option.count}
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* SEVERIDADE FILTER */}
                    <div className="space-y-2">
                      <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/85">Severidade</label>
                      <Select value={severityFilter} onValueChange={(value) => setSeverityFilter(value as SeverityFilter)}>
                        <SelectTrigger className="h-9 w-full rounded-lg border-border bg-muted/30 text-sm !border-0">
                          <SelectValue placeholder="Selecionar severidade..." />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { value: 'all', label: 'Todas as severidades', count: scopedCases.length, dot: 'bg-slate-400/70' },
                            { value: 'critical', label: 'Crítica', count: queueSummary.critical, dot: 'bg-red-300' },
                            { value: 'high', label: 'Alta', count: queueSummary.high, dot: 'bg-amber-300' },
                            { value: 'medium', label: 'Média', count: queueSummary.medium, dot: 'bg-cyan-300' },
                            { value: 'low', label: 'Baixa', count: queueSummary.low, dot: 'bg-emerald-300' }
                          ].map((option) => (
                            <SelectItem key={option.value} value={option.value} className={filterSelectItemClass}>
                              <span className={filterOptionLayoutClass}>
                                <span className={`${filterStateDotClass} ${option.dot}`} />
                                <span className="min-w-0 truncate">{option.label}</span>
                                <span className={filterCountClass}>
                                  {option.count}
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* PERÍODO FILTER */}
                    <div className="space-y-2">
                      <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/85">Período</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex h-9 w-full items-center gap-2 rounded-lg bg-muted/30 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground"
                          >
                            <CalendarIcon className="h-4 w-4 shrink-0" />
                            <span className="truncate text-xs font-medium">{dateFilterLabel}</span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" side="right" className="w-72 rounded-xl border-border bg-popover p-2 shadow-xl">
                          <div className="grid grid-cols-2 gap-1">
                            {[
                              { value: 'all', label: 'Todas' },
                              { value: 'today', label: 'Hoje' },
                              { value: 'week', label: 'Semana' },
                              { value: 'month', label: 'Mês' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  setDateFilterPreset(option.value as DateFilterPreset);
                                  if (option.value !== 'custom') setDateFilter('');
                                }}
                                className={`rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors ${
                                  dateFilterPreset === option.value
                                    ? 'bg-primary/15 text-foreground'
                                    : 'text-muted-foreground hover:bg-muted/45 hover:text-foreground'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>

                          <div className="mt-2 border-t border-border/70 pt-2">
                            <div className="mb-2 flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => setCurrentCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground"
                                aria-label="Mês anterior"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              <span className="text-xs font-semibold text-foreground">
                                {new Intl.DateTimeFormat('pt-PT', { month: 'long', year: 'numeric' }).format(currentCalendarMonth)}
                              </span>
                              <button
                                type="button"
                                onClick={() => setCurrentCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground"
                                aria-label="Mês seguinte"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-muted-foreground/70">
                              {['S', 'T', 'Q', 'Q', 'S', 'S', 'D'].map((day, index) => (
                                <span key={`${day}-${index}`}>{day}</span>
                              ))}
                            </div>
                            <div className="mt-1 grid grid-cols-7 gap-1">
                              {dateCalendarDays.map((day) => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => {
                                    setDateFilter(day.value);
                                    setDateFilterPreset('custom');
                                  }}
                                  className={`h-8 rounded-md text-xs transition-colors ${
                                    day.isSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : day.isToday
                                        ? 'bg-primary/12 text-foreground'
                                        : day.inMonth
                                          ? 'text-foreground hover:bg-muted/50'
                                          : 'text-muted-foreground/35 hover:bg-muted/30'
                                  }`}
                                >
                                  {day.date.getDate()}
                                </button>
                              ))}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* CLEAR BUTTON */}
                    {(textFilter || alertTypeFilter !== 'all' || statusFilter !== 'all' || severityFilter !== 'all' || quickFilter !== 'all' || dateFilter) && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-300/55 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/15"
                      >
                        <X className="h-3.5 w-3.5" />
                        Limpar Filtros
                      </button>
                    )}
                  </div>
                )}
              </aside>

              {/* MAIN CONTENT AREA */}
              <main className="flex-1 flex flex-col overflow-hidden">
                {/* TOP TOOLBAR */}
                <section className="border-b border-border bg-card px-4 h-14 flex items-center gap-2 flex-wrap lg:flex-nowrap">
                  {/* QUICK FILTERS BADGES */}
                  <button
                    type="button"
                    onClick={() => setSeverityFilter(severityFilter === 'critical' ? 'all' : 'critical')}
                    className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
                      severityFilter === 'critical'
                        ? 'border-red-300 bg-red-100 text-red-800 dark:border-red-300/60 dark:bg-red-500/20 dark:text-red-100'
                        : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-100/85 dark:hover:bg-red-500/15'
                    }`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Críticos <span className="tabular-nums">{queueSummary.critical}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
                    className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
                      statusFilter === 'pending'
                        ? 'border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-300/60 dark:bg-sky-500/20 dark:text-sky-50'
                        : 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 dark:border-sky-400/30 dark:bg-sky-500/10 dark:text-sky-100/85 dark:hover:bg-sky-500/15'
                    }`}
                  >
                    Pendentes <span className="tabular-nums">{queueSummary.pending}</span>
                  </button>
                  <span className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-3 text-xs font-semibold text-slate-700 dark:border-slate-400/25 dark:bg-slate-500/10 dark:text-slate-100/85">
                    Offline <span className="tabular-nums">{dashboardSummary?.device?.online === false ? 1 : 0}</span>
                  </span>

                  {/* SEARCH AND CONTROLS */}
                  <div className="ml-auto flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center lg:ml-auto lg:flex-1">
                    <div className="relative w-full sm:flex-1">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Search className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <input
                        type="text"
                        value={textFilter}
                        onChange={(e) => setTextFilter(e.target.value)}
                        placeholder="Pesquisar alerta, tanque, fase..."
                        className="block h-9 w-full rounded-xl !border-0 bg-muted/30 pl-10 pr-3 text-sm text-foreground backdrop-blur transition-all focus:border-primary focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>

                    <div className="relative w-full sm:w-52">
                      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
                        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Select value={alertSortMode} onValueChange={(value) => setAlertSortMode(value as AlertSortMode)}>
                        <SelectTrigger className="h-9 rounded-xl !border-0 bg-muted/30 pl-10 text-sm backdrop-blur">
                          <SelectValue placeholder="Ordenar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Mais recentes</SelectItem>
                          <SelectItem value="oldest">Mais antigos</SelectItem>
                          <SelectItem value="severity">Maior severidade</SelectItem>
                          <SelectItem value="confidence">Menor confiança</SelectItem>
                          <SelectItem value="tank">Tanque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="inline-flex shrink-0 rounded-xl !border-0 bg-muted/30 p-0.5 backdrop-blur">
                      <button
                        type="button"
                        onClick={() => setAlertViewMode('grid')}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                          alertViewMode === 'grid'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-card hover:text-foreground'
                        }`}
                        title="Vista em grelha"
                      >
                        <Grid2X2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setAlertViewMode('list')}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                          alertViewMode === 'list'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-card hover:text-foreground'
                        }`}
                        title="Vista em lista"
                      >
                        <List className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </section>

                {/* MAIN CONTENT - ALERTS LIST */}
                <div className="flex-1 overflow-y-auto p-4">
              <div className="min-w-0">
                {filteredCases.length === 0 ? (
                  <div className="aql-vision-panel-in rounded-2xl border-b border-border/40 bg-transparent py-20 text-center">
                    <h3 className="text-lg font-medium text-foreground">Nenhum alerta pendente.</h3>
                    <p className="mt-1 text-muted-foreground">Ainda não existem dados suficientes.</p>
                  </div>
	                ) : (
	                  <div className={showClinicalDetailPanel ? 'grid gap-3 2xl:grid-cols-[minmax(0,1fr)_380px]' : ''}>
	                    <div className={`${showClinicalDetailPanel ? '2xl:col-span-2' : ''} mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card/72 px-3 py-2 shadow-sm backdrop-blur`}>
	                      <div className="flex min-w-0 flex-wrap items-center gap-2">
	                        <span className="text-xs font-semibold text-muted-foreground">
	                          {bulkSelectedCases.length > 0
	                            ? `${bulkSelectedCases.length} alerta(s) selecionado(s)`
	                            : `${selectableBulkCases.length} pendente(s) disponível(is) para lote`}
	                        </span>
	                        {bulkSelectedCases.length > 0 ? (
	                          <button
	                            type="button"
	                            onClick={clearBulkSelection}
	                            className="rounded-lg px-2 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
	                          >
	                            Limpar seleção
	                          </button>
	                        ) : null}
	                      </div>
	                      <div className="flex flex-wrap items-center gap-2">
	                        <button
	                          type="button"
	                          onClick={() => setBulkValidationOpen(true)}
	                          disabled={bulkSelectedCases.length === 0}
	                          className="rounded-xl border border-cyan-300/60 bg-cyan-50 px-3 py-2 text-xs font-bold text-cyan-800 transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted/30 disabled:text-muted-foreground dark:border-cyan-300/35 dark:bg-cyan-500/12 dark:text-cyan-100 dark:hover:bg-cyan-500/18"
	                        >
	                          Validar em lote
	                        </button>
	                        <button
	                          type="button"
	                          onClick={selectVisiblePendingCases}
	                          disabled={selectableBulkCases.length === 0}
	                          className="rounded-xl border border-emerald-300/60 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted/30 disabled:text-muted-foreground dark:border-emerald-300/35 dark:bg-emerald-500/12 dark:text-emerald-100 dark:hover:bg-emerald-500/18"
	                        >
	                          Declarar instalação estável
	                        </button>
	                      </div>
	                    </div>
	                    {alertViewMode === 'list' ? (
	                      <div className="space-y-2">
	                        {sortedFilteredCases.map((item) => (
                          <ClinicalCaseCard
                            key={item.id}
                            item={item}
                            apiUrl={apiUrl}
                            authToken={authToken}
                            publicAnonKey={publicAnonKey}
                            onUpdate={() => fetchAlerts()}
                            autoOpen={caseMatchesSelectedAlert(item)}
	                            variant="row"
	                            selected={selectedClinicalCase?.id === item.id}
	                            onSelect={() => setSelectedClinicalCaseId(item.id)}
	                            selectable={item.validationStatus === 'pending'}
	                            selectedForBulk={bulkSelectedCaseIds.has(item.id)}
	                            onToggleBulkSelection={() => toggleBulkCase(item.id)}
	                          />
	                        ))}
	                      </div>
                    ) : (
                      <div className={`aql-vision-alert-grid grid grid-cols-1 gap-3 md:grid-cols-2 ${showClinicalDetailPanel ? 'xl:grid-cols-2 min-[1880px]:grid-cols-3' : 'xl:grid-cols-3 2xl:grid-cols-4 min-[1880px]:grid-cols-5'}`}>
                        {sortedFilteredCases.map((item) => (
                          <ClinicalCaseCard
                            key={item.id}
                            item={item}
                            apiUrl={apiUrl}
                            authToken={authToken}
                            publicAnonKey={publicAnonKey}
                            onUpdate={() => fetchAlerts()}
	                            autoOpen={caseMatchesSelectedAlert(item)}
	                            selected={selectedClinicalCase?.id === item.id}
	                            onSelect={() => setSelectedClinicalCaseId(item.id)}
	                            selectable={item.validationStatus === 'pending'}
	                            selectedForBulk={bulkSelectedCaseIds.has(item.id)}
	                            onToggleBulkSelection={() => toggleBulkCase(item.id)}
	                          />
	                        ))}
	                      </div>
                    )}

                    {showClinicalDetailPanel && selectedClinicalCase ? (
                      <aside className="aql-vision-panel-in rounded-2xl border-b border-border bg-transparent p-4 backdrop-blur 2xl:sticky 2xl:top-24 2xl:self-start">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div>
                            <p className="aql-lab-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Detalhe operacional</p>
                            <h3 className="text-base font-semibold text-foreground">
                              {selectedCaseIsSensor ? selectedCaseSensorLabel : selectedClinicalCase.tank ?? 'Tanque a confirmar'}
                            </h3>
                          </div>
                          {selectedCaseIsSensor ? <Activity className="h-5 w-5 text-cyan-300" /> : <Eye className="h-5 w-5 text-muted-foreground" />}
                        </div>
                        <div className="overflow-hidden rounded-2xl !border-0 bg-transparent">
                          {selectedCaseIsSensor ? (
                            <div className="flex min-h-56 flex-col justify-between bg-gradient-to-br from-cyan-500/12 via-card to-amber-500/8 p-5">
                              <div>
                                <p className="aql-lab-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Leitura recebida</p>
                                <p className="mt-4 text-4xl font-black text-foreground">
                                  {selectedCaseSensorValue ?? 'A confirmar'}
                                  {selectedClinicalCase.rawAlert.metadata?.unit ? ` ${selectedClinicalCase.rawAlert.metadata.unit}` : ''}
                                </p>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="rounded-xl !border-0 bg-muted/30 p-3">
                                  <p className="text-muted-foreground">Sensor</p>
                                  <p className="mt-1 font-semibold text-foreground">Sensor</p>
                                </div>
                                <div className="rounded-xl !border-0 bg-muted/30 p-3">
                                  <p className="text-muted-foreground">Descrição</p>
                                  <p className="mt-1 font-semibold text-foreground">
                                    {selectedCaseSensorDescription || 'Sensor ambiental'}
                                  </p>
                                </div>
                                <div className="col-span-2 rounded-xl !border-0 bg-muted/30 p-3">
                                  <p className="text-muted-foreground">Limite operacional</p>
                                  <p className="mt-1 font-semibold text-foreground">{selectedCaseRuleLabel}</p>
                                </div>
                              </div>
                            </div>
                          ) : selectedClinicalCase.image ? (
                            <img src={selectedClinicalCase.image} alt="Evidência visual do alerta" className="h-56 w-full object-cover" />
                          ) : (
                            <div className="flex h-56 items-center justify-center text-muted-foreground">
                              <Eye className="h-8 w-8" />
                            </div>
                          )}
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div className="rounded-xl !border-0 bg-muted/30 p-3">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Gravidade</p>
                            <p className="mt-1 font-semibold text-foreground">{SEVERITY_LABELS[selectedClinicalCase.severity] ?? selectedClinicalCase.severity}</p>
                          </div>
                          <div className="rounded-xl !border-0 bg-muted/30 p-3">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Validação</p>
                            <p className="mt-1 font-semibold text-foreground">{VALIDATION_STATUS_LABELS[selectedClinicalCase.validationStatus] ?? selectedClinicalCase.validationStatus}</p>
                          </div>
                          <div className="rounded-xl !border-0 bg-muted/30 p-3">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Confiança</p>
                            <p className="mt-1 font-semibold text-foreground">{selectedClinicalCase.confidence !== null ? `${selectedClinicalCase.confidence}%` : 'A confirmar'}</p>
                          </div>
                          <div className="rounded-xl !border-0 bg-muted/30 p-3">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Equipamento</p>
                            <p className="mt-1 truncate font-semibold text-foreground">
                              {selectedCaseIsSensor
                                ? selectedClinicalCase.rawAlert.metadata?.sensor_name ?? selectedCaseSensorLabel
                                : selectedClinicalCase.rawAlert.metadata?.camera_name ?? selectedClinicalCase.rawAlert.metadata?.camera ?? 'Captura visual'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 rounded-xl !border-0 bg-muted/20 p-3 text-sm text-muted-foreground">
                          <p className="font-semibold text-foreground">Ação recomendada</p>
                          <p className="mt-1">
                            {selectedCaseIsSensor
                              ? 'Confirmar leitura no tanque, verificar calibração/comunicação da sonda e registar a ação operacional.'
                              : selectedClinicalCase.validationStatus === 'pending'
                                ? 'Validar evidência visual e confirmar necessidade de intervenção no tanque.'
                                : 'Rever validação registada e manter histórico operacional atualizado.'}
                          </p>
                        </div>
                      </aside>
                    ) : null}
                  </div>
                )}
                </div>
              </div>
            </main>
            </div>
            </>
            )}

          </main>
          </div>
        </div>

        {currentView !== 'settings' ? mobileSectionNav : null}

        <AqlOperationalContextProvider value={assistantContextValue}>
          <AqlAssistantPanel
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
            authToken={authToken}
            summary={assistantSummary}
            demoMode={demoMode}
          />
        </AqlOperationalContextProvider>

        <CookiePreferencesDialog
          isOpen={showCookieSettings}
          preferences={cookiePreferences ?? defaultCookiePreferences()}
          onClose={() => setShowCookieSettings(false)}
          onSave={updateCookiePreferences}
        />

        {!cookiePreferences && !showPrivacyPolicy && (
          <CookieConsentBanner
            onAcceptAll={acceptAllCookies}
            onRejectOptional={rejectOptionalCookies}
            onManage={() => setShowCookieSettings(true)}
          />
        )}
      </div>
    );
  }
